import React, { useState, useEffect, useRef, useMemo } from 'react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  ChipProps,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Snackbar,
  Autocomplete,
  Paper,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  styled,
  LinearProgress,
  Grid,
  InputAdornment,
  FormHelperText,
  Backdrop,
  Fab
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FolderIcon from '@mui/icons-material/Folder';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SentimentAnalysisIcon from '@mui/icons-material/Psychology';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TopicIcon from '@mui/icons-material/Topic';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HighlightIcon from '@mui/icons-material/Highlight';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';
import SummarizeIcon from '@mui/icons-material/Summarize';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PieChartIcon from '@mui/icons-material/PieChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CategoryIcon from '@mui/icons-material/Category';
import SourceIcon from '@mui/icons-material/Source';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { environment } from '../../environments/environment';
import axios from 'axios';
import { styled as muiStyled } from '@mui/material/styles';
import botIcon from '../../assets/Chatbot.svg';
import { Chatbot } from './Chatbot/components/Chatbot';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import LockIcon from '@mui/icons-material/Lock';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import ReactMarkdown from 'react-markdown';
import InfoIcon from '@mui/icons-material/Info';
import DownloadIcon from '@mui/icons-material/Download';
import FileText from '@mui/icons-material/Description';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
import { GoogleDriveService, GoogleDriveFile, GoogleDriveAuthState, GoogleDriveServiceCallbacks, GoogleDriveFolder, GoogleDriveBreadcrumb } from '../api/googleDriveService';
import rbacApi from '../api/rbacApi';

// Interfaces
interface InputField {
  key: string;
  label: string;
  type: string;
  value: string | string[];
  info: string;
  options?: string[] | Array<{ id: string; label: string }>;
}

interface DocumentResponse {
  objectType: string;
  docUID: string;
  entries: Array<{
    key: string;
    label: string;
    value: string | string[] | Array<{ refDocUID: string }>;
    info?: string;
  }>;
}

interface RefDoc {
  refDocUID: string;
}

interface ChatMessage {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  interactionUID?: string;
}

interface MessageBubbleProps {
  sender: 'user' | 'bot';
  children?: React.ReactNode;
  sx?: any;
}

interface ResponseItem {
  objectType: 'DICTIONARY' | 'PARAGRAPH' | 'MULTITEXTOPTIONS';
  key?: string;
  value: any;
  entries?: Array<{
    key: string;
    label: string;
    value: string | string[];
    info?: string;
  }>;
  docUID?: string;
}

// Add Entry interface
interface Entry {
  key: string;
  value: string | string[] | Array<{
    key: string;
    label: string;
    info: string;
    options?: string[];
    value: string;
  }>;
  label: string;
  info?: string;
}

// Add NewsResponse interface
interface NewsResponse {
  objectType: 'DICTIONARY' | 'PARAGRAPH' | 'MULTITEXTOPTIONS';
  entries?: Entry[];
  value?: string;
  docUID?: string;
  key?: string;
  label?: string;
}

// Add Project interface
interface Project {
  id: string;
  org_id: string;
  project_name: string;
  project_description: string;
  project_type: string;
  configuration: any;
  source: string;
  active: boolean;
  status: string;
  last_updated_date: string;
}

// Add Grouped Response interface
interface GroupedResponse {
  groupName: string;
  items: DocumentResponse[];
}

interface GroupedDataResponse {
  status: number;
  data: GroupedResponse[];
}

// Export Content interfaces
interface ConversationPair {
  id: string;
  question: {
    id: string;
    content: string;
    timestamp: string;
  };
  answer: {
    id: string;
    content: string;
    timestamp: string;
  };
}

interface BrandDetails {
  website: string;
  websiteNotApplicable: boolean;
  industryKeywords: string;
  targetAudience: string[];
  brandVoice: string;
}

interface ContentPreferences {
  category: string;
  format: string;
  length: string;
  style: string;
  instructions: string;
}

// Add new interfaces for MULTICOLUMN support
interface MultiColumnData {
  headers: string[];
  data: Record<string, any>[];
  title: string;
  visual: boolean;
  dimension: number;
}

interface GeneratedContent {
  title: string;
  content: string;
  format: string;
  category: string;
  query?: string;
  followupQuestions?: string[];
  sourceLinks?: string[];
  visualization?: {
    type: 'table' | 'chart' | 'heatmap';
    data: MultiColumnData;
  };
}

// Google Drive interfaces imported from googleDriveService

// Styled Components
const FullScreenContainer = muiStyled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '#FFFFFF',
  zIndex: 1300,
  display: 'flex',
  flexDirection: 'column',
  padding: '24px',
  height: '90vh',
  overflow: 'hidden'
});

const MessageBubble = styled(Box)<MessageBubbleProps>(({ theme, sender }) => ({
  padding: '8px 12px',
  borderRadius: sender === 'user' ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
  maxWidth: '85%',
  wordBreak: 'break-word',
  fontSize: '0.75rem',
  lineHeight: '1.4',
  backgroundColor: sender === 'user' ? '#F3E5F5' : '#F5F5F5',
  color: sender === 'user' ? '#000000' : '#131F32',
  alignSelf: sender === 'user' ? 'flex-end' : 'flex-start',
  marginBottom: '8px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-1px)',
    transition: 'transform 0.2s ease',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
  }
}));

const StyledAccordion = muiStyled(Accordion)(({ theme }) => ({
  marginBottom: '12px',
  border: '1px solid #E0E0E0',
  borderRadius: '8px !important',
  boxShadow: 'none',
  transition: 'all 0.2s ease-in-out',
  overflow: 'hidden',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '0 0 12px 0',
    borderColor: '#9C27B0',
    boxShadow: '0 4px 12px rgba(21, 157, 255, 0.08)',
  },
  '&:hover': {
    borderColor: '#9C27B0',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(21, 157, 255, 0.08)',
  },
}));

const StyledAccordionSummary = muiStyled(AccordionSummary)(({ theme }) => ({
  padding: '16px',
  '& .MuiAccordionSummary-content': {
    margin: '0',
    '&.Mui-expanded': {
      margin: '0',
    },
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: '#9C27B0',
    transform: 'rotate(0deg)',
    transition: 'transform 0.2s ease-in-out',
    '&.Mui-expanded': {
      transform: 'rotate(180deg)',
    },
  },
}));

const StyledAccordionDetails = muiStyled(AccordionDetails)(({ theme }) => ({
  padding: '0 16px 16px 16px',
  borderTop: '1px solid #E0E0E0',
}));

const MarkdownMessage = ({ text }: { text: string }) => {
  return (
    <ReactMarkdown
      components={{
        strong: ({ children }) => (
          <Typography component="span" sx={{ fontWeight: 'bold' }}>
            {children}
          </Typography>
        ),
        em: ({ children }) => (
          <Typography component="span" sx={{ fontStyle: 'italic' }}>
            {children}
          </Typography>
        ),
        del: ({ children }) => (
          <Typography component="span" sx={{ textDecoration: 'line-through' }}>
            {children}
          </Typography>
        ),
        p: ({ children }) => (
          <Typography component="span" sx={{ display: 'inline' }}>
            {children}
          </Typography>
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  );
};

// Visualization Components
const DataTable = ({ data }: { data: MultiColumnData }) => {
  // Process data to handle complex arrays and improve display
  const processedData = data.data.map(row => {
    const processedRow: any = {};
    data.headers.forEach(header => {
      const value = row[header];
      if (Array.isArray(value)) {
        // Handle array values - show count and first few items
        if (value.length === 0) {
          processedRow[header] = 'No data';
        } else if (value.length <= 300) {
          processedRow[header] = value; // Keep as array for bullet point rendering
        } else {
          processedRow[header] = `${value.length} items (${value.slice(0, 2).join(', ')}...)`;
        }
      } else if (typeof value === 'number') {
        // Format numbers properly
        processedRow[header] = value.toFixed(2);
      } else if (typeof value === 'object' && value !== null) {
        // Handle object values
        processedRow[header] = JSON.stringify(value).substring(0, 50) + '...';
      } else {
        processedRow[header] = value;
      }
    });
    return processedRow;
  });

  return (
    <Box sx={{
      mt: 2,
      borderRadius: 2,
      overflow: 'hidden',
      border: '1px solid rgba(76, 55, 148, 0.1)',
      backgroundColor: '#fff',
      maxHeight: '400px',
      overflowY: 'auto'
    }}>
      <Box sx={{
        p: 2,
        backgroundColor: 'rgba(76, 55, 148, 0.05)',
        borderBottom: '1px solid rgba(76, 55, 148, 0.1)'
      }}>
        <Typography variant="h6" sx={{
          fontWeight: 600,
          color: '#4c3794',
          fontSize: '0.9rem'
        }}>
          {data.title}
        </Typography>
      </Box>
      <Box sx={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.75rem',
          minWidth: '600px'
        }}>
          <thead>
            <tr style={{ backgroundColor: 'rgba(76, 55, 148, 0.05)' }}>
              {data.headers.map((header, index) => (
                <th key={index} style={{
                  padding: '8px 12px',
                  textAlign: 'left',
                  fontWeight: 600,
                  color: '#4c3794',
                  borderBottom: '1px solid rgba(76, 55, 148, 0.1)',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap'
                }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {processedData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                style={{
                  backgroundColor: rowIndex % 2 === 0 ? '#fff' : 'rgba(76, 55, 148, 0.02)'
                }}
              >
                {data.headers.map((header, colIndex) => (
                  <td
                    key={colIndex}
                    style={{
                      padding: '8px 12px',
                      borderBottom: '1px solid rgba(18, 5, 62, 0.05)',
                      color: '#333',
                      fontSize: '0.75rem',
                      maxWidth: '200px',
                      wordWrap: 'break-word',
                      verticalAlign: 'top'
                    }}
                  >
                    {Array.isArray(row[header]) ? (
                      <ul style={{
                        margin: 0,
                        paddingLeft: '16px',
                        listStyleType: 'disc'
                      }}>
                        {row[header].map((item: any, index: number) => (
                          <li key={index} style={{
                            marginBottom: '4px',
                            fontSize: '0.7rem',
                            lineHeight: '1.3'
                          }}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      row[header]
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

const DataChart = ({ data }: { data: MultiColumnData }) => {
  // Convert data to chart format
  const chartData = data.data.map(row => {
    const chartItem: any = {};
    data.headers.forEach(header => {
      chartItem[header] = row[header];
    });
    return chartItem;
  });

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      return (
        <Box sx={{
          backgroundColor: '#ede7f6',
          borderRadius: '8px',
          border: 'none',
          boxShadow: '0 4px 12px rgba(76, 55, 148, 0.1)',
          padding: '12px 16px',
          color: '#4c3794'
        }}>
          {data.headers.map((header, index) => (
            <Box key={header} sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: index < data.headers.length - 1 ? 1 : 0,
              minWidth: '120px'
            }}>
              <Typography sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                textTransform: 'capitalize',
                color: '#4c3794'
              }}>
                {header.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
              </Typography>
              <Typography sx={{
                fontSize: '0.75rem',
                color: '#2c1810',
                fontWeight: 500
              }}>
                {typeof dataPoint[header] === 'number' && dataPoint[header] % 1 !== 0
                  ? dataPoint[header].toFixed(2)
                  : dataPoint[header]}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  // Determine chart type based on dimension
  if (data.dimension === 2) {
    // Bar chart for 2D data
    return (
      <Box sx={{
        mt: 2,
        borderRadius: 2,
        p: 2,
        border: '1px solid rgba(76, 55, 148, 0.1)',
        backgroundColor: '#fff'
      }}>
        <Typography variant="h6" sx={{
          fontWeight: 600,
          color: '#4c3794',
          mb: 2,
          fontSize: '1rem'
        }}>
          {data.title}
        </Typography>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: -10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ede7f6" />
            <XAxis
              dataKey={data.headers[0]}
              angle={-45}
              textAnchor="end"
              height={70}
              tick={{ fontSize: 12, fill: '#4c3794' }}
              tickLine={{ stroke: '#b39ddb' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#4c3794' }}
              tickLine={{ stroke: '#b39ddb' }}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Bar
              dataKey={data.headers[1]}
              radius={[4, 4, 0, 0]}
              maxBarSize={50}
              fill="#4c3794"
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  } else if (data.dimension === 3) {
    // Line chart for 3D data (time series with categories)
    const categories = Array.from(new Set(chartData.map(item => item[data.headers[0]])));

    return (
      <Box sx={{
        mt: 2,
        borderRadius: 2,
        p: 2,
        border: '1px solid rgba(76, 55, 148, 0.1)',
        backgroundColor: '#fff'
      }}>
        <Typography variant="h6" sx={{
          fontWeight: 600,
          color: '#4c3794',
          mb: 2,
          fontSize: '1rem'
        }}>
          {data.title}
        </Typography>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ede7f6" />
            <XAxis
              dataKey={data.headers[1]}
              angle={-45}
              textAnchor="end"
              height={70}
              tick={{ fontSize: 12, fill: '#4c3794' }}
              tickLine={{ stroke: '#b39ddb' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#4c3794' }}
              tickLine={{ stroke: '#b39ddb' }}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Legend />
            {categories.map((category, index) => {
              const categoryData = chartData.filter(item => item[data.headers[0]] === category);
              return (
                <Line
                  key={category}
                  type="monotone"
                  dataKey={data.headers[2]}
                  data={categoryData}
                  name={category}
                  stroke={`hsl(${240 + index * 60}, 70%, 50%)`}
                  strokeWidth={2}
                  dot={{
                    r: 4,
                    fill: `hsl(${240 + index * 60}, 70%, 50%)`,
                    strokeWidth: 2,
                    stroke: '#fff'
                  }}
                  activeDot={{
                    r: 6,
                    fill: `hsl(${240 + index * 60}, 70%, 50%)`,
                    stroke: '#fff',
                    strokeWidth: 2
                  }}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    );
  } else {
    // Default to table for other dimensions
    return <DataTable data={data} />;
  }
};

// Folder Tree Item Component
const FolderTreeItem = ({
  folder,
  onNavigate,
  onCloseDialog,
  level = 0
}: {
  folder: GoogleDriveFolder;
  onNavigate: (folderId: string) => void;
  onCloseDialog: () => void;
  level?: number;
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleClick = () => {
    onNavigate(folder.id);
    onCloseDialog();
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          pl: level * 2 + 1,
          cursor: 'pointer',
          borderRadius: 1,
          '&:hover': {
            backgroundColor: 'rgba(156, 39, 176, 0.1)',
          }
        }}
        onClick={handleClick}
      >
        <Typography sx={{ fontSize: '1.2rem', mr: 1 }}>📁</Typography>
        <Typography sx={{ flex: 1, fontWeight: 500 }}>
          {folder.name}
        </Typography>
        {folder.children && folder.children.length > 0 && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            sx={{ color: '#9C27B0' }}
          >
            <ExpandMoreIcon
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}
            />
          </IconButton>
        )}
      </Box>
      {expanded && folder.children && (
        <Box>
          {folder.children.map((child) => (
            <FolderTreeItem
              key={child.id}
              folder={child}
              onNavigate={onNavigate}
              onCloseDialog={onCloseDialog}
              level={level + 1}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const DocsInsights = () => {
  const navigate = useNavigate();
  // State management
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [inputFields, setInputFields] = useState<InputField[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [documents, setDocuments] = useState<DocumentResponse[]>([]);

  const [chatOpen, setChatOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentResponse | null>(null);
  const [chatQuery, setChatQuery] = useState('');
  const [chatResponse, setChatResponse] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Ref to track previous config to prevent unnecessary Chatbot resets
  const previousConfigRef = useRef<string>('');
  const chatbotApiConfigRef = useRef<{
    baseUrl: string;
    queryEndpoint: string;
    campaignId: string;
    agentCode: string;
    contentTypesEndpoint: string;
    generateContentEndpoint: string;
    customQueryParams: Record<string, any>;
  }>({
    baseUrl: environment.controlplaneUrl,
    queryEndpoint: '/agent/flow/execute',
    campaignId: '',
    agentCode: 'QUERY_DOCUMENTS_AND_RESPOND',
    contentTypesEndpoint: '/account/getContentTypes',
    generateContentEndpoint: '/agent/flow/execute',
    customQueryParams: {}
  });

  const handleCloseChat = () => {
    setIsChatOpen(false);
    setChatOpen(false);
  };

  const handleChatClick = () => {
    setIsChatOpen(true);
    setChatOpen(true);
  };
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [allDocuments, setAllDocuments] = useState<DocumentResponse[]>([]);
  const [refDocDialogOpen, setRefDocDialogOpen] = useState(false);
  const [selectedRefDoc, setSelectedRefDoc] = useState<{ docUID: string, refDocUID: string } | null>(null);
  const [docDifferences, setDocDifferences] = useState<any>(null);
  const [loadingDifferences, setLoadingDifferences] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  // Memoize apiConfig to prevent unnecessary Chatbot resets
  // Dynamically set agent code based on selectedDocument
  const chatbotApiConfig = useMemo(() => {
    const currentCampaignId = selectedProject?.id || '';
    const currentDocUID = selectedDocument?.docUID || '';
    const currentAgentCode = currentDocUID ? 'QUERY_DOCUMENT_SECTIONS_AND_RESPOND' : 'QUERY_DOCUMENTS_AND_RESPOND';
    const configKey = `${currentCampaignId}_${currentDocUID}_${currentAgentCode}`;

    // Only create new object if config actually changed
    if (previousConfigRef.current === configKey) {
      return chatbotApiConfigRef.current;
    }

    // Config changed, update refs and return new config
    previousConfigRef.current = configKey;
    const customQueryParams: Record<string, any> = {};
    if (currentDocUID) {
      customQueryParams.docUID = currentDocUID;
    }

    const newConfig = {
      baseUrl: environment.controlplaneUrl,
      queryEndpoint: '/agent/flow/execute',
      campaignId: currentCampaignId,
      agentCode: currentAgentCode,
      contentTypesEndpoint: '/account/getContentTypes',
      generateContentEndpoint: '/agent/flow/execute',
      customQueryParams
    };
    chatbotApiConfigRef.current = newConfig;
    return newConfig;
  }, [selectedProject?.id, selectedDocument?.docUID]);

  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({
    project_name: '',
    project_description: '',
    period_start_date: new Date().toISOString().split('T')[0],
    period_end_date: new Date().toISOString().split('T')[0]
  });
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);
  const [projectError, setProjectError] = useState('');
  const [groupedData, setGroupedData] = useState<GroupedResponse[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);

  // RBAC permission state
  const [canEditProject, setCanEditProject] = useState(false);
  const [canViewProject, setCanViewProject] = useState(true);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [uploadFormData, setUploadFormData] = useState<{
    topic: string;
    keywords: string[];
  }>({
    topic: '',
    keywords: []
  });

  // Add top-level tab state
  const [topLevelTab, setTopLevelTab] = useState(0); // 0: Dashboard, 1: AI Summary, 2: Documents

  // Source type filter state
  const [selectedSourceType, setSelectedSourceType] = useState<string>('all');

  // Export Content state
  const [isExportMode, setIsExportMode] = useState(false);
  const [selectedPairs, setSelectedPairs] = useState<Set<string>>(new Set());
  const [showExportDialog, setShowExportDialog] = useState(false);

  // Document selection state for AI Summary tab export
  const [isDocumentExportMode, setIsDocumentExportMode] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set()); // Stores UUIDs for both UI and API
  const [currentStep, setCurrentStep] = useState(1);
  const [brandDetails, setBrandDetails] = useState<BrandDetails>({
    website: '',
    websiteNotApplicable: false,
    industryKeywords: '',
    targetAudience: [],
    brandVoice: ''
  });
  const [contentPreferences, setContentPreferences] = useState<ContentPreferences>({
    category: 'SOCIAL_POST_LINKEDIN',
    format: 'Text Only',
    length: 'Short (100-200 words)',
    style: 'Professional',
    instructions: ''
  });
  const [newAudience, setNewAudience] = useState('');
  const [showAudienceDropdown, setShowAudienceDropdown] = useState(false);
  const [showContentDisplay, setShowContentDisplay] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [isEditingGeneratedContent, setIsEditingGeneratedContent] = useState(false);
  const [editableGeneratedTitle, setEditableGeneratedTitle] = useState('');
  const [editableGeneratedBody, setEditableGeneratedBody] = useState('');
  const [isExportingAsIs, setIsExportingAsIs] = useState(false);
  const [contentTypes, setContentTypes] = useState<any[]>([]);
  useEffect(() => {
    if (showContentDisplay && generatedContent) {
      setEditableGeneratedTitle(generatedContent.title || '');
      setEditableGeneratedBody(generatedContent.content || '');
    } else {
      setIsEditingGeneratedContent(false);
      setEditableGeneratedTitle('');
      setEditableGeneratedBody('');
    }
  }, [showContentDisplay, generatedContent]);

  // Add state for expanded cards
  const [expandedCards, setExpandedCards] = useState<{ [key: string]: { topics: boolean, highlights: boolean } }>({});

  // Add dashboard data state
  const [dashboardData, setDashboardData] = useState({
    totalDocuments: 0,
    totalTopics: 0,
    totalHighlights: 0,
    topTopics: [] as { name: string, count: number }[],
    topSources: [] as { name: string, count: number }[],
    documentsByGroup: [] as { group: string, count: number }[],
    recentDocuments: [] as DocumentResponse[],
    topicsDistribution: [] as { name: string, count: number }[],
    highlightsTimeline: [] as { date: string, count: number }[],
    recentHighlights: [] as { text: string, documentTitle: string, documentId: string }[],
    highlightsByTopic: [] as {
      topic: string,
      highlights: { text: string, documentTitle: string, documentId: string, docDateMs?: number }[],
      latestDateMs?: number,
      mentions?: number,
      dominantHighlight?: { text: string; documentTitle: string; documentId: string }
    }[]
  });

  // Track the project ID that the current documents belong to
  const currentProjectIdRef = useRef<string | null>(null);

  // Initial loading state - tracks all API calls needed for dashboard to be ready
  const [initialLoadingState, setInitialLoadingState] = useState({
    projects: false,
    inputFields: false,
    contentTypes: false,
    documents: false,
    groupedData: false,
    permissions: false,
    dashboardProcessed: false
  });

  // Compute if initial loading is complete
  // Only relevant for dashboard tab - other tabs don't show loader
  const isInitialLoading = useMemo(() => {
    // Only show loader on dashboard tab (topLevelTab === 0)
    if (topLevelTab !== 0) {
      return false;
    }

    // If no project is selected, we only need projects, inputFields, and contentTypes
    if (!selectedProject) {
      return !initialLoadingState.projects || !initialLoadingState.inputFields || !initialLoadingState.contentTypes;
    }

    // For dashboard tab, we need:
    // - Basic data (projects, inputFields, contentTypes, permissions)
    // - Documents (required)
    // - groupedData (optional - only if we're trying to fetch it)
    // - dashboardProcessed (required)
    const hasBasicData = initialLoadingState.projects &&
      initialLoadingState.inputFields &&
      initialLoadingState.contentTypes &&
      initialLoadingState.permissions;

    const hasDocuments = initialLoadingState.documents;
    const hasGroupedData = initialLoadingState.groupedData || groupedData.length === 0; // Optional - true if loaded or not needed
    const isProcessed = initialLoadingState.dashboardProcessed;

    // If we have documents, we can show dashboard (groupedData is optional)
    return !hasBasicData || !hasDocuments || !isProcessed;
  }, [selectedProject, topLevelTab, initialLoadingState, groupedData.length]);

  // Add Google Drive state variables
  const [uploadMethod, setUploadMethod] = useState<'manual' | 'auto-sources' | 'auto-web'>('manual');
  const [manualUploadType, setManualUploadType] = useState<'device' | 'google-drive'>('device');

  // Web Search State
  const [isWebSearchLoading, setIsWebSearchLoading] = useState(false);
  const [webSearchError, setWebSearchError] = useState<string | null>(null);
  const [webSearchSuccess, setWebSearchSuccess] = useState<string | null>(null);
  const [scheduleFrequency, setScheduleFrequency] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [fileUrl, setFileUrl] = useState('');
  const [isUrlValidating, setIsUrlValidating] = useState(false);
  const [urlValidationError, setUrlValidationError] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [showGoogleDriveDialog, setShowGoogleDriveDialog] = useState(false);
  const [googleDriveAuthState, setGoogleDriveAuthState] = useState<GoogleDriveAuthState>({
    isAuthenticated: false,
    sessionId: ''
  });
  const [googleDriveFiles, setGoogleDriveFiles] = useState<GoogleDriveFile[]>([]);
  const [isLoadingGoogleFiles, setIsLoadingGoogleFiles] = useState(false);
  const [selectedGoogleFile, setSelectedGoogleFile] = useState<GoogleDriveFile | null>(null);
  const [selectedGoogleFiles, setSelectedGoogleFiles] = useState<GoogleDriveFile[]>([]);
  const [isDownloadingGoogleFile, setIsDownloadingGoogleFile] = useState(false);
  const [isDownloadingMultipleFiles, setIsDownloadingMultipleFiles] = useState(false);
  const [googleDriveSearchQuery, setGoogleDriveSearchQuery] = useState('');
  const [googleDriveCurrentPage, setGoogleDriveCurrentPage] = useState(1);
  const [googleDriveTotalFiles, setGoogleDriveTotalFiles] = useState(0);
  const [googleDriveDownloadProgress, setGoogleDriveDownloadProgress] = useState(0);
  const [showGoogleDriveAuthDialog, setShowGoogleDriveAuthDialog] = useState(false);
  const [googleDriveAuthUrl, setGoogleDriveAuthUrl] = useState('');
  const [isGoogleDriveAuthInProgress, setIsGoogleDriveAuthInProgress] = useState(false);
  const [googleDriveAuthWindow, setGoogleDriveAuthWindow] = useState<Window | null>(null);
  const [showFileInfo, setShowFileInfo] = useState(false);
  const [fileInfo, setFileInfo] = useState<any>(null);
  const [isLoadingFileInfo, setIsLoadingFileInfo] = useState(false);

  // Folder navigation state
  const [currentFolderId, setCurrentFolderId] = useState<string>('root');
  const [folderTree, setFolderTree] = useState<GoogleDriveFolder[]>([]);
  const [showFolderTree, setShowFolderTree] = useState(false);
  const [isLoadingFolderTree, setIsLoadingFolderTree] = useState(false);
  const [folderBreadcrumb, setFolderBreadcrumb] = useState<GoogleDriveBreadcrumb[]>([]);

  // Tab navigation state
  const [activeTab, setActiveTab] = useState<'myDrive' | 'shared' | 'teamDrives'>('myDrive');
  const [teamDrives, setTeamDrives] = useState<GoogleDriveFile[]>([]);
  const [selectedTeamDrive, setSelectedTeamDrive] = useState<string | null>(null);

  // Google Drive service ref
  const googleDriveService = useRef<GoogleDriveService | null>(null);

  // Frontend-only search - filter files based on search query
  const filteredGoogleDriveFiles = googleDriveFiles.filter(file => {
    if (!googleDriveSearchQuery.trim()) return true;

    const query = googleDriveSearchQuery.toLowerCase();
    const fileName = file.name.toLowerCase();
    const fileType = file.mimeType.toLowerCase();

    return fileName.includes(query) ||
      fileType.includes(query) ||
      (file.size && file.size.toLowerCase().includes(query));
  });

  // Fetch initial input fields
  useEffect(() => {
    fetchInputFields();
    fetchContentTypes();
    // Load initial documents - handleSearch will be called when selectedProject is set
  }, []);

  // Initialize Google Drive service
  useEffect(() => {
    googleDriveService.current = new GoogleDriveService({
      onAuthStateChange: (state: GoogleDriveAuthState) => {
        setGoogleDriveAuthState(state);
      },
      onFilesLoaded: (files: GoogleDriveFile[], totalFiles: number) => {
        setGoogleDriveFiles(files);
        setGoogleDriveTotalFiles(totalFiles);
      },
      onError: (error: string) => {
        setError(error);
      },
      onSuccess: (message: string) => {
        setSuccess(message);
      },
      onDownloadProgress: (progress: number) => {
        setGoogleDriveDownloadProgress(progress);
      },
      onLoadingStateChange: (isLoading: boolean) => {
        setIsLoadingGoogleFiles(isLoading);
      },
      onFilePreviewLoaded: (previewInfo: any, previewUrl: string, previewContent: string) => {
        // Not used in DocsInsights, but required by interface
      },
      onFolderBreadcrumbLoaded: (breadcrumb: any[]) => {
        setFolderBreadcrumb(breadcrumb);
      },
      onFolderTreeLoaded: (folderTree: any[]) => {
        setFolderTree(folderTree);
        setShowFolderTree(true);
      },
      onFileInfoLoaded: (fileInfo: any) => {
        setFileInfo(fileInfo);
        setShowFileInfo(true);
      }
    });

    // Note: Removed immediate checkExistingSession call to avoid duplicate API calls
    // The delayed check in the useEffect at lines 2095-2109 handles this more reliably

    return () => {
      if (googleDriveService.current) {
        googleDriveService.current.destroy();
      }
    };
  }, []);

  const fetchInputFields = async () => {
    try {
      const response = await axios.post(
        `${environment.apiUrl}/agent/getPayload`,
        {
          data: {},
          agent: {
            code: "INDEX_REGULATORY_DOCUMENT"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );
      setInputFields(response.data.data.primaryInput);
      setInitialLoadingState(prev => ({ ...prev, inputFields: true }));
    } catch (err: any) {
      if (
        err?.response?.data?.error === 'jwt malformed' ||
        err?.response?.data?.error === 'jwt expired'
      ) {
        console.log(err?.response?.data?.error);
        navigate('/login');
      }
      // setError('Failed to fetch input fields');
      console.error('Error fetching input fields:', err);
      // Mark as complete even on error to prevent infinite loading
      setInitialLoadingState(prev => ({ ...prev, inputFields: true }));
    }
  };

  // Helper function to convert schedule to cron expression
  const getCronExpression = (frequency: string, time: string) => {
    const [hours, minutes] = time.split(':').map(Number);

    switch (frequency) {
      case 'daily':
        return `${minutes} ${hours} * * *`; // Every day at specified time
      case 'weekly':
        return `${minutes} ${hours} * * 1`; // Every Monday at specified time
      case 'monthly':
        return `${minutes} ${hours} 1 * *`; // First day of every month at specified time
      default:
        return `${minutes} ${hours} * * *`; // Default to daily
    }
  };

  // Web Search API Integration
  const handleWebSearch = async () => {
    if (!uploadFormData.topic.trim()) {
      setWebSearchError('Please enter a document topic');
      return;
    }

    if (uploadFormData.keywords.length === 0) {
      setWebSearchError('Please add at least one keyword');
      return;
    }

    try {
      setIsWebSearchLoading(true);
      setWebSearchError(null);
      setWebSearchSuccess(null);

      const payload = {
        data: {
          campaignId: selectedProject?.id || "68b572fb63d958bba6f82961", // Use selected project ID or default
          topic: uploadFormData.topic,
          keywords: uploadFormData.keywords
        },
        agent: {
          code: "INDEX_WEB_FOR_REGULATORY_INTEL"
        },
        schedule: {
          jobType: "User",
          scheduleType: "cron",
          configuration: {
            cronExpression: getCronExpression(scheduleFrequency, scheduleTime)
          }
        }
      };

      const response = await axios.post(
        `${environment.apiUrl}/agent/scheduleFlow`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.status === 200) {
        setWebSearchSuccess('Web search job scheduled successfully! The system will automatically search and fetch relevant documents based on your criteria.');

        // Reset form data
        setUploadFormData({
          topic: '',
          keywords: []
        });

        // Close dialog after a delay
        setTimeout(() => {
          setUploadDialogOpen(false);
        }, 2000);
      } else {
        setWebSearchError('Failed to schedule web search job');
      }
    } catch (error: any) {
      console.error('Web search API error:', error);
      setWebSearchError(
        error.response?.data?.message ||
        error.message ||
        'Failed to schedule web search job'
      );
    } finally {
      setIsWebSearchLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) return;

    setUploadLoading(true);
    const file = event.target.files[0];
    const formData = new FormData();
    formData.append('file', file);
    formData.append('token', `Bearer ${localStorage.getItem('token')}`);
    formData.append('type', 'docs');
    formData.append('campaignId', selectedProject?.id || '');
    try {
      // Upload file
      const uploadResponse = await axios.post(
        `${environment.apiUrl}/account/uploadFileForOrg`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      if (uploadResponse.data.status === 'success') {
        // Index the document with form data
        const indexResponse = await axios.post(
          `${environment.controlplaneUrl}/agent/flow/execute`,
          {
            data: {
              filePath: uploadResponse.data.path,
              fileType: "docs",
              contentType: "CIRCULAR",
              sourceType: "user_upload",
              campaignId: selectedProject?.id || '',
              topic: uploadFormData.topic || '',
              keywords: uploadFormData.keywords || []
            },
            agent: {
              code: "INDEX_REGULATORY_DOCUMENT"
            }
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            }
          }
        );

        setSuccess('Document uploaded and indexed successfully');
        // Reset form data
        setUploadFormData({
          topic: '',
          keywords: []
        });
        // Close dialog
        setUploadDialogOpen(false);
        // Refresh document list
        handleSearch();
      }
    } catch (err: any) {
      if (
        err?.response?.data?.error === 'jwt malformed' ||
        err?.response?.data?.error === 'jwt expired'
      ) {
        console.log(err?.response?.data?.error);
        navigate('/login');
      }
      setError('Failed to upload and index document');
      console.error('Error uploading document:', err);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleSearch = async (useGrouping = true) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            topic: selectedTopic || '',
            keywords: keywords || [],
            contentType: "CIRCULAR",
            campaignId: selectedProject?.id || '',
            pageSize: 500,
            pageNumber: 1,
          },
          agent: {
            // code: "QUERY_RELEVANT_DOCUMENTS"
            code: useGrouping ? "QUERY_DOCUMENT_INSIGHTS" : "QUERY_RELEVANT_DOCUMENTS",
            ...(useGrouping && { groupBy: "contentClass" })
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );

      // Handle both old and new response formats
      const responseData = response.data.data || [];


      // Check if response is grouped (new format) and grouping is enabled
      if (useGrouping && Array.isArray(responseData) && responseData.length > 0 && responseData[0].groupName) {

        // New grouped format
        setGroupedData(responseData);
        setAllDocuments([]);
        setDocuments([]);


        // No need to initialize paginated data - will be calculated in render
      } else {
        // Old flat format
        setGroupedData([]);
        setAllDocuments(responseData);


        // Set all documents for flat format
        setDocuments(responseData);
      }

      // Add a small delay to ensure data is properly set before hiding loader
      await new Promise(resolve => setTimeout(resolve, 100));

      // Reset selected tab and visible items when new search is performed
      setSelectedTab(0);
      resetVisibleItems();

      // Mark documents as loaded for initial loading state
      setInitialLoadingState(prev => ({ ...prev, documents: true }));

      // Remove default document selection
    } catch (err: any) {
      if (
        err?.response?.data?.error === 'jwt malformed' ||
        err?.response?.data?.error === 'jwt expired'
      ) {
        console.log(err?.response?.data?.error);
        navigate('/login');
      }
      setError('Failed to search documents');
      console.error('Error searching documents:', err);
      // Mark as complete even on error to prevent infinite loading
      setInitialLoadingState(prev => ({ ...prev, documents: true }));
    } finally {
      setLoading(false);
    }
  };



  // Infinite scroll state
  const [visibleItems, setVisibleItems] = useState(10);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Get all items for current view
  const getAllItems = () => {
    if (groupedData.length > 0 && groupedData[selectedTab]) {
      return groupedData[selectedTab].items;
    } else if (documents.length > 0) {
      return documents;
    }
    return [];
  };

  // Get visible items for infinite scroll
  const getVisibleItems = () => {
    const allItems = getAllItems();
    return allItems.slice(0, visibleItems);
  };

  // Handle scroll to load more
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const allItems = getAllItems();

    // Load more when user is near bottom (within 100px)
    if (scrollHeight - scrollTop - clientHeight < 100 &&
      visibleItems < allItems.length &&
      !isLoadingMore) {
      setIsLoadingMore(true);

      // Simulate loading delay
      setTimeout(() => {
        setVisibleItems(prev => Math.min(prev + 10, allItems.length));
        setIsLoadingMore(false);
      }, 300);
    }
  };

  // Reset visible items when tab changes
  const resetVisibleItems = () => {
    setVisibleItems(10);
    setIsLoadingMore(false);
  };

  const handleChat = async () => {
    if (!chatQuery.trim() || !selectedDocument?.docUID) return;

    setChatLoading(true);
    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            query: chatQuery,
            docUID: selectedDocument.docUID,
            campaignId: selectedProject?.id || ''
          },
          agent: {
            code: selectedDocument?.docUID ? "QUERY_DOCUMENT_SECTIONS_AND_RESPOND" : "QUERY_DOCUMENTS_AND_RESPOND"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );

      const responseText = response.data.data.find((item: any) => item.key === 'response')?.value || '';
      setChatResponse(responseText);
    } catch (err: any) {
      if (
        err?.response?.data?.error === 'jwt malformed' ||
        err?.response?.data?.error === 'jwt expired'
      ) {
        console.log(err?.response?.data?.error);
        navigate('/login');
      }
      setError('Failed to get chat response');
      console.error('Error in chat:', err);
    } finally {
      setChatLoading(false);
    }
  };

  const formatMessageContent = (message: ChatMessage) => {
    try {
      console.log(message.text);

      // If the message is a plain string and not JSON, format it as regular text
      if (typeof message.text === 'string' && !message.text.startsWith('{') && !message.text.startsWith('[')) {
        return <MarkdownMessage text={message.text} />;
      }

      // Parse the message if it's a string, or use it directly if it's already an object
      const data = typeof message.text === 'string' ? JSON.parse(message.text) : message.text;

      // If data is an array, wrap it in an object with a data property
      const formattedData = Array.isArray(data) ? { data } : data;

      if (formattedData.data) {
        return (
          <Box sx={{ width: '100%' }}>
            {formattedData.data.map((item: any, index: number) => {
              if (item.objectType === 'DICTIONARY') {
                const entries = item.entries || [];
                const title = entries.find((e: any) => e.key === 'title')?.value?.toString() || 'Resume Details';
                const briefDescription = entries.find((e: any) => e.key === 'briefDescription')?.value?.toString() || '';
                const topicsEntry = entries.find((e: any) => e.key === 'topics')?.value;
                const highlightsEntry = entries.find((e: any) => e.key === 'highlights')?.value;
                const detailedDescription = entries.find((e: any) => e.key === 'detailedDescription')?.value?.toString() || '';

                // Ensure topics and highlights are string arrays
                const topics = Array.isArray(topicsEntry) ? topicsEntry.map(t => t.toString()) : [];
                const highlights = Array.isArray(highlightsEntry) ? highlightsEntry.map(h => h.toString()) : [];

                return (
                  <StyledAccordion key={item.docUID}
                    sx={{
                      mb: 1,
                      border: selectedDocument?.docUID === item.docUID ? '2px solid #9C27B0' : '1px solid #E0E0E0',
                      borderRadius: '8px !important',
                      '&:before': {
                        display: 'none',
                      },
                      '&.Mui-expanded': {
                        margin: '0 0 8px 0',
                      }
                    }}

                  >
                    <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontWeight: 600, color: '#1A2027', fontSize: '0.75rem' }}>
                          {title}
                        </Typography>

                      </Box>
                    </StyledAccordionSummary>
                    <StyledAccordionDetails>

                      {briefDescription && (
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.75rem' }}>
                          <MarkdownMessage text={briefDescription} />
                        </Typography>
                      )}
                      {topics.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A2027', mb: 1, fontSize: '0.75rem' }}>
                            Topics
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {topics.map((topic, idx) => (
                              <Chip
                                key={idx}
                                label={topic}
                                sx={{
                                  backgroundColor: '#F3E5F5',
                                  color: '#9C27B0',
                                  '&:hover': {
                                    backgroundColor: '#BBDEFB'
                                  },
                                  fontSize: '0.625rem',
                                  height: '20px',
                                  '& .MuiChip-label': {
                                    padding: '0 6px'
                                  }
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {highlights.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A2027', mb: 1, fontSize: '0.75rem' }}>
                            Highlights
                          </Typography>
                          {highlights.map((highlight, idx) => (
                            <Typography key={idx} variant="body2" sx={{
                              color: '#666',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 0.5,
                              fontSize: '0.75rem'
                            }}>
                              <Box sx={{
                                width: 4,
                                height: 4,
                                borderRadius: '50%',
                                backgroundColor: '#1976D2',
                                flexShrink: 0
                              }} />
                              {highlight}
                            </Typography>
                          ))}
                        </Box>
                      )}

                      {detailedDescription && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A2027', mb: 1, fontSize: '0.75rem' }}>
                            Detailed Description
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                            <MarkdownMessage text={detailedDescription} />
                          </Typography>
                        </Box>
                      )}
                    </StyledAccordionDetails>
                  </StyledAccordion>
                );
              } else if (item.objectType === 'PARAGRAPH') {
                return (
                  <Typography key={index} variant="body2" sx={{
                    whiteSpace: 'pre-wrap',
                    mb: 2,
                    color: '#666',
                    lineHeight: 1.6,
                    fontSize: '0.75rem'
                  }}>
                    <MarkdownMessage text={item.value?.toString() || ''} />
                  </Typography>
                );
              } else if (item.objectType === 'MULTITEXTOPTIONS' && item.key === 'sourceLinks') {
                return (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{
                      fontWeight: 600,
                      color: '#1A2027',
                      mb: 1,
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <LinkIcon sx={{ fontSize: 16, color: '#9C27B0' }} />
                      {item.label || 'Source Links'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {Array.isArray(item.value) && item.value.map((link: string, linkIndex: number) => (
                        <Box
                          key={linkIndex}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1.5,
                            backgroundColor: '#F8F9FA',
                            borderRadius: '8px',
                            border: '1px solid #E5E9EF',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: '#F3E5F5',
                              borderColor: '#9C27B0',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 2px 8px rgba(156, 39, 176, 0.1)'
                            }
                          }}
                          onClick={() => {
                            // Extract filename from URL for display
                            const urlParts = link.split('/');
                            const filename = urlParts[urlParts.length - 1]?.split('?')[0] || 'Document';
                            window.open(link, '_blank');
                          }}
                        >
                          <DescriptionOutlinedIcon sx={{
                            fontSize: 18,
                            color: '#9C27B0',
                            flexShrink: 0
                          }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{
                              color: '#131F32',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap'
                            }}>
                              {link.split('/').pop()?.split('?')[0] || 'Document'}
                            </Typography>
                            <Typography variant="caption" sx={{
                              color: '#64748B',
                              fontSize: '0.625rem',
                              display: 'block',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap'
                            }}>
                              {link.length > 50 ? `${link.substring(0, 50)}...` : link}
                            </Typography>
                          </Box>
                          <OpenInNewIcon sx={{
                            fontSize: 16,
                            color: '#9C27B0',
                            flexShrink: 0
                          }} />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              }
              return null;
            })}
          </Box>
        );
      }
      // If no data property is found, format as regular text
      return <MarkdownMessage text={typeof message.text === 'string' ? message.text : JSON.stringify(message.text)} />;
    } catch (error) {
      // If there's an error parsing the message, display it as regular text
      return <MarkdownMessage text={typeof message.text === 'string' ? message.text : JSON.stringify(message.text)} />;
    }
  };

  // Old chat functions removed - now using reusable Chatbot component
  // The Chatbot component handles all messaging internally

  const handleRefDocClick = async (docUID: string, refDocUID: string) => {
    setSelectedRefDoc({ docUID, refDocUID });
    setRefDocDialogOpen(true);
    setLoadingDifferences(true);
    setCurrentPage(0);

    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            refDocUID,
            docUID
          },
          agent: {
            code: "QUERY_REGULATORY_DOCUMENT_DIFFERENCES"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );
      setDocDifferences(response.data.data);
      setTotalPages(response.data.data.length);
    } catch (err: any) {
      if (
        err?.response?.data?.error === 'jwt malformed' ||
        err?.response?.data?.error === 'jwt expired'
      ) {
        console.log(err?.response?.data?.error);
        navigate('/login');
      }
      setError('Failed to fetch document differences');
      console.error('Error fetching document differences:', err);
    } finally {
      setLoadingDifferences(false);
    }
  };

  // Helper function to safely get string value from document entry
  const getStringValue = (doc: DocumentResponse, key: string): string => {
    const entry = doc.entries.find(e => e.key === key);
    if (!entry) return '';
    return typeof entry.value === 'string' ? entry.value : '';
  };

  // Helper function to safely get array value from document entry
  const getArrayValue = <T extends string | { refDocUID: string }>(
    doc: DocumentResponse,
    key: string
  ): T[] => {
    const entry = doc.entries.find(e => e.key === key);
    if (!entry) return [];
    return Array.isArray(entry.value) ? entry.value as T[] : [];
  };

  // Helper function to filter documents by source type
  const filterDocumentsBySourceType = (docs: DocumentResponse[], sourceType: string): DocumentResponse[] => {
    if (sourceType === 'all') return docs;
    return docs.filter(doc => {
      const docSourceType = getStringValue(doc, 'sourceType');
      return docSourceType === sourceType;
    });
  };

  // Get source type counts
  const getSourceTypeCounts = (docs: DocumentResponse[]) => {
    const counts = {
      all: docs.length,
      user_upload: 0,
      shared_drive: 0,
      web_search: 0
    };

    docs.forEach(doc => {
      const sourceType = getStringValue(doc, 'sourceType');
      if (sourceType === 'user_upload') counts.user_upload++;
      else if (sourceType === 'shared_drive') counts.shared_drive++;
      else if (sourceType === 'web_search') counts.web_search++;
    });

    return counts;
  };

  // Fetch projects on mount
  useEffect(() => {
    const fetchProjects = async () => {
      setIsLoadingProjects(true);
      try {
        const response = await axios.post<Project[]>(
          `${environment.apiUrl}/workspace/getProjectForOrg`,
          {
            data: {
              org_id: localStorage.getItem("org_id"),
              project_type: ["GVRN_DOCU"]
            }
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
          }
        );
        setProjects(response.data);
        if (response.data.length > 0) {
          const firstProject = response.data[0];
          setSelectedProject(firstProject);
          // Initialize the project ID ref
          currentProjectIdRef.current = firstProject.id;
        }
        setInitialLoadingState(prev => ({ ...prev, projects: true }));
      } catch (error) {
        // Optionally handle error
        // Mark as complete even on error to prevent infinite loading
        setInitialLoadingState(prev => ({ ...prev, projects: true }));
      } finally {
        setIsLoadingProjects(false);
      }
    };
    fetchProjects();
  }, []);

  // Fetch content types from API
  const fetchContentTypes = async () => {
    try {
      const response = await axios.post(
        `${environment.apiUrl}/account/getContentTypes`,
        {
          data: {}
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && Array.isArray(response.data)) {
        setContentTypes(response.data);
      }
      setInitialLoadingState(prev => ({ ...prev, contentTypes: true }));
    } catch (error: any) {
      console.error('Error fetching content types:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      // Set fallback content types if API fails
      setContentTypes([
        { contentType: 'SOCIAL_POST_LINKEDIN', contentTypeDescription: 'LinkedIn Post', class: 'Semi Formal' },
        { contentType: 'SOCIAL_POST_FACEBOOK', contentTypeDescription: 'Facebook Post', class: 'Semi Formal' },
        { contentType: 'SOCIAL_POST_TWITTER', contentTypeDescription: 'Twitter Post', class: 'Semi Formal' },
        { contentType: 'BLOG_POST', contentTypeDescription: 'Blog', class: 'Semi Formal' },
        { contentType: 'EMAIL', contentTypeDescription: 'Email', class: 'Semi Formal' },
        { contentType: 'VIDEO', contentTypeDescription: 'Video', class: 'Semi Formal' },
        { contentType: 'SHORT_MESSAGE', contentTypeDescription: 'Short Message', class: 'Semi Formal' },
        { contentType: 'CUSTOM', contentTypeDescription: 'Custom', class: 'Semi Formal' }
      ]);
      // Mark as complete even on error to prevent infinite loading
      setInitialLoadingState(prev => ({ ...prev, contentTypes: true }));
    }
  };

  // RBAC: Fetch project permissions
  const fetchProjectPermissions = async () => {
    if (!selectedProject) {
      setInitialLoadingState(prev => ({ ...prev, permissions: true }));
      return;
    }

    const userId = localStorage.getItem('user_email') || 'demouser01@bahwancybertek.com';
    const organisationId = localStorage.getItem('org_id') || 'bctglobal';

    setLoadingPermissions(true);

    try {
      // Call the correct RBAC endpoint: /analytics/rbac/users/{userId}/project-permissions/{projectId}
      const response = await fetch(
        `${environment.apiUrl}/analytics/rbac/users/${userId}/project-permissions/${selectedProject.id}?organisationId=${organisationId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Check both canView and canEdit permissions
        const canView = data.permissions?.canView || false;
        const canEdit = data.permissions?.canEdit || false;
        setCanViewProject(canView);
        setCanEditProject(canEdit);

        console.log('🔐 Project permissions loaded:', { projectId: selectedProject.id, canView, canEdit, data });
      } else {
        console.error('❌ Project permission fetch failed:', response.status);
        setCanViewProject(false);
        setCanEditProject(false);
      }
    } catch (error) {
      console.error('❌ Project permission fetch error:', error);
      // Set default to false if fetch fails
      setCanViewProject(false);
      setCanEditProject(false);
    } finally {
      setLoadingPermissions(false);
      setInitialLoadingState(prev => ({ ...prev, permissions: true }));
    }
  };

  // Handle project creation
  const handleCreateProject = async () => {
    if (!newProject.project_name.trim()) return;

    setIsSubmittingProject(true);
    setProjectError('');

    try {
      const response = await axios.post(
        `${environment.apiUrl}/workspace/updateCustomProjectForOrg`,
        {
          data: {
            org_id: localStorage.getItem("org_id"),
            source: "10_org",
            project_items: [
              {
                project_name: newProject.project_name,
                project_description: newProject.project_description,
                project_type: "GVRN_DOCU",
                configuration: {
                  period_start_date: newProject.period_start_date,
                  period_end_date: newProject.period_end_date,
                  period_type: 'D'
                }
              }
            ]
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.upsertRef && response.data.upsertRef.length > 0) {
        // Fetch project details
        const projectDetails = await axios.post(
          `${environment.apiUrl}/workspace/getProjectDetails`,
          {
            data: {
              id: response.data.upsertRef[0]
            }
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
          }
        );

        setProjects(prev => [...prev, projectDetails.data]);
        setSelectedProject(projectDetails.data);
        // Update the project ID ref when new project is created and selected
        currentProjectIdRef.current = projectDetails.data.id;
        setIsProjectDialogOpen(false);
        setNewProject({
          project_name: '',
          project_description: '',
          period_start_date: new Date().toISOString().split('T')[0],
          period_end_date: new Date().toISOString().split('T')[0]
        });
        setSuccess('Project created successfully!');

        // Auto-assign permissions for the new project
        try {
          const autoAssignResponse = await axios.post(
            `${environment.apiUrl}/manage/project-permissions/auto-assign`,
            {
              projectId: projectDetails.data.id,
              userId: localStorage.getItem('user_email') || 'admin@example.com',
              organisationId: localStorage.getItem('org_id') || 'org123'
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json',
              },
            }
          );

          console.log('✅ Auto-assign permissions successful:', autoAssignResponse.data);

          // Refresh project permissions after auto-assign
          await fetchProjectPermissions();

        } catch (autoAssignError: any) {
          console.error('⚠️ Auto-assign permissions failed:', autoAssignError);
          // Don't show error to user as project creation was successful
          // Auto-assign is a convenience feature, not critical
        }
      }
    } catch (error) {
      console.error('Project creation error:', error);
      setProjectError('Failed to create project. Please try again.');
    } finally {
      setIsSubmittingProject(false);
    }
  };

  // Fetch docs when selectedProject changes
  useEffect(() => {
    if (selectedProject) {
      const isDashboardTab = topLevelTab === 0;
      const projectChanged = currentProjectIdRef.current !== selectedProject.id;

      // If project changed, clear old data and reset loading states
      if (projectChanged) {
        // Clear old documents from previous project
        setDocuments([]);
        setAllDocuments([]);
        setGroupedData([]);

        // Update the ref to track current project
        currentProjectIdRef.current = selectedProject.id;

        // Reset all loading states for new project
        setInitialLoadingState({
          projects: true, // Already loaded
          inputFields: true, // Already loaded
          contentTypes: true, // Already loaded
          documents: false, // Will be loaded for new project
          groupedData: !isDashboardTab, // Will be loaded if dashboard tab
          permissions: false, // Will be loaded for new project
          dashboardProcessed: false // Will be processed
        });

        // Always fetch new data when project changes
        // For dashboard tab, always use QUERY_RELEVANT_DOCUMENTS (no grouping)
        // For AI Summary tab, use QUERY_DOCUMENT_INSIGHTS (with grouping)
        const useGrouping = topLevelTab === 1; // AI Summary tab
        handleSearch(useGrouping);

        // If we're on dashboard tab, also fetch grouped data for charts
        if (isDashboardTab) {
          fetchGroupedDataForDashboard();
        }
      } else {
        // Same project, just tab change - check if data already exists
        const hasDocuments = documents.length > 0;
        const hasGroupedData = groupedData.length > 0;

        // Reset loading states when tab changes (same project)
        // If data already exists, mark it as complete immediately
        const newLoadingState = {
          projects: true, // Already loaded
          inputFields: true, // Already loaded
          contentTypes: true, // Already loaded
          documents: hasDocuments, // Already loaded if documents exist
          groupedData: !isDashboardTab || hasGroupedData, // Complete if not dashboard tab or if data exists
          permissions: true, // Already loaded for this project
          dashboardProcessed: (hasDocuments || hasGroupedData) && isDashboardTab && !loading // Already processed if data exists, on dashboard, and not currently loading
        };

        setInitialLoadingState(newLoadingState);

        // Only fetch if data doesn't exist
        if (!hasDocuments) {
          // For dashboard tab, always use QUERY_RELEVANT_DOCUMENTS (no grouping)
          // For AI Summary tab, use QUERY_DOCUMENT_INSIGHTS (with grouping)
          const useGrouping = topLevelTab === 1; // AI Summary tab
          handleSearch(useGrouping);
        }

        // If we're on dashboard tab, check if we need to fetch grouped data
        if (isDashboardTab) {
          if (!hasGroupedData) {
            fetchGroupedDataForDashboard();
          }
          // If data exists, the processDashboardData useEffect will handle marking dashboardProcessed as true
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, topLevelTab]);

  // RBAC: Fetch project permissions when project changes
  useEffect(() => {
    if (selectedProject) {
      fetchProjectPermissions();
    }
  }, [selectedProject]);

  // Process dashboard data when documents change or loading completes
  // Consolidated to avoid duplicate processing
  useEffect(() => {
    // Only process when not loading and we have data, or when project is selected with no data (empty state)
    // Also check if we're on dashboard tab
    if (!loading && topLevelTab === 0) {
      if (documents.length > 0 || groupedData.length > 0) {
        processDashboardData();
        setInitialLoadingState(prev => ({ ...prev, dashboardProcessed: true }));
      } else if (selectedProject && (documents.length === 0 && groupedData.length === 0)) {
        // If project is selected but no data, still process to show empty state
        processDashboardData();
        setInitialLoadingState(prev => ({ ...prev, dashboardProcessed: true }));
      }
    }
  }, [loading, documents, groupedData, selectedProject, topLevelTab]);

  // Handle case when switching back to dashboard tab with existing data
  // This ensures loader disappears immediately when data already exists
  useEffect(() => {
    if (topLevelTab === 0 && selectedProject && !loading) {
      const hasDocuments = documents.length > 0;
      const hasGroupedData = groupedData.length > 0;

      // If data exists and we're on dashboard, ensure all states are marked complete immediately
      if (hasDocuments || hasGroupedData) {
        setInitialLoadingState(prev => {
          // Only update if not already complete to avoid unnecessary re-renders
          if (prev.documents && (prev.groupedData || !hasGroupedData) && prev.dashboardProcessed) {
            return prev;
          }
          return {
            ...prev,
            documents: true,
            groupedData: hasGroupedData ? true : prev.groupedData, // Mark true only if we have grouped data
            dashboardProcessed: true
          };
        });
      }
    }
  }, [topLevelTab, selectedProject, documents.length, groupedData.length, loading]);

  // Check Google Drive auth status when component mounts or service is ready
  useEffect(() => {
    // Use a small delay to ensure service is fully initialized
    const timer = setTimeout(() => {
      if (googleDriveService.current) {
        // Check if there's an existing session ID in localStorage
        const existingSessionId = localStorage.getItem('googleDriveSessionId');
        if (existingSessionId) {
          // Check auth status with existing session ID
          googleDriveService.current.checkAuthStatus(existingSessionId);
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []); // Only run once when component mounts

  // Note: Removed duplicate useEffect that was causing double API calls
  // The useEffect at lines 2062-2075 already handles loading data when topLevelTab or selectedProject changes

  // Google Drive Functions
  const initiateGoogleDriveAuth = async () => {
    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    try {
      setIsGoogleDriveAuthInProgress(true);
      setError('');

      const result = await googleDriveService.current.initiateAuth();
      setGoogleDriveAuthUrl(result.authUrl);
      setShowGoogleDriveAuthDialog(true);

      const authWindow = googleDriveService.current.openAuthWindow(result.authUrl);
      if (authWindow) {
        setGoogleDriveAuthWindow(authWindow);
      }

      // Check for authentication completion
      const checkAuthStatus = setInterval(async () => {
        try {
          let isWindowClosed = false;
          let isOnCallbackPage = false;

          try {
            isWindowClosed = authWindow?.closed || false;
          } catch (error) {
            isWindowClosed = false;
          }

          // Check if window is on callback page (indicating successful auth)
          try {
            const currentUrl = authWindow?.location?.href || '';
            isOnCallbackPage = currentUrl.includes('/analytics/google-drive/auth/callback');
          } catch (error) {
            // Ignore cross-origin restrictions
          }

          if (isWindowClosed || isOnCallbackPage) {
            clearInterval(checkAuthStatus);
            setShowGoogleDriveAuthDialog(false);
            setGoogleDriveAuthWindow(null);
            setIsGoogleDriveAuthInProgress(false);

            // Test if authentication was successful using auth status endpoint
            try {
              // Get the sessionId we stored earlier
              const sessionId = localStorage.getItem('googleDriveSessionId');

              if (!sessionId) {
                setError('Authentication failed: No session ID found. Please try again.');
                return;
              }

              // Add a small delay to allow backend to process tokens
              setSuccess('Processing authentication...');
              await new Promise(resolve => setTimeout(resolve, 2000));

              // Try auth status check first
              if (!googleDriveService.current) {
                setError('Google Drive service not initialized');
                return;
              }
              let isAuthenticated = await googleDriveService.current.checkAuthStatus(sessionId);

              if (isAuthenticated) {
                setGoogleDriveAuthState((prev: GoogleDriveAuthState) => ({
                  ...prev,
                  isAuthenticated: true,
                  sessionId: sessionId
                }));

                // Load files and open dialog
                await loadGoogleDriveFilesWithSessionId(sessionId);
                setShowGoogleDriveDialog(true);
              } else {
                // If auth status says not authenticated but we have a sessionId, 
                // try to load files directly to see if authentication actually worked
                try {
                  setSuccess('Verifying authentication by loading files...');
                  // Try to load files directly to see if authentication actually worked
                  try {
                    const result = await googleDriveService.current.loadFiles(sessionId, undefined, undefined, 1, 5);

                    // Authentication actually worked, just the status endpoint is lagging
                    setGoogleDriveAuthState((prev: GoogleDriveAuthState) => ({
                      ...prev,
                      isAuthenticated: true,
                      sessionId: sessionId
                    }));

                    // Load files and open dialog
                    await loadGoogleDriveFilesWithSessionId(sessionId);
                    setShowGoogleDriveDialog(true);
                  } catch (fileError) {
                    // Try one more time with a longer delay
                    setSuccess('Authentication processing, please wait...');
                    await new Promise(resolve => setTimeout(resolve, 3000));

                    isAuthenticated = await googleDriveService.current.checkAuthStatus(sessionId);
                    if (isAuthenticated) {
                      setGoogleDriveAuthState((prev: GoogleDriveAuthState) => ({
                        ...prev,
                        isAuthenticated: true,
                        sessionId: sessionId
                      }));

                      // Load files and open dialog
                      await loadGoogleDriveFilesWithSessionId(sessionId);
                      setShowGoogleDriveDialog(true);
                    } else {
                      setError('Authentication was cancelled or failed. Please try again.');
                    }
                  }
                } catch (fileError) {
                  setError('Authentication was cancelled or failed. Please try again.');
                }
              }
            } catch (error) {
              setError('Authentication was cancelled or failed. Please try again.');
            }
          }
        } catch (error: any) {
          // Auth not complete yet
        }
      }, 2000);

      // Cleanup interval after 5 minutes
      setTimeout(() => {
        clearInterval(checkAuthStatus);
        if (authWindow && !authWindow.closed) {
          authWindow.close();
          setGoogleDriveAuthWindow(null);
        }
        setShowGoogleDriveAuthDialog(false);
        setIsGoogleDriveAuthInProgress(false);
      }, 300000); // 5 minutes timeout
    } catch (error: any) {
      setIsGoogleDriveAuthInProgress(false);

      if (error?.message === 'jwt_expired') {
        navigate('/login');
      } else {
        const errorMessage = error?.response?.data?.error || error?.message || 'Failed to initiate Google Drive authentication';
        setError(`Google Drive authentication failed: ${errorMessage}. Please try again.`);
      }
    }
  };

  const clearGoogleDriveAuth = async () => {
    if (!googleDriveService.current) {
      return;
    }

    try {
      // Clear any stored files first
      setGoogleDriveFiles([]);
      setSelectedGoogleFile(null);
      setSelectedGoogleFiles([]);

      // Clear authentication using service
      await googleDriveService.current.clearAuth(googleDriveAuthState.sessionId);

      setError('');
      setSuccess('Google Drive authentication cleared. You can now authenticate with a different account.');
    } catch (error) {
      console.error('Error clearing Google Drive auth:', error);
    }
  };

  const loadGoogleDriveFilesWithSessionId = async (sessionId: string, searchQuery?: string, folderId?: string) => {
    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    if (!sessionId) {
      setError('Session ID is required. Please authenticate with Google Drive first.');
      return;
    }

    try {
      if (searchQuery && searchQuery.trim()) {
        await googleDriveService.current.loadFiles(sessionId, searchQuery.trim());
      } else {
        await googleDriveService.current.loadFiles(sessionId, undefined, folderId, googleDriveCurrentPage, 1000);
      }
    } catch (error: any) {
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      } else if (error?.response?.data?.error === 'Session not authenticated') {
        setError('Session expired. Please authenticate with Google Drive again.');
        setGoogleDriveAuthState({
          isAuthenticated: false,
          sessionId: ''
        });
      } else {
        const errorMsg = `Failed to load Google Drive files: ${error?.response?.data?.error || error?.message || 'Unknown error'}`;
        setError(errorMsg);
      }
    }
  };

  const loadGoogleDriveFiles = async (searchQuery?: string) => {
    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    // Check if authenticated first
    if (!googleDriveAuthState.isAuthenticated) {
      setError('Please authenticate with Google Drive first. Click "Connect to Google Drive" to get started.');
      return;
    }

    // If we have a session ID, use it
    if (googleDriveAuthState.sessionId) {
      await loadGoogleDriveFilesWithSessionId(googleDriveAuthState.sessionId, searchQuery);
    } else {
      setError('No session ID found. Please authenticate with Google Drive again.');
    }
  };

  // Load files based on active tab
  const loadFilesForActiveTab = async () => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    try {
      setIsLoadingGoogleFiles(true);
      setError('');

      let result;

      switch (activeTab) {
        case 'myDrive':
          if (selectedTeamDrive) {
            result = await googleDriveService.current!.loadTeamDriveFiles(
              googleDriveAuthState.sessionId,
              selectedTeamDrive,
              50
            );
          } else {
            result = await googleDriveService.current!.loadMyDriveFiles(
              googleDriveAuthState.sessionId,
              50
            );
          }
          break;
        case 'shared':
          result = await googleDriveService.current!.loadSharedFiles(
            googleDriveAuthState.sessionId,
            50
          );
          break;
        case 'teamDrives':
          result = await googleDriveService.current!.loadTeamDrives(
            googleDriveAuthState.sessionId,
            50
          );
          setTeamDrives(result.drives);
          return; // Don't set files for team drives tab
      }

      setGoogleDriveFiles(result.files);
      setGoogleDriveTotalFiles(result.totalFiles);
    } catch (error: any) {
      console.error('Error loading files for tab:', error);
      if (error.message === 'jwt_expired') {
        navigate('/login');
      } else if (error.message === 'Session not authenticated') {
        setError('Session expired. Please authenticate with Google Drive again.');
      } else {
        setError(error.message || 'Failed to load files');
      }
    } finally {
      setIsLoadingGoogleFiles(false);
    }
  };

  // Handle tab switching
  const handleTabChange = (tab: 'myDrive' | 'shared' | 'teamDrives') => {
    setActiveTab(tab);
    setSelectedTeamDrive(null);
    setCurrentFolderId('root');
    setFolderBreadcrumb([]);
    setGoogleDriveSearchQuery('');
    setSelectedGoogleFile(null);
    setSelectedGoogleFiles([]);
  };

  // Handle team drive selection
  const handleTeamDriveSelect = (teamDriveId: string) => {
    setSelectedTeamDrive(teamDriveId);
    setCurrentFolderId('root');
    setFolderBreadcrumb([]);
  };

  // Get appropriate icon for file type
  const getFileTypeIcon = (mimeType: string) => {
    if (mimeType?.includes('image')) return '🖼️';
    if (mimeType?.includes('pdf')) return '📄';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return '📝';
    if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return '📊';
    if (mimeType?.includes('powerpoint') || mimeType?.includes('presentation')) return '📈';
    if (mimeType?.includes('text')) return '📄';
    if (mimeType?.includes('video')) return '🎥';
    if (mimeType?.includes('audio')) return '🎵';
    if (mimeType?.includes('zip') || mimeType?.includes('archive')) return '📦';
    if (mimeType?.includes('code') || mimeType?.includes('javascript') || mimeType?.includes('python')) return '💻';
    return '📄';
  };

  // Get file type label
  const getFileTypeLabel = (mimeType: string) => {
    if (mimeType?.includes('image')) return 'IMAGE';
    if (mimeType?.includes('pdf')) return 'PDF';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return 'DOC';
    if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return 'XLS';
    if (mimeType?.includes('powerpoint') || mimeType?.includes('presentation')) return 'PPT';
    if (mimeType?.includes('text')) return 'TXT';
    if (mimeType?.includes('video')) return 'VIDEO';
    if (mimeType?.includes('audio')) return 'AUDIO';
    if (mimeType?.includes('zip') || mimeType?.includes('archive')) return 'ZIP';
    if (mimeType?.includes('code') || mimeType?.includes('javascript') || mimeType?.includes('python')) return 'CODE';
    return 'FILE';
  };

  // Enhanced image preview component with fallback
  const ImagePreview = ({ file }: { file: GoogleDriveFile }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);

    // Try to load thumbnail if available
    const hasThumbnail = file.thumbnailLink && !imageError;

    // Get the same background gradient as other file types
    const getImageBackground = (mimeType: string) => {
      if (mimeType?.includes('image')) {
        // Use a beautiful gradient for images
        return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      }
      return 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)';
    };

    return (
      <Box sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        borderRadius: '8px',
        overflow: 'hidden',
        background: getImageBackground(file.mimeType),
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(1px)'
        }
      }}>
        {hasThumbnail && imageLoading ? (
          <Box
            component="img"
            src={file.thumbnailLink}
            alt={file.name}
            onLoad={() => setImageLoading(false)}
            onError={() => {
              setImageError(true);
              setImageLoading(false);
            }}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '8px',
              position: 'relative',
              zIndex: 1
            }}
          />
        ) : (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            height: '100%',
            width: '100%',
            zIndex: 1,
            position: 'relative'
          }}>
            <Typography sx={{
              fontSize: '2.5rem',
              mb: 0.5,
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
            }}>
              {getFileTypeIcon(file.mimeType)}
            </Typography>
            <Typography sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              opacity: 0.9,
              textAlign: 'center'
            }}>
              {getFileTypeLabel(file.mimeType)}
            </Typography>
          </Box>
        )}

        {/* Loading overlay */}
        {imageLoading && hasThumbnail && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            borderRadius: '8px',
            zIndex: 2
          }}>
            <CircularProgress size={24} sx={{ color: '#9C27B0' }} />
          </Box>
        )}
      </Box>
    );
  };

  // Check authentication status
  const checkGoogleDriveAuthStatus = async (sessionId: string) => {
    if (!googleDriveService.current) {
      return false;
    }

    try {
      return await googleDriveService.current.checkAuthStatus(sessionId);
    } catch (error: any) {
      return false;
    }
  };

  // Handle session recovery and re-authentication
  const handleSessionRecovery = async () => {
    // Clear the current session
    const currentSessionId = googleDriveAuthState.sessionId || localStorage.getItem('googleDriveSessionId');
    if (currentSessionId) {
      try {
        // Try to revoke the current session
        if (googleDriveService.current) {
          await googleDriveService.current.clearAuth(currentSessionId);
        }
      } catch (error) {
        // Ignore revocation errors
      }
    }

    // Clear local state
    setGoogleDriveAuthState({
      isAuthenticated: false,
      sessionId: ''
    });
    localStorage.removeItem('googleDriveSessionId');

    // Start fresh authentication
    setError('');
    setSuccess('Starting fresh authentication...');
    await initiateGoogleDriveAuth();
  };

  const downloadGoogleDriveFile = async (file: GoogleDriveFile) => {
    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    setIsDownloadingGoogleFile(true);
    setGoogleDriveDownloadProgress(0);
    setSelectedGoogleFile(file);

    try {
      // Get download URL using service
      const downloadUrl = await googleDriveService.current.getDownloadUrl(googleDriveAuthState.sessionId, file.id);

      // Download file using service
      const downloadedFile = await googleDriveService.current.downloadFile(
        downloadUrl,
        file.name,
        (progress) => setGoogleDriveDownloadProgress(progress)
      );

      // Process the downloaded file using existing upload flow
      await handleFileUpload({ target: { files: [downloadedFile] } } as any);

      setSuccess(`Successfully downloaded and processed ${file.name}`);
      setShowGoogleDriveDialog(false);
      setSelectedGoogleFile(null);
    } catch (error: any) {
      console.error('Error downloading Google Drive file:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      setError(`Failed to download ${file.name}`);
    } finally {
      setIsDownloadingGoogleFile(false);
      setGoogleDriveDownloadProgress(0);
    }
  };

  // checkDownloadStatus function removed - not used in DocsInsights

  // Get file preview information
  const getFilePreviewInfo = async (fileId: string) => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    try {
      await googleDriveService.current.getFilePreviewInfo(
        googleDriveAuthState.sessionId,
        fileId
      );
    } catch (error: any) {
      console.error('Error getting file preview info:', error);
      if (error?.message === 'jwt_expired') {
        navigate('/login');
      }
      setError('Failed to get file preview information');
    }
  };

  const getFileInfo = async (file: GoogleDriveFile) => {
    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    setIsLoadingFileInfo(true);
    try {
      await googleDriveService.current.getFileInfo(googleDriveAuthState.sessionId, file.id);
    } catch (error: any) {
      console.error('Error getting file info:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      setError('Failed to get file information');
    } finally {
      setIsLoadingFileInfo(false);
    }
  };

  const downloadMultipleGoogleDriveFiles = async (files: GoogleDriveFile[]) => {
    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    setIsDownloadingMultipleFiles(true);
    setGoogleDriveDownloadProgress(0);

    try {
      // Download multiple files using service
      const downloadedFiles = await googleDriveService.current.downloadMultipleFiles(
        googleDriveAuthState.sessionId,
        files,
        (progress) => setGoogleDriveDownloadProgress(progress)
      );

      if (downloadedFiles.length > 0) {
        setGoogleDriveDownloadProgress(100);

        // Process the first file (or you could implement a way to handle multiple files)
        await handleFileUpload({ target: { files: [downloadedFiles[0]] } } as any);

        setSuccess(`Successfully downloaded and processed ${downloadedFiles.length} files`);
        setShowGoogleDriveDialog(false);
        setSelectedGoogleFiles([]);
      } else {
        setError('No files were successfully downloaded');
      }
    } catch (error: any) {
      console.error('Error downloading multiple Google Drive files:', error);
      if (error?.response?.data?.error === 'jwt malformed' || error?.response?.data?.error === 'jwt expired') {
        navigate('/login');
      }
      setError(`Failed to download ${files.length} files`);
    } finally {
      setIsDownloadingMultipleFiles(false);
      setGoogleDriveDownloadProgress(0);
    }
  };

  const handleGoogleDriveFileSelect = (file: GoogleDriveFile, event: React.MouseEvent) => {
    if (event.ctrlKey || event.metaKey) {
      // Multi-select mode
      setSelectedGoogleFiles(prev => {
        const isSelected = prev.some(f => f.id === file.id);
        if (isSelected) {
          return prev.filter(f => f.id !== file.id);
        } else {
          return [...prev, file];
        }
      });
      setSelectedGoogleFile(null);
    } else {
      // Single select mode
      setSelectedGoogleFile(file);
      setSelectedGoogleFiles([]);
    }
  };

  const openGoogleDriveDialog = () => {
    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    // Clear any previous errors when starting fresh
    setError('');

    if (!googleDriveAuthState.isAuthenticated) {
      // If not authenticated, start the authentication process
      initiateGoogleDriveAuth();
    } else {
      // If already authenticated, show the file selection dialog
      setShowGoogleDriveDialog(true);
      loadGoogleDriveFiles();
    }
  };

  const searchGoogleDriveFiles = async () => {
    // Frontend-only search - no API calls needed
    // Files are filtered in real-time as user types
  };



  const closeGoogleDriveDialog = () => {
    setShowGoogleDriveDialog(false);
    setSelectedGoogleFile(null);
    setSelectedGoogleFiles([]);
    setGoogleDriveSearchQuery('');
    setGoogleDriveCurrentPage(1);
    setCurrentFolderId('root');
    setFolderBreadcrumb([]);
    setShowFileInfo(false);
    setFileInfo(null);
    setShowFolderTree(false);
    setFolderTree([]);
  };

  const formatFileSize = (bytes: string) => {
    if (!googleDriveService.current) return '0 Bytes';
    return googleDriveService.current.formatFileSize(bytes);
  };

  const formatDate = (dateString: string) => {
    if (!googleDriveService.current) return new Date(dateString).toLocaleDateString();
    return googleDriveService.current.formatDate(dateString);
  };

  const getFileIcon = (mimeType: string) => {
    if (!googleDriveService.current) return '📁';
    return googleDriveService.current.getFileIcon(mimeType);
  };

  // Navigate to a specific folder
  const navigateToFolder = async (folderId: string) => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    setCurrentFolderId(folderId);
    setGoogleDriveCurrentPage(1); // Reset to first page

    // Load files and get breadcrumb for the folder
    await Promise.all([
      loadGoogleDriveFilesWithSessionId(googleDriveAuthState.sessionId, undefined, folderId),
      getFolderBreadcrumb(folderId)
    ]);
  };

  // Navigate to breadcrumb item
  const navigateToBreadcrumb = async (folderId: string) => {
    await navigateToFolder(folderId);
  };

  // Get folder breadcrumb
  const getFolderBreadcrumb = async (folderId: string) => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return [];
    }

    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return [];
    }

    try {
      const breadcrumb = await googleDriveService.current.getFolderBreadcrumb(
        googleDriveAuthState.sessionId,
        folderId
      );
      return breadcrumb;
    } catch (error: any) {
      console.error('Error getting breadcrumb:', error);
      if (error?.message === 'jwt_expired') {
        navigate('/login');
      } else if (error?.message === 'Session not authenticated') {
        setError('Session expired. Please authenticate with Google Drive again.');
        setGoogleDriveAuthState({
          isAuthenticated: false,
          sessionId: ''
        });
      }
      return [];
    }
  };

  // Load folder tree structure
  const loadFolderTree = async () => {
    if (!googleDriveAuthState.isAuthenticated || !googleDriveAuthState.sessionId) {
      setError('Please authenticate with Google Drive first.');
      return;
    }

    if (!googleDriveService.current) {
      setError('Google Drive service not initialized');
      return;
    }

    try {
      setIsLoadingFolderTree(true);
      setError('');

      await googleDriveService.current.loadFolderTree(
        googleDriveAuthState.sessionId,
        'root',
        5
      );
    } catch (error: any) {
      console.error('Error loading folder tree:', error);
      if (error?.message === 'jwt_expired') {
        navigate('/login');
      } else if (error?.message === 'Session not authenticated') {
        setError('Session expired. Please authenticate with Google Drive again.');
        setGoogleDriveAuthState({
          isAuthenticated: false,
          sessionId: ''
        });
      } else {
        setError('Failed to load folder structure');
      }
    } finally {
      setIsLoadingFolderTree(false);
    }
  };

  // New function to fetch grouped data for dashboard display only
  const fetchGroupedDataForDashboard = async () => {
    if (!selectedProject?.id) {
      setInitialLoadingState(prev => ({ ...prev, groupedData: true }));
      return;
    }

    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            topic: selectedTopic || '',
            keywords: keywords || [],
            contentType: "CIRCULAR",
            campaignId: selectedProject.id,
            pageSize: 500,
            pageNumber: 1,
          },
          agent: {
            code: "QUERY_DOCUMENT_INSIGHTS",
            groupBy: "contentClass"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );

      const responseData = response.data.data || [];

      if (Array.isArray(responseData) && responseData.length > 0 && responseData[0].groupName) {
        // Set the grouped data so processDashboardData can use it
        setGroupedData(responseData);

        // Also update dashboard grouped data for display
        setDashboardData(prev => ({
          ...prev,
          documentsByGroup: responseData.map(group => ({
            group: group.groupName,
            count: group.items.length
          }))
        }));
      }
      setInitialLoadingState(prev => ({ ...prev, groupedData: true }));
    } catch (error) {
      // Silently fail - don't affect existing functionality
      // Mark as complete even on error to prevent infinite loading
      setInitialLoadingState(prev => ({ ...prev, groupedData: true }));
    }
  };

  // Lightweight semantic deduplication helpers for topic labels
  // Normalizes a text by lowercasing, trimming, removing punctuation and collapsing spaces
  const normalizeText = (text: string): string => {
    return (text || '')
      .toLowerCase()
      .replace(/[\p{P}\p{S}]+/gu, ' ') // remove punctuation/symbols (unicode aware)
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Tokenize and lightly stem words (basic plural removal)
  const tokenize = (text: string): string[] => {
    return normalizeText(text)
      .split(' ')
      .filter(Boolean)
      .map((t) => (t.length > 4 && t.endsWith('s') ? t.slice(0, -1) : t));
  };

  // Jaccard similarity between two token sets
  const jaccardSimilarity = (a: Set<string>, b: Set<string>): number => {
    if (a.size === 0 && b.size === 0) return 1;
    let intersection = 0;
    for (const token of a) {
      if (b.has(token)) intersection += 1;
    }
    const union = a.size + b.size - intersection;
    return union === 0 ? 0 : intersection / union;
  };

  // Checks if two phrases are semantically similar enough using token-based Jaccard and substring inclusion
  const areSemanticallySimilar = (a: string, b: string, threshold = 0.65): boolean => {
    const na = normalizeText(a);
    const nb = normalizeText(b);
    if (!na || !nb) return false;
    if (na === nb) return true;
    // direct substring containment helps merge variants like "russia" vs "russian sanctions"
    if (na.length >= 4 && nb.includes(na)) return true;
    if (nb.length >= 4 && na.includes(nb)) return true;
    const sa = new Set(tokenize(na));
    const sb = new Set(tokenize(nb));
    return jaccardSimilarity(sa, sb) >= threshold;
  };

  // Merge similar topics by accumulating counts under a canonical label
  const mergeSimilarTopicsCount = (topicCounts: { [key: string]: number }, threshold = 0.65): { [key: string]: number } => {
    const entries = Object.entries(topicCounts).sort((a, b) => b[1] - a[1]); // high count first
    const canonLabels: string[] = [];
    const canonMap: { [canonical: string]: number } = {};

    for (const [label, count] of entries) {
      let merged = false;
      for (let i = 0; i < canonLabels.length; i++) {
        const canonical = canonLabels[i];
        if (areSemanticallySimilar(label, canonical, threshold)) {
          canonMap[canonical] = (canonMap[canonical] || 0) + count;
          merged = true;
          break;
        }
      }
      if (!merged) {
        canonLabels.push(label);
        canonMap[label] = (canonMap[label] || 0) + count;
      }
    }

    return canonMap;
  };

  const processDashboardData = () => {
    // For dashboard, use documents from QUERY_RELEVANT_DOCUMENTS for counts
    // but use grouped data (from QUERY_DOCUMENT_INSIGHTS) for highlights if available
    const allDocs = topLevelTab === 0
      ? documents
      : (groupedData.length > 0 ? groupedData.flatMap(group => group.items) : documents);

    // For highlights processing, prefer grouped data as it has more complete information
    const docsForHighlights = topLevelTab === 0 && groupedData.length > 0
      ? groupedData.flatMap(group => group.items)
      : allDocs;

    if (allDocs.length === 0) {
      // Set default values for empty state
      setDashboardData({
        totalDocuments: 0,
        totalTopics: 0,
        totalHighlights: 0,
        topTopics: [],
        topSources: [],
        documentsByGroup: [],
        recentDocuments: [],
        topicsDistribution: [],
        highlightsTimeline: [],
        recentHighlights: [],
        highlightsByTopic: []
      });
      return;
    }



    // Count topics and highlights
    const topicCounts: { [key: string]: number } = {};
    const sourceCounts: { [key: string]: number } = {};
    let totalHighlights = 0;

    allDocs.forEach((doc, index) => {
      // Count topics - try multiple possible field names
      const topics = getArrayValue<string>(doc, 'topics') ||
        getArrayValue<string>(doc, 'Topics') ||
        getArrayValue<string>(doc, 'topic');

      topics.forEach(topic => {
        topicCounts[topic] = (topicCounts[topic] || 0) + 1;
      });

      // Count highlights - try multiple possible field names
      const highlights = getArrayValue<string>(doc, 'highlights') ||
        getArrayValue<string>(doc, 'Highlights') ||
        getArrayValue<string>(doc, 'highlight');

      totalHighlights += highlights.length;

      // Count sources - try multiple possible field names
      const sourceName = getStringValue(doc, 'sourceName') ||
        getStringValue(doc, 'source') ||
        getStringValue(doc, 'Source') ||
        getStringValue(doc, 'source_name');

      if (sourceName) {
        sourceCounts[sourceName] = (sourceCounts[sourceName] || 0) + 1;
      }
    });

    // Deduplicate semantically similar topics before ranking
    const dedupedTopicCounts = mergeSimilarTopicsCount(topicCounts, 0.65);

    // Get top topics from deduplicated counts
    let topTopics = Object.entries(dedupedTopicCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Use only real data - if no topics found, keep empty array

    // Get top sources
    const topSources = Object.entries(sourceCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Get documents by group
    let documentsByGroup = groupedData.length > 0
      ? groupedData.map(group => ({ group: group.groupName, count: group.items.length }))
      : [];

    // Use only real data - no sample data
    // If no grouped data found, keep empty array

    // Get recent documents (last 5) - use docsForHighlights for better data
    const recentDocuments = docsForHighlights.slice(-5);

    // Get topics distribution for pie chart
    const topicsDistribution = Object.entries(topicCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    // Use only real data - no fallbacks
    const finalTopTopics = topTopics;
    const finalTopSources = topSources;
    const finalDocumentsByGroup = documentsByGroup;

    // Collect recent highlights from documents and group by topic
    const recentHighlights: { text: string, documentTitle: string, documentId: string }[] = [];
    const topicToHighlights: Record<string, { text: string, documentTitle: string, documentId: string, docDateMs?: number }[]> = {};

    docsForHighlights.forEach((doc, docIndex) => {
      const highlights = getArrayValue<string>(doc, 'highlights') ||
        getArrayValue<string>(doc, 'Highlights') ||
        getArrayValue<string>(doc, 'highlight');

      const title = getStringValue(doc, 'title') || 'Untitled Document';
      const dateStr = getStringValue(doc, 'publishedAt') || getStringValue(doc, 'published_date') || getStringValue(doc, 'createdAt') || getStringValue(doc, 'date');
      const docDateMs = dateStr ? Date.parse(dateStr) : undefined;
      // Gather possible topic fields from doc and remap through semantic canonical labels
      const rawTopics = getArrayValue<string>(doc, 'topics') ||
        getArrayValue<string>(doc, 'Topics') ||
        getArrayValue<string>(doc, 'topic');
      // Create a mapping from raw label -> canonical label using mergeSimilarTopicsCount ordering
      const canonicalLabels = Object.keys(dedupedTopicCounts);
      const mapToCanonical = (label: string): string => {
        for (const canon of canonicalLabels) {
          if (areSemanticallySimilar(label, canon, 0.65)) return canon;
        }
        return label;
      };
      const topics = rawTopics.map(mapToCanonical);

      highlights.slice(0, 2).forEach(highlight => {
        recentHighlights.push({
          text: highlight,
          documentTitle: title,
          documentId: doc.docUID
        });

        // Map highlight under each topic present in the document
        (topics && topics.length > 0 ? topics : ['General']).forEach(topic => {
          const t = topic || 'General';
          if (!topicToHighlights[t]) topicToHighlights[t] = [];
          // Limit per-topic list growth to keep UI performant
          if (topicToHighlights[t].length < 50) {
            topicToHighlights[t].push({ text: highlight, documentTitle: title, documentId: doc.docUID, docDateMs });
          }
        });
      });
    });

    // Helper: choose dominant highlight using frequency + cue-word scoring
    const chooseDominantHighlight = (
      topicLabel: string,
      items: { text: string; documentTitle: string; documentId: string; docDateMs?: number }[]
    ): { text: string; documentTitle: string; documentId: string; docDateMs?: number } | undefined => {
      if (!items || items.length === 0) return undefined;
      const normalize = (s: string) => (s || '').trim().toLowerCase();
      const freq = new Map<string, number>();
      const score = new Map<string, number>();
      const topicTokens = new Set(tokenize(topicLabel || ''));
      const times = items.map(i => i.docDateMs || 0);
      const maxT = Math.max(...times);
      const minT = Math.min(...times);
      const normTime = (t: number) => {
        if (!isFinite(t) || maxT === minT) return 0;
        return (t - minT) / (maxT - minT);
      };
      for (const it of items) {
        const key = normalize(it.text);
        const base = (freq.get(key) || 0) + 1;
        freq.set(key, base);
      }
      for (const it of items) {
        const key = normalize(it.text);
        const baseFreq = freq.get(key) || 1;
        const textTokens = new Set(tokenize(it.text));
        const topicality = jaccardSimilarity(topicTokens, textTokens); // 0..1 dynamic match
        const lengthBonus = Math.min(it.text.length, 220) / 220 * 0.3; // mild, data-agnostic
        const recencyBonus = normTime(it.docDateMs || 0) * 0.4; // normalized within topic
        const s = baseFreq + topicality + lengthBonus + recencyBonus;
        score.set(key, Math.max(score.get(key) || 0, s));
      }
      // pick max by score; return the first matching original item to preserve casing
      let bestKey = '';
      let bestScore = -Infinity;
      for (const [k, v] of score.entries()) {
        if (v > bestScore) { bestScore = v; bestKey = k; }
      }
      return items.find(it => normalize(it.text) === bestKey) || items[0];
    };

    setDashboardData(prev => ({
      totalDocuments: allDocs.length,
      totalTopics: Object.keys(topicCounts).length,
      totalHighlights: totalHighlights,
      topTopics: finalTopTopics,
      topSources: finalTopSources,
      documentsByGroup: prev.documentsByGroup.length > 0 ? prev.documentsByGroup : finalDocumentsByGroup,
      recentDocuments,
      topicsDistribution,
      highlightsTimeline: [],
      recentHighlights: recentHighlights.slice(0, 8),
      highlightsByTopic: Object.entries(topicToHighlights).map(([topic, items]) => {
        const latestDateMs = items.reduce((acc, it) => Math.max(acc, it.docDateMs || 0), 0);
        // choose dominant highlight by frequency + cue-word scoring
        const dominant = chooseDominantHighlight(topic, items) || items[0];
        return {
          topic,
          highlights: items
            .sort((a, b) => (b.docDateMs || 0) - (a.docDateMs || 0))
            .slice(0, 10),
          latestDateMs,
          mentions: items.length,
          dominantHighlight: dominant
        };
      })
    }));
  };

  // Clear selected document when tab changes
  useEffect(() => {
    setSelectedDocument(null);
    // Chat state is now managed by the reusable Chatbot component
    // No need to reset messages, newMessage, or suggestedQuestions

    // Reset visible items for new tab
    resetVisibleItems();
  }, [selectedTab, groupedData]);

  // Reset source type filter when changing top-level tabs
  useEffect(() => {
    if (topLevelTab === 2) {
      // Reset to 'all' when entering Documents tab
      setSelectedSourceType('all');
    }
  }, [topLevelTab]);

  // Reset selected document when source type filter changes
  useEffect(() => {
    setSelectedDocument(null);
  }, [selectedSourceType]);

  // Clear document export mode when switching tabs
  useEffect(() => {
    setIsDocumentExportMode(false);
    setSelectedDocuments(new Set());
  }, [topLevelTab]);

  // Load files when tab changes or dialog opens
  useEffect(() => {
    if (showGoogleDriveDialog && googleDriveAuthState.isAuthenticated && googleDriveAuthState.sessionId) {
      loadFilesForActiveTab();
    }
  }, [activeTab, showGoogleDriveDialog, googleDriveAuthState.isAuthenticated, googleDriveAuthState.sessionId]);

  // Load files when team drive is selected
  useEffect(() => {
    if (selectedTeamDrive && activeTab === 'myDrive' && googleDriveAuthState.isAuthenticated && googleDriveAuthState.sessionId) {
      loadFilesForActiveTab();
    }
  }, [selectedTeamDrive, activeTab, googleDriveAuthState.isAuthenticated, googleDriveAuthState.sessionId]);

  // Export Content helper functions
  const audienceOptions = [
    'Retail bank users',
    'Corporate clients',
    'Small business owners',
    'Internal employees',
    'Investment clients',
    'Insurance customers',
    'Loan applicants',
    'Credit card users'
  ];

  const handleExportMode = () => {
    setIsExportMode(true);
    setSelectedPairs(new Set());
  };

  const handleCancelExport = () => {
    setIsExportMode(false);
    setSelectedPairs(new Set());
  };

  const handlePairSelection = (pairId: string) => {
    const newSelected = new Set(selectedPairs);
    if (newSelected.has(pairId)) {
      newSelected.delete(pairId);
    } else {
      newSelected.add(pairId);
    }
    setSelectedPairs(newSelected);
  };

  const handleExport = () => {
    if (selectedPairs.size === 0) return;

    setIsExportMode(false);
    setShowExportDialog(true);
    setCurrentStep(1);
  };

  const handleCloseExportDialog = () => {
    setShowExportDialog(false);
    setSelectedPairs(new Set());
    setSelectedDocuments(new Set());
    setCurrentStep(1);
  };

  // Document export handlers
  const handleDocumentExportMode = () => {
    setIsDocumentExportMode(true);
    setSelectedDocuments(new Set());
  };

  const handleCancelDocumentExport = () => {
    setIsDocumentExportMode(false);
    setSelectedDocuments(new Set());
  };

  const handleDocumentSelection = (uuid: string) => {
    const newSelected = new Set(selectedDocuments);

    if (newSelected.has(uuid)) {
      newSelected.delete(uuid);
    } else {
      newSelected.add(uuid);
    }

    setSelectedDocuments(newSelected);
  };

  const handleDocumentExport = () => {
    if (selectedDocuments.size === 0) return;

    setIsDocumentExportMode(false);
    setShowExportDialog(true);
    setCurrentStep(1);
  };

  const handleNextStep = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleBrandDetailsChange = (field: keyof BrandDetails, value: string) => {
    setBrandDetails(prev => ({ ...prev, [field]: value }));
  };

  const handleAudienceToggle = (audience: string) => {
    setBrandDetails(prev => ({
      ...prev,
      targetAudience: prev.targetAudience.includes(audience)
        ? prev.targetAudience.filter(a => a !== audience)
        : [...prev.targetAudience, audience]
    }));
  };

  const handleAddCustomAudience = () => {
    if (newAudience.trim() && !brandDetails.targetAudience.includes(newAudience.trim())) {
      setBrandDetails(prev => ({
        ...prev,
        targetAudience: [...prev.targetAudience, newAudience.trim()]
      }));
      setNewAudience('');
    }
  };

  const handleRemoveAudience = (audience: string) => {
    setBrandDetails(prev => ({
      ...prev,
      targetAudience: prev.targetAudience.filter(a => a !== audience)
    }));
  };

  const handleContentPreferenceChange = (field: keyof ContentPreferences, value: string) => {
    setContentPreferences(prev => ({ ...prev, [field]: value }));
  };

  const isContentLengthApplicable = () => {
    const textBasedFormats = ['Text Only', 'Video'];
    return textBasedFormats.includes(contentPreferences.format);
  };

  // Helper function to map content preferences to API format
  const mapContentPreferencesToAPI = () => {
    const contentTypeMap: { [key: string]: string } = {
      'SOCIAL_POST_INSTAGRAM': 'SOCIAL_POST_INSTAGRAM',
      'SOCIAL_POST_FACEBOOK': 'SOCIAL_POST_FACEBOOK',
      'SOCIAL_POST_LINKEDIN': 'SOCIAL_POST_LINKEDIN',
      'SOCIAL_POST_TWITTER': 'SOCIAL_POST_TWITTER',
      'BLOG_POST': 'BLOG_POST',
      'EMAIL': 'EMAIL',
      'VIDEO': 'VIDEO',
      'SHORT_MESSAGE': 'SHORT_MESSAGE',
      'JOB_DESCRIPTION': 'JOB_DESCRIPTION',
      'CUSTOM': 'CUSTOM'
    };

    const writingStyleMap: { [key: string]: string } = {
      'Professional': 'professional',
      'Casual': 'casual',
      'Technical': 'technical',
      'Creative': 'creative'
    };

    return {
      contentType: contentTypeMap[contentPreferences.category] || 'SOCIAL_POST_LINKEDIN',
      writingStyle: writingStyleMap[contentPreferences.style] || 'professional',
      tone: 'warm', // Default tone
      audience: brandDetails.targetAudience.length > 0 ? brandDetails.targetAudience[0] : 'general',
      guidelines: contentPreferences.instructions || ''
    };
  };

  // Helper function to extract interaction UIDs from selected pairs
  // Note: Export functionality is now handled by the reusable Chatbot component
  // This function returns empty array as Chatbot manages its own messages and export
  const getInteractionUIDs = (): string[] => {
    // Export functionality is now handled by the reusable Chatbot component
    // The Chatbot component has its own export functionality built-in
    return [];
  };

  const generateContentFromAPI = async (): Promise<GeneratedContent> => {
    // Check if we're generating from documents or conversations
    const isDocumentExport = selectedDocuments.size > 0;
    const interactionUIDs = isDocumentExport ? [] : getInteractionUIDs();
    // Send UUIDs directly for document export
    const docUIDs = isDocumentExport ? Array.from(selectedDocuments) : [];
    const apiConfig = mapContentPreferencesToAPI();

    console.log('Generate Content API - Document Export Mode:', isDocumentExport);
    console.log('Generate Content API - Interaction UIDs:', interactionUIDs);
    console.log('Generate Content API - Document UUIDs (for API):', docUIDs);
    console.log('Generate Content API - API Config:', apiConfig);

    const payload = isDocumentExport ? {
      data: {
        docUIDs: docUIDs,
        instructions: {
          keywords: brandDetails.industryKeywords.split(',').map(k => k.trim()).filter(k => k),
          writing_style: apiConfig.writingStyle,
          tone: apiConfig.tone,
          audience: apiConfig.audience,
          guidelines: apiConfig.guidelines
        },
        contentType: apiConfig.contentType
      },
      agent: {
        code: "GENERATE_CONTENT_FROM_DOCUMENT_INTEL"
      }
    } : {
      data: {
        interactionUIDs: interactionUIDs,
        instructions: {
          keywords: brandDetails.industryKeywords.split(',').map(k => k.trim()).filter(k => k),
          writing_style: apiConfig.writingStyle,
          tone: apiConfig.tone,
          audience: apiConfig.audience,
          guidelines: apiConfig.guidelines
        },
        contentType: apiConfig.contentType
      },
      agent: {
        code: "GENERATE_CONTENT_FROM_CONVERSATION"
      }
    };

    console.log('Generate Content API - Payload:', payload);

    const response = await axios.post(
      `${environment.controlplaneUrl}/agent/flow/execute`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      }
    );

    console.log('Generate Content API - Response:', response.data);

    if (response.data.status === 200 && response.data.data && response.data.data.length > 0) {
      // Process all DICTIONARY objects to extract all entries
      const allEntries: any[] = [];

      response.data.data.forEach((item: any) => {
        if (item.objectType === 'DICTIONARY' && item.entries) {
          allEntries.push(...item.entries);
        }
      });

      console.log('Generate Content - All Entries:', allEntries);

      // Helper to get value from all entries
      const getEntryValue = (key: string) => {
        const entry = allEntries.find((e: any) => e.key === key);
        return entry ? entry.value : '';
      };

      // Extract data from merged entries
      const titleText = getEntryValue('title') || (isDocumentExport ? 'Generated Document Insights' : 'Generated Content');
      const contentText = getEntryValue('text') || getEntryValue('response') || getEntryValue('content') || '';

      const result = {
        title: titleText,
        content: contentText,
        format: contentPreferences.format,
        category: contentPreferences.category,
        query: getEntryValue('query') || '',
        followupQuestions: getEntryValue('followupQuestions') || [],
        sourceLinks: getEntryValue('sourceLinks') || [],
        visualization: getEntryValue('visualization') || undefined
      };

      console.log('Generate Content - Final Result:', result);
      return result;
    } else {
      throw new Error('No content generated from API');
    }
  };

  const generateDummyContent = (): GeneratedContent => {
    return {
      title: 'Sample Generated Content',
      content: 'This is a sample generated content based on your conversation. In a real implementation, this would be generated by the AI based on your selected conversation pairs and preferences.',
      format: contentPreferences.format,
      category: contentPreferences.category,
      query: 'Sample query: What are the key insights from the conversation?',
      followupQuestions: ['What would you like to know more about?', 'Would you like to explore other topics?'],
      sourceLinks: ['https://example.com/source1', 'https://example.com/source2']
    };
  };

  const handleGenerateContent = async () => {
    setIsGeneratingContent(true);
    try {
      const content = await generateContentFromAPI();
      console.log('Generated Content for Display:', content);

      if (!content.content || content.content.trim() === '') {
        throw new Error('Generated content is empty');
      }

      setGeneratedContent(content);
      setShowContentDisplay(true);
      setShowExportDialog(false); // Close the export dialog
    } catch (error: any) {
      console.error('Error generating content:', error);
      setError(error?.message || 'Failed to generate content. Please try again.');
    } finally {
      setIsGeneratingContent(false);
    }
  };

  const handleStartEditingGeneratedContent = () => {
    if (!generatedContent) return;
    setIsEditingGeneratedContent(true);
    setEditableGeneratedTitle(generatedContent.title || '');
    setEditableGeneratedBody(generatedContent.content || '');
  };

  const handleSaveEditedGeneratedContent = () => {
    if (!generatedContent) return;
    const updatedContent = {
      ...generatedContent,
      title: editableGeneratedTitle.trim() || generatedContent.title,
      content: editableGeneratedBody
    };
    setGeneratedContent(updatedContent);
    setIsEditingGeneratedContent(false);
    setSuccess('Content updated successfully!');
  };

  const handleCancelEditingGeneratedContent = () => {
    if (!generatedContent) return;
    setEditableGeneratedTitle(generatedContent.title || '');
    setEditableGeneratedBody(generatedContent.content || '');
    setIsEditingGeneratedContent(false);
  };

  const handleCopyContent = () => {
    if (!generatedContent) return;

    const contentToCopy = {
      ...generatedContent,
      title: isEditingGeneratedContent
        ? (editableGeneratedTitle.trim() || generatedContent.title)
        : generatedContent.title,
      content: isEditingGeneratedContent ? editableGeneratedBody : generatedContent.content
    };

    let fullContent = '';
    fullContent += `${contentToCopy.title}\n\n`;
    fullContent += `Format: ${contentToCopy.format}\n`;
    fullContent += `Category: ${contentToCopy.category}\n\n`;
    if (contentToCopy.query) {
      fullContent += `Query: ${contentToCopy.query}\n\n`;
    }
    fullContent += `---\n\n`;
    fullContent += `Content:\n\n${contentToCopy.content}\n\n`;

    if (contentToCopy.followupQuestions && contentToCopy.followupQuestions.length > 0) {
      fullContent += `Follow-up Questions:\n`;
      contentToCopy.followupQuestions.forEach((question, index) => {
        fullContent += `${index + 1}. ${question}\n`;
      });
      fullContent += `\n`;
    }

    if (contentToCopy.sourceLinks && contentToCopy.sourceLinks.length > 0) {
      fullContent += `Source Links:\n`;
      contentToCopy.sourceLinks.forEach((link, index) => {
        fullContent += `${index + 1}. ${link}\n`;
      });
      fullContent += `\n`;
    }

    navigator.clipboard.writeText(fullContent.trim());
    setSuccess('Content copied to clipboard!');
  };

  const handleCloseContentDisplay = () => {
    setShowContentDisplay(false);
    setGeneratedContent(null);
    setIsEditingGeneratedContent(false);
  };

  const exportAsIs = async () => {
    try {
      setIsExportingAsIs(true);

      // Check if we're exporting from documents or conversations
      const isDocumentExport = selectedDocuments.size > 0;
      const interactionUIDs = isDocumentExport ? [] : getInteractionUIDs();
      // Send UUIDs directly for document export
      const docUIDs = isDocumentExport ? Array.from(selectedDocuments) : [];

      console.log('Export As-Is API - Document Export Mode:', isDocumentExport);
      console.log('Export As-Is API - Interaction UIDs:', interactionUIDs);
      console.log('Export As-Is API - Document UUIDs (for API):', docUIDs);
      console.log('Export As-Is API - Selected Pairs Set:', selectedPairs);

      const payload = isDocumentExport ? {
        data: {
          docUIDs: docUIDs
        },
        agent: {
          code: "EXPORT_CONTENT_FROM_DOCUMENT_INTEL"
        }
      } : {
        data: {
          interactionUIDs: interactionUIDs
        },
        agent: {
          code: "EXPORT_CONTENT_FROM_CONVERSATION"
        }
      };

      console.log('Export As-Is API - Payload:', JSON.stringify(payload, null, 2));

      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      console.log('Export As-Is API - Response:', response.data);
      console.log('Export As-Is API - Response Data Length:', response.data.data?.length);
      console.log('Export As-Is API - Response Status:', response.data.status);

      if (response.data.status === 200 && response.data.data && response.data.data.length > 0) {
        const isDocumentExport = selectedDocuments.size > 0;

        // Merge all DICTIONARY entries into one consolidated view
        const allEntries: any[] = [];
        response.data.data.forEach((item: any) => {
          if (item.objectType === 'DICTIONARY' && item.entries) {
            allEntries.push(...item.entries);
          }
        });

        console.log('Export As-Is - All Merged Entries:', allEntries);

        // Helper to get value from all entries
        const getEntryValue = (key: string) => {
          const entry = allEntries.find((e: any) => e.key === key);
          return entry ? entry.value : '';
        };

        const defaultTitle = isDocumentExport ? 'Exported Document Analysis' : 'Exported Content';

        // Create single processed item from all merged entries
        const processedData = [{
          title: getEntryValue('title') || defaultTitle,
          content: getEntryValue('text') || getEntryValue('response') || getEntryValue('content') || '',
          followupQuestions: getEntryValue('followupQuestions') || [],
          sourceLinks: getEntryValue('sourceLinks') || [],
          visualization: getEntryValue('visualization') || undefined,
          query: getEntryValue('query') || '',
          dataset: getEntryValue('dataset') || null,
          geography: getEntryValue('geography') || '',
          language: getEntryValue('language') || ''
        }];

        console.log('Export As-Is - Processed Data:', processedData);

        // Check if processed data has any content
        const hasContent = processedData.some((item: any) => item.content && item.content.trim() !== '');

        if (!hasContent) {
          throw new Error('The API returned empty content. Please try selecting different documents or check if the documents have extractable content.');
        }

        // Generate PDF with the processed data
        await generatePDFFromExportData(processedData);
        setSuccess('PDF exported successfully!');
      } else {
        throw new Error('No export data received from API. Please ensure the selected documents have content to export.');
      }
    } catch (error: any) {
      console.error('Error exporting conversation:', error);
      setError(error?.message || 'Failed to export PDF. Please try again.');
    } finally {
      setIsExportingAsIs(false);
    }
  };

  // Generate PDF from GeneratedContent (for download content)
  const generatePDFFromGeneratedContent = async (generatedContent: GeneratedContent) => {
    // Convert to exportData format and use the improved export function
    const exportData = [{
      title: generatedContent.title,
      content: generatedContent.content,
      query: generatedContent.query || '',
      followupQuestions: generatedContent.followupQuestions || [],
      sourceLinks: generatedContent.sourceLinks || [],
      visualization: generatedContent.visualization || undefined
    }];

    await generatePDFFromExportData(exportData);
  };

  // Legacy function kept for reference (now using unified export)
  const generatePDFFromGeneratedContent_Legacy = async (generatedContent: GeneratedContent) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 20;
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);

    // Add header with styling
    pdf.setFillColor(156, 39, 176);
    pdf.rect(0, 0, pageWidth, 35, 'F');

    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Generated Content Export', margin, 15);

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}`, margin, 25);

    pdf.setTextColor(0, 0, 0);
    yPosition = 45;

    // Add title with decorative line
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(156, 39, 176);
    const titleLines = pdf.splitTextToSize(generatedContent.title, contentWidth);
    titleLines.forEach((line: string) => {
      pdf.text(line, margin, yPosition);
      yPosition += 8;
    });

    pdf.setDrawColor(156, 39, 176);
    pdf.setLineWidth(0.5);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;

    // Add content - use html2canvas for Unicode support
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Content:', margin, yPosition);
    yPosition += 10;

    // Create a temporary div to render the content with proper Unicode support
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '-9999px';
    tempDiv.style.width = `${contentWidth}px`;
    tempDiv.style.fontSize = '12px';
    tempDiv.style.fontFamily = 'Arial, sans-serif';
    tempDiv.style.lineHeight = '1.4';
    tempDiv.style.color = '#000000';
    tempDiv.style.whiteSpace = 'pre-wrap';
    tempDiv.style.wordWrap = 'break-word';
    tempDiv.style.backgroundColor = '#ffffff';
    tempDiv.style.padding = '10px';
    tempDiv.style.borderRadius = '4px';
    tempDiv.style.boxSizing = 'border-box';
    (tempDiv.style as any).webkitFontSmoothing = 'antialiased';
    (tempDiv.style as any).mozOsxFontSmoothing = 'grayscale';
    tempDiv.textContent = generatedContent.content;

    document.body.appendChild(tempDiv);

    try {
      const canvas = await html2canvas(tempDiv, {
        width: contentWidth,
        height: tempDiv.scrollHeight,
        scale: 3, // Higher scale for better quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        removeContainer: true,
        imageTimeout: 0,
        foreignObjectRendering: true
      });

      const imgData = canvas.toDataURL('image/png');
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;

      // Check if image fits on current page
      if (yPosition + imgHeight > pageHeight - 20) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
      yPosition += imgHeight + 10;

    } catch (error) {
      console.error('Error rendering content with html2canvas:', error);
      // Fallback to text rendering
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const contentLines = pdf.splitTextToSize(generatedContent.content, contentWidth);
      for (const line of contentLines) {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = 20;
        }
        pdf.text(line, margin, yPosition);
        yPosition += 5;
      }
    } finally {
      document.body.removeChild(tempDiv);
    }

    // Add visualization if present
    if (generatedContent.visualization) {
      yPosition += 10;

      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(156, 39, 176);
      pdf.text('Data Visualization:', margin, yPosition);
      yPosition += 10;

      // Generate chart image
      try {
        const chartImage = await generateChartImage(generatedContent.visualization.data);
        if (chartImage) {
          const imgWidth = Math.min(contentWidth, 160);
          const imgHeight = (imgWidth * 0.75);

          if (yPosition + imgHeight > pageHeight - 20) {
            pdf.addPage();
            yPosition = 20;
          }

          pdf.addImage(chartImage, 'PNG', margin, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 15;
        }
      } catch (error) {
        console.error('Error generating chart image:', error);
        // Fallback to table
        yPosition += 5;
        pdf.setFontSize(9);

        const headers = generatedContent.visualization.data.headers || [];
        const data = generatedContent.visualization.data.data || [];

        if (headers.length > 0 && data.length > 0) {
          const colWidth = contentWidth / headers.length;
          let xPos = margin;

          // Add headers
          for (const header of headers) {
            pdf.setFont('helvetica', 'bold');
            pdf.text(header.substring(0, 15), xPos + 2, yPosition + 2);
            xPos += colWidth;
          }
          yPosition += 8;

          // Add data rows
          const maxRows = Math.min(8, data.length);
          for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
            if (yPosition > pageHeight - 20) {
              pdf.addPage();
              yPosition = 20;
            }

            const row = data[rowIndex];
            xPos = margin;
            for (const header of headers) {
              pdf.setFont('helvetica', 'normal');
              const cellValue = String(row[header] || '').substring(0, 15);
              pdf.text(cellValue, xPos + 2, yPosition + 2);
              xPos += colWidth;
            }
            yPosition += 5;
          }
        }
      }
    }

    // Add source links if present
    if (generatedContent.sourceLinks && generatedContent.sourceLinks.length > 0) {
      yPosition += 10;

      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(156, 39, 176);
      pdf.text('Source Links:', margin, yPosition);
      yPosition += 10;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0, 0, 0);

      for (const link of generatedContent.sourceLinks) {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = 20;
        }
        pdf.text(`• ${link}`, margin, yPosition);
        yPosition += 5;
      }
    }


    // Add footer
    const totalPages = pdf.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 30, pageHeight - 10);
      pdf.text('Generated Content Export', margin, pageHeight - 10);
    }

    // Save the PDF
    const fileName = `${generatedContent.title.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
  };

  const generatePDFFromExportData = async (exportData: any[]) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 20;
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);

    // Helper function to add page header (only on first page)
    const addPageHeader = (isFirstPage: boolean) => {
      if (isFirstPage) {
        pdf.setFillColor(156, 39, 176);
        pdf.rect(0, 0, pageWidth, 35, 'F');

        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(20);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Document Analysis Export', margin, 15);

        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}`, margin, 25);

        pdf.setTextColor(0, 0, 0);
        return 45; // Return starting Y position after header
      }
      return 15; // Starting position for subsequent pages
    };

    yPosition = addPageHeader(true);

    // Process export data
    for (const item of exportData) {
      // Add title section with decorative underline
      if (item.title) {
        // Check if we need a new page for title
        if (yPosition > pageHeight - 50) {
          pdf.addPage();
          yPosition = 15;
        }

        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(156, 39, 176);

        // Split long titles across multiple lines
        const titleLines = pdf.splitTextToSize(item.title, contentWidth);
        titleLines.forEach((line: string, index: number) => {
          pdf.text(line, margin, yPosition);
          yPosition += 8;
        });

        // Add decorative line under title
        pdf.setDrawColor(156, 39, 176);
        pdf.setLineWidth(0.5);
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 8;

        pdf.setTextColor(0, 0, 0);
      }

      // Add query if present
      if (item.query) {
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text('Query:', margin, yPosition);
        yPosition += 5;

        // Use html2canvas for Unicode support
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-9999px';
        tempDiv.style.top = '-9999px';
        tempDiv.style.width = `${contentWidth}px`;
        tempDiv.style.fontSize = '10px';
        tempDiv.style.fontFamily = 'Arial, sans-serif';
        tempDiv.style.lineHeight = '1.4';
        tempDiv.style.color = '#000000';
        tempDiv.style.whiteSpace = 'pre-wrap';
        tempDiv.style.wordWrap = 'break-word';
        tempDiv.style.backgroundColor = '#ffffff';
        tempDiv.style.padding = '10px';
        tempDiv.style.borderRadius = '4px';
        tempDiv.style.boxSizing = 'border-box';
        (tempDiv.style as any).webkitFontSmoothing = 'antialiased';
        (tempDiv.style as any).mozOsxFontSmoothing = 'grayscale';
        tempDiv.textContent = item.query;

        document.body.appendChild(tempDiv);

        try {
          const canvas = await html2canvas(tempDiv, {
            width: contentWidth,
            height: tempDiv.scrollHeight,
            scale: 2,
            useCORS: true,
            allowTaint: true
          });

          const imgData = canvas.toDataURL('image/png');
          const imgWidth = contentWidth;
          const imgHeight = (canvas.height * contentWidth) / canvas.width;

          // Check if image fits on current page
          if (yPosition + imgHeight > pageHeight - 20) {
            pdf.addPage();
            yPosition = 20;
          }

          pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 10;

        } catch (error) {
          console.error('Error rendering query with html2canvas:', error);
          // Fallback to text rendering
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          const queryLines = pdf.splitTextToSize(item.query, contentWidth);
          for (const line of queryLines) {
            if (yPosition > pageHeight - 20) {
              pdf.addPage();
              yPosition = 20;
            }
            pdf.text(line, margin, yPosition);
            yPosition += 5;
          }
          yPosition += 5;
        } finally {
          document.body.removeChild(tempDiv);
        }
      }

      if (item.content) {
        // Add some spacing before content section
        yPosition += 3;

        // Create styled div for content rendering
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-9999px';
        tempDiv.style.top = '-9999px';
        tempDiv.style.width = `${contentWidth * 3}px`; // 3x for better quality
        tempDiv.style.fontSize = '16px';
        tempDiv.style.fontFamily = 'Georgia, "Times New Roman", serif';
        tempDiv.style.lineHeight = '1.8';
        tempDiv.style.color = '#2c3e50';
        tempDiv.style.backgroundColor = '#ffffff';
        tempDiv.style.padding = '40px';
        tempDiv.style.boxSizing = 'border-box';
        (tempDiv.style as any).webkitFontSmoothing = 'antialiased';
        (tempDiv.style as any).mozOsxFontSmoothing = 'grayscale';

        // Enhanced markdown formatting with proper HTML structure
        let formattedContent = item.content;

        // Replace headers (##, ###, etc.)
        formattedContent = formattedContent.replace(/^### (.+)$/gm, '<h3 style="font-size: 18px; font-weight: 700; color: #1a1a1a; margin: 20px 0 12px 0;">$1</h3>');
        formattedContent = formattedContent.replace(/^## (.+)$/gm, '<h2 style="font-size: 20px; font-weight: 700; color: #1a1a1a; margin: 24px 0 14px 0;">$1</h2>');

        // Replace bold and italic
        formattedContent = formattedContent.replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight: 700; color: #1a1a1a;">$1</strong>');
        formattedContent = formattedContent.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em style="font-style: italic; color: #34495e;">$1</em>');

        // Replace bullet points with proper formatting
        formattedContent = formattedContent.replace(/^[\s]*\*\s+(.+)$/gm, '<div style="margin: 6px 0; padding-left: 24px; position: relative;"><span style="position: absolute; left: 0; color: #9C27B0; font-weight: bold;">•</span> $1</div>');

        // Replace paragraphs (double newline)
        formattedContent = formattedContent.split('\n\n').map((para: string) => {
          if (!para.trim().startsWith('<')) {
            return `<p style="margin: 14px 0; text-align: justify; line-height: 1.8;">${para}</p>`;
          }
          return para;
        }).join('');

        // Replace remaining single newlines with breaks
        formattedContent = formattedContent.replace(/\n/g, '<br/>');

        // Wrap in container
        formattedContent = `<div style="font-family: Georgia, 'Times New Roman', serif;">${formattedContent}</div>`;

        tempDiv.innerHTML = formattedContent;

        document.body.appendChild(tempDiv);

        try {
          const canvas = await html2canvas(tempDiv, {
            width: contentWidth * 3,
            height: tempDiv.scrollHeight,
            scale: 3, // Higher quality
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
            windowWidth: contentWidth * 3,
            windowHeight: tempDiv.scrollHeight
          });

          const imgData = canvas.toDataURL('image/png', 1.0); // Maximum quality
          const imgWidth = contentWidth;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          // Smart pagination - split content across pages if needed
          const maxContentHeight = pageHeight - 30; // Leave margin
          const availableOnCurrentPage = pageHeight - yPosition - 15;

          if (imgHeight <= availableOnCurrentPage) {
            // Fits on current page
            pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight, undefined, 'FAST');
            yPosition += imgHeight + 10;
          } else {
            // Need to split across pages
            const pagesNeeded = Math.ceil(imgHeight / maxContentHeight);

            for (let pageIndex = 0; pageIndex < pagesNeeded; pageIndex++) {
              if (pageIndex > 0 || availableOnCurrentPage < 50) {
                pdf.addPage();
                yPosition = 15;
              }

              // Calculate what portion of the image to show
              const sourceY = pageIndex * maxContentHeight * (canvas.height / imgHeight);
              const sourceHeight = Math.min(
                maxContentHeight * (canvas.height / imgHeight),
                canvas.height - sourceY
              );

              const displayHeight = (sourceHeight * imgWidth) / canvas.width;

              // Create a cropped version of the canvas for this page
              const croppedCanvas = document.createElement('canvas');
              croppedCanvas.width = canvas.width;
              croppedCanvas.height = sourceHeight;
              const ctx = croppedCanvas.getContext('2d');

              if (ctx) {
                ctx.drawImage(
                  canvas,
                  0, sourceY, canvas.width, sourceHeight,
                  0, 0, canvas.width, sourceHeight
                );

                const croppedImgData = croppedCanvas.toDataURL('image/png', 1.0);
                pdf.addImage(croppedImgData, 'PNG', margin, yPosition, imgWidth, displayHeight, undefined, 'FAST');
                yPosition = 15; // Reset for next page
              }
            }

            yPosition = 15; // Start fresh on next page
          }

        } catch (error) {
          console.error('Error rendering content with html2canvas:', error);
          // Enhanced fallback with better text formatting
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(40, 40, 40);

          // Process content line by line with basic markdown
          const lines = item.content.split('\n');
          for (const line of lines) {
            if (yPosition > pageHeight - 25) {
              pdf.addPage();
              yPosition = 15;
            }

            // Check for bold headings
            if (line.trim().startsWith('**') && line.trim().endsWith('**')) {
              pdf.setFont('helvetica', 'bold');
              pdf.setFontSize(13);
              pdf.setTextColor(156, 39, 176);
              const cleanLine = line.replace(/\*\*/g, '').trim();
              pdf.text(cleanLine, margin, yPosition);
              pdf.setFont('helvetica', 'normal');
              pdf.setFontSize(11);
              pdf.setTextColor(40, 40, 40);
              yPosition += 8;
            } else if (line.trim().startsWith('*')) {
              // Bullet point
              const cleanLine = line.trim().substring(1).trim();
              const bulletLines = pdf.splitTextToSize(`• ${cleanLine}`, contentWidth - 5);
              for (const bulletLine of bulletLines) {
                if (yPosition > pageHeight - 25) {
                  pdf.addPage();
                  yPosition = 15;
                }
                pdf.text(bulletLine, margin + 3, yPosition);
                yPosition += 5.5;
              }
            } else if (line.trim()) {
              // Regular paragraph
              const paraLines = pdf.splitTextToSize(line, contentWidth);
              for (const paraLine of paraLines) {
                if (yPosition > pageHeight - 25) {
                  pdf.addPage();
                  yPosition = 15;
                }
                pdf.text(paraLine, margin, yPosition);
                yPosition += 5.5;
              }
              yPosition += 2; // Extra space after paragraph
            } else {
              yPosition += 4; // Empty line spacing
            }
          }
          yPosition += 8;
        } finally {
          document.body.removeChild(tempDiv);
        }
      }


      // Add visualization if present
      if (item.visualization) {
        try {
          yPosition += 10;
          if (yPosition > pageHeight - 100) {
            pdf.addPage();
            yPosition = 20;
          }

          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Visualization:', margin, yPosition);
          yPosition += 10;

          const chartImage = await chartDataToImage(item.visualization);
          if (chartImage) {
            // Add chart image
            const imgWidth = pageWidth - 2 * margin;
            const imgHeight = (imgWidth * 3) / 4; // 4:3 aspect ratio

            pdf.addImage(chartImage, 'PNG', margin, yPosition, imgWidth, imgHeight);
            yPosition += imgHeight + 10;
          }
        } catch (error) {
          console.error('Error adding chart to PDF:', error);
          // Continue without chart if there's an error
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          pdf.text(`Type: ${item.visualization.type}`, margin, yPosition);
          yPosition += 5;
        }
      }


      // Add source links if present
      if (item.sourceLinks && item.sourceLinks.length > 0) {
        yPosition += 12;
        if (yPosition > pageHeight - 50) {
          pdf.addPage();
          yPosition = 15;
        }

        // Section header
        pdf.setFontSize(13);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(156, 39, 176);
        pdf.text('📚 References & Source Links', margin, yPosition);
        yPosition += 8;

        // Add subtle line
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.3);
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 6;

        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(60, 60, 60);

        item.sourceLinks.forEach((link: string, index: number) => {
          if (yPosition > pageHeight - 25) {
            pdf.addPage();
            yPosition = 15;
          }

          // Format link with number
          const linkText = `[${index + 1}] ${link.substring(0, 100)}${link.length > 100 ? '...' : ''}`;
          const linkLines = pdf.splitTextToSize(linkText, contentWidth - 5);

          linkLines.forEach((line: string) => {
            if (yPosition > pageHeight - 25) {
              pdf.addPage();
              yPosition = 15;
            }
            pdf.text(line, margin + 3, yPosition);
            yPosition += 5;
          });
          yPosition += 2;
        });
      }

      // Add spacing after each export item
      yPosition += 15;
    }

    // Add professional footer to all pages
    const totalPages = pdf.getNumberOfPages();
    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      pdf.setPage(pageNum);

      // Footer background
      pdf.setFillColor(250, 250, 250);
      pdf.rect(0, pageHeight - 15, pageWidth, 15, 'F');

      // Footer line
      pdf.setDrawColor(156, 39, 176);
      pdf.setLineWidth(0.5);
      pdf.line(0, pageHeight - 15, pageWidth, pageHeight - 15);

      // Page number (right)
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Page ${pageNum} of ${totalPages}`, pageWidth - margin - 20, pageHeight - 8);

      // Footer text (left)
      pdf.text('Knowledge Insights - Datascient AI', margin, pageHeight - 8);

      // Footer text (center)
      pdf.text('Confidential', pageWidth / 2 - 10, pageHeight - 8);
    }

    // Generate filename with title
    const firstItem = exportData[0];
    const safeTitle = (firstItem?.title || 'Document_Analysis')
      .replace(/[^a-z0-9]/gi, '_')
      .substring(0, 50);
    const timestamp = new Date().toISOString().split('T')[0];

    pdf.save(`${safeTitle}_${timestamp}.pdf`);
  };

  // Chart generation functions for PDF export
  const generateChartImage = async (chartData: any): Promise<string | null> => {
    try {
      // Validate chart data
      if (!chartData || !chartData.headers || !chartData.data || chartData.data.length === 0) {
        console.warn('Invalid chart data provided:', chartData);
        return await generateTableChartImage(chartData);
      }

      // Create a temporary container for the chart
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '600px';
      tempContainer.style.height = '400px';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.padding = '20px';
      tempContainer.style.borderRadius = '8px';
      tempContainer.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
      document.body.appendChild(tempContainer);

      // Create a React root to render the chart component
      const { createRoot } = await import('react-dom/client');
      const root = createRoot(tempContainer);

      // Render the same DataChart component used in chatbot
      const ChartComponent = () => {
        return (
          <div style={{ width: '100%', height: '100%' }}>
            {chartData.visual ? (
              <DataChart data={chartData} />
            ) : chartData.title && chartData.title.toLowerCase().includes('sentiment') ? (
              // Special handling for sentiment charts - use pie chart
              <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <h3 style={{ color: '#4c3794', marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>
                  {chartData.title}
                </h3>
                <div style={{ width: '300px', height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData.data}
                        dataKey={chartData.headers[1]}
                        nameKey={chartData.headers[0]}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        innerRadius={40}
                        fill="#4c3794"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={false}
                      >
                        {chartData.data.map((entry: any, index: number) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              entry[chartData.headers[0]] === 'Positive' ? '#4c3794' :
                                entry[chartData.headers[0]] === 'Negative' ? '#b19cd9' :
                                  '#d8bfd8'
                            }
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value: number, name: string) => [`${value}`, name]}
                        contentStyle={{
                          backgroundColor: '#ede7f6',
                          borderRadius: '8px',
                          border: 'none',
                          boxShadow: '0 4px 12px rgba(76, 55, 148, 0.1)',
                          padding: '8px 12px',
                          color: '#4c3794'
                        }}
                      />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        formatter={(value) => (
                          <span style={{ color: '#4c3794', fontSize: '12px' }}>{value}</span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <DataTable data={chartData} />
            )}
          </div>
        );
      };

      // Render the chart
      root.render(<ChartComponent />);

      // Wait a bit for the chart to render
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Convert to image using html2canvas
      const canvas = await html2canvas(tempContainer, {
        backgroundColor: '#ffffff',
        scale: 3,
        useCORS: true,
        allowTaint: true,
        logging: false,
        removeContainer: true,
        imageTimeout: 0,
        foreignObjectRendering: true,
        width: tempContainer.offsetWidth,
        height: tempContainer.offsetHeight
      });

      const imageDataUrl = canvas.toDataURL('image/png');

      // Clean up
      root.unmount();
      document.body.removeChild(tempContainer);

      return imageDataUrl;
    } catch (error) {
      console.error('Error generating chart image:', error);
      return null;
    }
  };

  const generateTableChartImage = async (chartData: any): Promise<string | null> => {
    try {
      const chartElement = createTableChart(chartData);
      const canvas = await html2canvas(chartElement);
      const imageDataUrl = canvas.toDataURL('image/png');
      return imageDataUrl;
    } catch (error) {
      console.error('Error generating table chart image:', error);
      return null;
    }
  };

  const createTableChart = (chartData: any): HTMLElement => {
    const container = document.createElement('div');
    container.style.width = '800px';
    container.style.height = '400px';
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.style.backgroundColor = 'white';
    container.style.padding = '20px';
    container.style.fontFamily = 'Arial, sans-serif';

    const title = document.createElement('h3');
    title.textContent = chartData.title || 'Data Table';
    title.style.margin = '0 0 20px 0';
    title.style.color = '#333';
    container.appendChild(title);

    const table = document.createElement('table');
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';
    table.style.border = '1px solid #ddd';

    // Create header
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    headerRow.style.backgroundColor = '#f5f5f5';

    chartData.headers.forEach((header: string) => {
      const th = document.createElement('th');
      th.textContent = header;
      th.style.padding = '12px';
      th.style.border = '1px solid #ddd';
      th.style.textAlign = 'left';
      headerRow.appendChild(th);
    });

    thead.appendChild(headerRow);
    table.appendChild(thead);

    // Create body
    const tbody = document.createElement('tbody');
    chartData.data.forEach((row: any, index: number) => {
      const tr = document.createElement('tr');
      tr.style.backgroundColor = index % 2 === 0 ? 'white' : '#f9f9f9';

      chartData.headers.forEach((header: string) => {
        const td = document.createElement('td');
        td.textContent = row[header] || '';
        td.style.padding = '12px';
        td.style.border = '1px solid #ddd';
        tr.appendChild(td);
      });

      tbody.appendChild(tr);
    });

    table.appendChild(tbody);
    container.appendChild(table);

    document.body.appendChild(container);
    return container;
  };

  const createTextChart = (chartData: any): HTMLElement => {
    const container = document.createElement('div');
    container.style.width = '800px';
    container.style.height = '400px';
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.style.backgroundColor = 'white';
    container.style.padding = '20px';
    container.style.fontFamily = 'Arial, sans-serif';

    const title = document.createElement('h3');
    title.textContent = chartData.title || 'Text Content';
    title.style.margin = '0 0 20px 0';
    title.style.color = '#333';
    container.appendChild(title);

    const content = document.createElement('div');
    content.textContent = chartData.content || '';
    content.style.lineHeight = '1.6';
    content.style.color = '#555';
    container.appendChild(content);

    document.body.appendChild(container);
    return container;
  };

  const chartDataToImage = async (visualization: any): Promise<string> => {
    if (!visualization || !visualization.data) {
      throw new Error('No visualization data provided');
    }

    try {
      const imageDataUrl = await generateChartImage(visualization.data);
      if (!imageDataUrl) {
        throw new Error('Failed to generate chart image');
      }
      return imageDataUrl;
    } catch (error) {
      console.error('Error converting chart to image:', error);
      throw error;
    }
  };

  const generatePDFWithCharts = async (generatedContent: GeneratedContent): Promise<string> => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 20;
    const margin = 20;

    // Add title
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text(generatedContent.title, margin, yPosition);
    yPosition += 15;

    // Add content
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    const contentLines = pdf.splitTextToSize(generatedContent.content, pageWidth - 2 * margin);

    for (const line of contentLines) {
      if (yPosition > pageHeight - 20) {
        pdf.addPage();
        yPosition = 20;
      }
      pdf.text(line, margin, yPosition);
      yPosition += 5;
    }

    // Add visualization if present
    if (generatedContent.visualization) {
      try {
        yPosition += 10;
        if (yPosition > pageHeight - 100) {
          pdf.addPage();
          yPosition = 20;
        }

        const chartImage = await chartDataToImage(generatedContent.visualization);
        if (chartImage) {
          // Add chart image
          const imgWidth = pageWidth - 2 * margin;
          const imgHeight = (imgWidth * 3) / 4; // 4:3 aspect ratio

          pdf.addImage(chartImage, 'PNG', margin, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 10;
        }
      } catch (error) {
        console.error('Error adding chart to PDF:', error);
        // Continue without chart if there's an error
      }
    }

    // Add follow-up questions if present
    if (generatedContent.followupQuestions && generatedContent.followupQuestions.length > 0) {
      yPosition += 10;
      if (yPosition > pageHeight - 50) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Follow-up Questions:', margin, yPosition);
      yPosition += 10;

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      generatedContent.followupQuestions.forEach((question: string) => {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = 20;
        }
        pdf.text(`• ${question}`, margin, yPosition);
        yPosition += 8;
      });
    }

    // Add source links if present
    if (generatedContent.sourceLinks && generatedContent.sourceLinks.length > 0) {
      yPosition += 10;
      if (yPosition > pageHeight - 50) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Source Links:', margin, yPosition);
      yPosition += 10;

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      generatedContent.sourceLinks.forEach((link: string) => {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = 20;
        }
        pdf.text(`• ${link}`, margin, yPosition);
        yPosition += 8;
      });
    }

    const fileName = `generated-content-${Date.now()}.pdf`;
    pdf.save(fileName);
    return fileName;
  };

  const handleDownloadContent = async () => {
    if (!generatedContent) return;

    const contentToDownload = isEditingGeneratedContent
      ? {
        ...generatedContent,
        title: editableGeneratedTitle.trim() || generatedContent.title,
        content: editableGeneratedBody
      }
      : generatedContent;

    const format = generatedContent.format.toLowerCase();
    let fileName = '';
    let mimeType = '';

    try {
      if (format === 'image only' || format === 'infographic') {
        // For image content, generate an image
        setSuccess('Image download functionality would be implemented here');
      } else if (format === 'video') {
        // For video content, generate a video
        setSuccess('Video download functionality would be implemented here');
      } else {
        // For text content, generate PDF using the same function as Export As-Is
        try {
          // Convert GeneratedContent to the same format as Export As-Is
          const exportData = [{
            title: contentToDownload.title,
            content: contentToDownload.content,
            query: contentToDownload.query || '',
            followupQuestions: contentToDownload.followupQuestions || [],
            sourceLinks: contentToDownload.sourceLinks || [],
            visualization: contentToDownload.visualization || undefined
          }];

          await generatePDFFromExportData(exportData);
          setSuccess('PDF downloaded successfully!');
          return; // PDF is saved directly, no need to create blob
        } catch (error) {
          console.error('Error generating PDF:', error);
          // Fallback to text if PDF generation fails
          const textContent = generateTextContent(generatedContent);
          const blob = new Blob([textContent], { type: 'text/plain' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `generated-content-${Date.now()}.txt`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          setSuccess('Text file downloaded successfully!');
        }
      }
    } catch (error) {
      console.error('Error downloading content:', error);
      setError('Failed to download content. Please try again.');
    }
  };

  // Helper function to generate text content (fallback)
  const generateTextContent = (generatedContent: GeneratedContent): string => {
    let fullContent = '';

    // Add title
    fullContent += `${generatedContent.title}\n\n`;

    // Add content
    fullContent += `${generatedContent.content}\n\n`;

    // Add follow-up questions if present
    if (generatedContent.followupQuestions && generatedContent.followupQuestions.length > 0) {
      fullContent += 'Follow-up Questions:\n';
      generatedContent.followupQuestions.forEach((question: string) => {
        fullContent += `• ${question}\n`;
      });
      fullContent += '\n';
    }

    // Add source links if present
    if (generatedContent.sourceLinks && generatedContent.sourceLinks.length > 0) {
      fullContent += 'Source Links:\n';
      generatedContent.sourceLinks.forEach((link: string) => {
        fullContent += `• ${link}\n`;
      });
    }

    return fullContent;
  };

  return (
    <Box sx={{ padding: '10px' }}>

      {/* Back Button */}


      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        // marginBottom: '24px',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIosIcon sx={{ fontSize: 18 }} />}
            onClick={() => {
              // Check if we have a stored navigation context
              const context = sessionStorage.getItem('appsNavigationContext');
              if (context && context.startsWith('industry:')) {
                // Navigate back to Apps with the stored context
                navigate('/Apps');
              } else {
                // Default navigation to Apps
                navigate('/Apps');
              }
            }}
            sx={{
              color: '#9C27B0',
              fontWeight: 600,
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: '#F3E5F5'
              }
            }}
          >
            Back
          </Button>
          <DescriptionIcon sx={{ fontSize: 24, color: '#9C27B0', marginLeft: '24px' }} />
          <Typography variant="h5" sx={{ color: '#131F32', fontWeight: 600, fontSize: '1.25rem' }}>
            Knowledge Insights
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel>Select Project</InputLabel>
            <Select
              value={selectedProject?.id || ''}
              onChange={e => {
                const proj = projects.find(p => p.id === e.target.value);
                setSelectedProject(proj || null);
              }}
              label="Select Project"
              disabled={isLoadingProjects}
            >
              {projects.map(project => (
                <MenuItem key={project.id} value={project.id}>
                  {project.project_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsProjectDialogOpen(true)}
            sx={{
              backgroundColor: '#9C27B0',
              '&:hover': { backgroundColor: '#7B1FA2' },
              fontWeight: 600
            }}
          >
            Create Project
          </Button>
        </Box>
      </Box>

      {/* Access Denied Check */}
      {!canViewProject && selectedProject ? (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          textAlign: 'center',
          p: 4,
          backgroundColor: '#fafafa',
          borderRadius: 2,
          mx: 2
        }}>
          <LockIcon sx={{ fontSize: 64, color: '#f44336', mb: 2 }} />
          <Typography variant="h4" sx={{ mb: 2, color: '#333' }}>
            Access Denied
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, color: '#666' }}>
            You don't have access to this project
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: '#666' }}>
            Please create a new project or select another project that you have access to.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsProjectDialogOpen(true)}
              sx={{
                backgroundColor: '#9C27B0',
                '&:hover': { backgroundColor: '#7B1FA2' },
                fontWeight: 600
              }}
            >
              Create New Project
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
              sx={{
                borderColor: '#9C27B0',
                color: '#9C27B0',
                '&:hover': {
                  borderColor: '#7B1FA2',
                  backgroundColor: 'rgba(156, 39, 176, 0.04)'
                },
                fontWeight: 600
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          {/* Top-Level Tabs */}
          <Box sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mb: 3,
            px: 2
          }}>
            <Tabs
              value={topLevelTab}
              onChange={(e, newValue) => setTopLevelTab(newValue)}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  minWidth: 'auto',
                  px: 3,
                  py: 1.5,
                  color: '#666',
                  '&.Mui-selected': {
                    color: '#9C27B0',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#9C27B0',
                  height: 3,
                  borderRadius: '2px 2px 0 0',
                },
              }}
            >
              <Tab
                label="Dashboard"
                icon={<DashboardIcon sx={{ fontSize: 18, mb: 0.5 }} />}
                iconPosition="top"
              />
              <Tab
                label="AI Summary"
                icon={<AssessmentIcon sx={{ fontSize: 18, mb: 0.5 }} />}
                iconPosition="top"
              />
              <Tab
                label="Documents"
                icon={<DescriptionIcon sx={{ fontSize: 18, mb: 0.5 }} />}
                iconPosition="top"
              />
            </Tabs>
          </Box>

          {/* Loading Indicator - Only shows on Dashboard tab */}
          {topLevelTab === 0 && isInitialLoading && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              px: 2
            }}>
              <CircularProgress size={50} sx={{ color: '#9C27B0', mb: 2 }} />
              <Typography variant="body1" sx={{ color: '#666', fontWeight: 500 }}>
                Loading dashboard...
              </Typography>
              <Typography variant="body2" sx={{ color: '#999', mt: 0.5 }}>
                Fetching documents and preparing your insights
              </Typography>
            </Box>
          )}

          {/* Main Content */}
          {isFullScreen ? (
            <FullScreenContainer>
              {/* Chat Interface - Using Reusable Chatbot */}
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                <Chatbot
                  placeholder="Type your message..."
                  welcomeMessage={selectedDocument ? `Hello! I'm your AI assistant. How can I help you analyze "${getStringValue(selectedDocument, 'title')}"?` : "Hello! I'm your AI assistant. How can I help you analyze the documents?"}
                  enableVoice={true}
                  enableGovAPI={true}
                  apiConfig={chatbotApiConfig}
                  getAuthToken={async () => localStorage.getItem('token')}
                />
              </Box>
            </FullScreenContainer>
          ) : (
            <>
              {/* Dashboard Tab */}
              {topLevelTab === 0 && !isInitialLoading && (
                <Box sx={{
                  height: 'calc(100vh - 280px)',
                  overflowY: 'auto',
                  p: 2
                }}>

                  {/* Overview Cards */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: 3,
                    mb: 4
                  }}>
                    {/* Total Documents */}
                    <Card sx={{
                      p: 3,
                      borderRadius: '12px',
                      background: '#ffffff',
                      color: '#1f2937',
                      position: 'relative',
                      overflow: 'hidden',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        // transform: 'translateY(-2px)',
                        // boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        borderColor: '#d1d5db',
                        transition: 'all 0.2s ease'
                      },
                      transition: 'all 0.2s ease'
                    }}>
                      <Box sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        opacity: 0.02,
                        transform: 'rotate(15deg)'
                      }}>
                        <DescriptionIcon sx={{ fontSize: 60 }} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: '#1f2937' }}>
                            {dashboardData.totalDocuments}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 500, color: '#6b7280' }}>
                            Total Documents
                          </Typography>
                        </Box>
                        <Box sx={{
                          p: 2,
                          borderRadius: '8px',
                          backgroundColor: '#f3f4f6',
                          color: '#6366f1'
                        }}>
                          <DescriptionIcon sx={{ fontSize: 28 }} />
                        </Box>
                      </Box>
                    </Card>

                    {/* Total Topics */}
                    <Card sx={{
                      p: 3,
                      borderRadius: '12px',
                      background: '#ffffff',
                      color: '#1f2937',
                      position: 'relative',
                      overflow: 'hidden',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        // transform: 'translateY(-2px)',
                        // boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        borderColor: '#d1d5db',
                        transition: 'all 0.2s ease'
                      },
                      transition: 'all 0.2s ease'
                    }}>
                      <Box sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        opacity: 0.02,
                        transform: 'rotate(15deg)'
                      }}>
                        <CategoryIcon sx={{ fontSize: 60 }} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: '#1f2937' }}>
                            {dashboardData.totalTopics}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 500, color: '#6b7280' }}>
                            Unique Topics
                          </Typography>
                        </Box>
                        <Box sx={{
                          p: 2,
                          borderRadius: '8px',
                          backgroundColor: '#f0f9ff',
                          color: '#0ea5e9'
                        }}>
                          <CategoryIcon sx={{ fontSize: 28 }} />
                        </Box>
                      </Box>
                    </Card>

                    {/* Total Highlights */}
                    <Card sx={{
                      p: 3,
                      borderRadius: '12px',
                      background: '#ffffff',
                      color: '#1f2937',
                      position: 'relative',
                      overflow: 'hidden',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        // transform: 'translateY(-2px)',
                        // boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        borderColor: '#d1d5db',
                        transition: 'all 0.2s ease'
                      },
                      transition: 'all 0.2s ease'
                    }}>
                      <Box sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        opacity: 0.02,
                        transform: 'rotate(15deg)'
                      }}>
                        <HighlightIcon sx={{ fontSize: 60 }} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: '#1f2937' }}>
                            {dashboardData.totalHighlights}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 500, color: '#6b7280' }}>
                            Key Highlights
                          </Typography>
                        </Box>
                        <Box sx={{
                          p: 2,
                          borderRadius: '8px',
                          backgroundColor: '#f0fdf4',
                          color: '#16a34a'
                        }}>
                          <HighlightIcon sx={{ fontSize: 28 }} />
                        </Box>
                      </Box>
                    </Card>

                    {/* Document Groups */}
                    <Card sx={{
                      p: 3,
                      borderRadius: '12px',
                      background: '#ffffff',
                      color: '#1f2937',
                      position: 'relative',
                      overflow: 'hidden',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        // transform: 'translateY(-2px)',
                        // boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        borderColor: '#d1d5db',
                        transition: 'all 0.2s ease'
                      },
                      transition: 'all 0.2s ease'
                    }}>
                      <Box sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        opacity: 0.02,
                        transform: 'rotate(15deg)'
                      }}>
                        <PieChartIcon sx={{ fontSize: 60 }} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: '#1f2937' }}>
                            {dashboardData.documentsByGroup.length}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 500, color: '#6b7280' }}>
                            Content Groups
                          </Typography>
                        </Box>
                        <Box sx={{
                          p: 2,
                          borderRadius: '8px',
                          backgroundColor: '#fef3c7',
                          color: '#d97706'
                        }}>
                          <PieChartIcon sx={{ fontSize: 28 }} />
                        </Box>
                      </Box>
                    </Card>
                  </Box>

                  {/* Charts Section with 2-column layout: left timeline spans 2 rows; right pie (row 1) + compact highlights (row 2) */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 3,
                    alignItems: 'stretch',
                    mb: 3
                  }}>
                    {/* Semantic Topic Timeline (compact, 2x height) */}
                    <Card sx={{ p: 3, borderRadius: '12px', border: '1px solid #E0E0E0', gridColumn: { xs: '1', md: '1' }, gridRow: { xs: 'auto', md: '1 / span 2' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <TimelineIcon sx={{ color: '#9C27B0', fontSize: 20 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#131F32' }}>
                          Semantic Topic Timeline
                        </Typography>
                      </Box>
                      <Box sx={{ height: 600, width: '100%', overflowY: 'auto', pr: 1 }}>
                        {dashboardData.highlightsByTopic && dashboardData.highlightsByTopic.length > 0 ? (
                          <Box sx={{ position: 'relative', pl: 3 }}>
                            {/* vertical line */}
                            <Box sx={{ position: 'absolute', left: 8, top: 0, bottom: 0, width: 2, background: 'linear-gradient(#9C27B0, #E1BEE7)' }} />
                            {dashboardData.highlightsByTopic
                              .sort((a, b) => {
                                const ad = a.latestDateMs || 0;
                                const bd = b.latestDateMs || 0;
                                if (ad === bd) {
                                  const am = a.mentions || a.highlights.length;
                                  const bm = b.mentions || b.highlights.length;
                                  return bm - am;
                                }
                                return bd - ad;
                              })
                              .slice(0, 12)
                              .map((group) => (
                                <Box key={group.topic} sx={{ position: 'relative', display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1.5 }}>
                                  {/* dot */}
                                  <Box sx={{ position: 'relative', flexShrink: 0, width: 12, height: 12, borderRadius: '50%', backgroundColor: '#9C27B0', mt: 0.5 }} />
                                  {/* row card */}
                                  <Box sx={{ flex: 1, backgroundColor: '#FFFFFF', border: '1px solid #E5E9EF', borderLeft: '3px solid #9C27B0', borderRadius: '8px', p: 1.25 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#131F32', maxWidth: '65%', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        {group.topic}
                                      </Typography>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#9CA3AF' }}>
                                        <AccessTimeIcon sx={{ fontSize: 14 }} />
                                        <Typography variant="caption">
                                          {group.latestDateMs ? new Date(group.latestDateMs).toLocaleDateString() : ''}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    {/* <Typography variant="caption" sx={{ color: '#64748B', mb: 0.5, display: 'block' }}>
                                {group.mentions || group.highlights.length} mentions
                              </Typography> */}
                                    {/* dominant highlight */}
                                    {group.dominantHighlight ? (
                                      <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        {group.dominantHighlight.text}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                </Box>
                              ))}
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748B', fontStyle: 'italic' }}>
                            No timeline data available
                          </Box>
                        )}
                      </Box>
                    </Card>

                    {/* Documents by Group Pie Chart (right column, row 1) */}
                    <Card sx={{ p: 3, borderRadius: '12px', border: '1px solid #E0E0E0', gridColumn: { xs: '1', md: '2' }, gridRow: { xs: 'auto', md: '1' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <PieChartIcon sx={{ color: '#9C27B0', fontSize: 20 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#131F32' }}>
                          Documents by Group
                        </Typography>
                      </Box>
                      <Box sx={{ height: 300, width: '100%' }}>
                        {dashboardData.documentsByGroup.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={dashboardData.documentsByGroup}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ group, percent }) => `${(percent * 100).toFixed(0)}%`}
                                outerRadius={70}
                                innerRadius={30}
                                fill="#8884d8"
                                dataKey="count"
                                paddingAngle={2}
                              >
                                {dashboardData.documentsByGroup.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={[
                                      '#9C27B0', // Primary Purple
                                      '#7B1FA2', // Dark Purple
                                      '#BA68C8', // Light Purple
                                      '#E1BEE7', // Very Light Purple
                                      '#4A148C', // Deep Purple
                                      '#6A1B9A', // Medium Purple
                                      '#8E24AA', // Purple
                                      '#AB47BC', // Medium Light Purple
                                      '#CE93D8', // Light Purple
                                      '#F3E5F5'  // Very Light Purple
                                    ][index % 10]}
                                  />
                                ))}
                              </Pie>
                              <RechartsTooltip
                                formatter={(value, name, props) => [
                                  `${value} documents`,
                                  props.payload.group
                                ]}
                                contentStyle={{
                                  backgroundColor: '#FFFFFF',
                                  border: '1px solid #E0E0E0',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }}
                              />
                              <Legend
                                verticalAlign="bottom"
                                height={60}
                                iconType="circle"
                                formatter={(value, entry, index) => [
                                  dashboardData.documentsByGroup[index]?.group || value,
                                  `${dashboardData.documentsByGroup[index]?.count || 0} docs`
                                ]}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: '#64748B',
                            fontStyle: 'italic'
                          }}>
                            No grouped data available
                          </Box>
                        )}
                      </Box>
                    </Card>

                    {/* Compact Recent Key Highlights (right column, row 2) */}
                    <Card sx={{ p: 3, borderRadius: '12px', border: '1px solid #E0E0E0', gridColumn: { xs: '1', md: '2' }, gridRow: { xs: 'auto', md: '2' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <HighlightIcon sx={{ color: '#9C27B0', fontSize: 20 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#131F32' }}>
                          Recent Key Highlights
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
                        {dashboardData.highlightsByTopic
                          .sort((a, b) => (b.latestDateMs || 0) - (a.latestDateMs || 0))
                          .slice(0, 4)
                          .map((group) => (
                            <Box key={group.topic} sx={{ border: '1px solid #E5E9EF', borderRadius: '8px', p: 1.25 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#131F32', mb: 0.5, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {group.topic}
                              </Typography>
                              {(group.highlights || []).slice(0, 1).map((h, i) => (
                                <Typography key={i} variant="body2" sx={{ color: '#374151', fontWeight: 500, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                  {h.text}
                                </Typography>
                              ))}
                              {/* <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.5, display: 'block' }}>
                          {(group.mentions || group.highlights.length)} mentions{group.latestDateMs ? ` • ${new Date(group.latestDateMs).toLocaleDateString()}` : ''}
                        </Typography> */}
                            </Box>
                          ))}
                      </Box>
                    </Card>
                  </Box>


                  {/* Recent Documents & Top Sources */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                    gap: 3
                  }}>
                    {/* Recent Documents */}
                    <Card sx={{ p: 3, borderRadius: '12px', border: '1px solid #E0E0E0' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <CalendarTodayIcon sx={{ color: '#9C27B0', fontSize: 20 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#131F32' }}>
                          Recent Documents
                        </Typography>
                      </Box>
                      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                        {dashboardData.recentDocuments.length > 0 ? (
                          dashboardData.recentDocuments.map((doc, index) => {
                            const title = getStringValue(doc, 'title');
                            const documentIdentifier = getStringValue(doc, 'documentIdentifier');
                            return (
                              <Box key={doc.docUID} sx={{
                                p: 1.5,
                                mb: 1,
                                borderRadius: '8px',
                                backgroundColor: '#F8F9FA',
                                border: '1px solid #E5E9EF',
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: '#F3E5F5',
                                  borderColor: '#9C27B0'
                                }
                              }}
                                onClick={() => {
                                  setSelectedDocument(doc);
                                  setTopLevelTab(1); // Switch to AI Summary tab
                                }}>
                                <Typography variant="body2" sx={{
                                  fontWeight: 500,
                                  color: '#131F32',
                                  mb: 0.5,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}>
                                  {title || 'Untitled Document'}
                                </Typography>
                                {documentIdentifier && (
                                  <Typography variant="caption" sx={{
                                    color: '#9C27B0',
                                    fontSize: '0.7rem'
                                  }}>
                                    Ref: {documentIdentifier}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })
                        ) : (
                          <Typography variant="body2" sx={{ color: '#64748B', fontStyle: 'italic' }}>
                            No recent documents
                          </Typography>
                        )}
                      </Box>
                    </Card>

                    {/* Top Sources */}
                    <Card sx={{ p: 3, borderRadius: '12px', border: '1px solid #E0E0E0' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <SourceIcon sx={{ color: '#9C27B0', fontSize: 20 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#131F32' }}>
                          Top Sources
                        </Typography>
                      </Box>
                      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                        {dashboardData.topSources.length > 0 ? (
                          dashboardData.topSources.map((source, index) => (
                            <Box key={source.name} sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              mb: 1.5,
                              p: 1,
                              borderRadius: '6px',
                              backgroundColor: index < 3 ? '#F3E5F5' : '#F8F9FA'
                            }}>
                              <Typography variant="body2" sx={{
                                fontWeight: index < 3 ? 600 : 400,
                                color: '#131F32',
                                minWidth: 0,
                                flex: 1,
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap'
                              }}>
                                {source.name}
                              </Typography>
                              <Typography variant="caption" sx={{
                                color: '#64748B',
                                fontWeight: 500
                              }}>
                                {source.count} docs
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Box sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="body2" sx={{ color: '#64748B', fontStyle: 'italic', mb: 1 }}>
                              No source data available
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
                              Documents may not have source information
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Card>
                  </Box>
                </Box>
              )}

              {/* AI Summary Tab */}
              {topLevelTab === 1 && (
                <Box sx={{ display: 'flex', gap: '24px', width: '100%', overflow: 'hidden' }}>
                  {/* Left Panel - Document List */}
                  <Box sx={{ flex: isChatOpen ? 1.7 : 2, width: isChatOpen ? '65%' : '100%', minWidth: 0, overflow: 'hidden', transition: 'width 0.3s ease-in-out' }}>
                    <Paper sx={{ p: 3, borderRadius: '16px', height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
                      {/* Export Controls */}
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3
                      }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                          {isDocumentExportMode ? `${selectedDocuments.size} document${selectedDocuments.size !== 1 ? 's' : ''} selected` : 'AI Insights'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {isDocumentExportMode ? (
                            <>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={handleCancelDocumentExport}
                                sx={{
                                  color: '#64748B',
                                  borderColor: '#E0E0E0',
                                  fontSize: '0.75rem',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    borderColor: '#B0B0B0'
                                  }
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={exportAsIs}
                                disabled={selectedDocuments.size === 0 || isExportingAsIs}
                                startIcon={isExportingAsIs ? <CircularProgress size={16} /> : <FileText />}
                                sx={{
                                  color: selectedDocuments.size > 0 ? '#4c3794' : 'rgba(76, 55, 148, 0.3)',
                                  borderColor: selectedDocuments.size > 0 ? '#4c3794' : 'rgba(76, 55, 148, 0.3)',
                                  fontSize: '0.75rem',
                                  '&:hover': {
                                    backgroundColor: selectedDocuments.size > 0 ? 'rgba(76, 55, 148, 0.05)' : 'rgba(76, 55, 148, 0.3)',
                                    borderColor: selectedDocuments.size > 0 ? '#7B1FA2' : 'rgba(76, 55, 148, 0.3)'
                                  },
                                  '&.Mui-disabled': {
                                    backgroundColor: 'rgba(76, 55, 148, 0.3)',
                                    color: 'rgba(255, 255, 255, 0.5)'
                                  }
                                }}
                              >
                                {isExportingAsIs ? 'Exporting...' : 'Export As-Is'}
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={handleDocumentExport}
                                disabled={selectedDocuments.size === 0 || isGeneratingContent}
                                startIcon={isGeneratingContent ? <CircularProgress size={16} /> : <DownloadIcon />}
                                sx={{
                                  backgroundColor: selectedDocuments.size > 0 ? '#4c3794' : 'rgba(76, 55, 148, 0.3)',
                                  fontSize: '0.75rem',
                                  '&:hover': {
                                    backgroundColor: selectedDocuments.size > 0 ? '#3d2e7a' : 'rgba(76, 55, 148, 0.3)'
                                  },
                                  '&.Mui-disabled': {
                                    backgroundColor: 'rgba(76, 55, 148, 0.3)',
                                    color: 'rgba(255, 255, 255, 0.5)'
                                  }
                                }}
                              >
                                {isGeneratingContent ? 'Generating...' : 'Generate Content'}
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={handleDocumentExportMode}
                              sx={{
                                borderColor: '#4c3794',
                                color: '#4c3794',
                                fontSize: '0.75rem',
                                '&:hover': {
                                  borderColor: '#7B1FA2',
                                  backgroundColor: 'rgba(76, 55, 148, 0.05)'
                                }
                              }}
                            >
                              Export Content
                            </Button>
                          )}
                        </Box>
                      </Box>

                      {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                          <CircularProgress sx={{ color: '#4c3794' }} />
                        </Box>
                      ) : (
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          {/* Tabs for grouped data */}
                          {groupedData.length > 0 && (
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                              <Tabs
                                value={selectedTab}
                                onChange={(e, newValue) => {
                                  console.log('Tab changed from', selectedTab, 'to', newValue);
                                  console.log('Group data:', groupedData);
                                  console.log('Selected group:', groupedData[newValue]);
                                  setSelectedTab(newValue);
                                }}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{
                                  '& .MuiTab-root': {
                                    color: '#64748B',
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                    textTransform: 'none',
                                    minWidth: 'auto',
                                    padding: '12px 16px',
                                    '&.Mui-selected': {
                                      color: '#9C27B0',
                                      fontWeight: 600
                                    }
                                  },
                                  '& .MuiTabs-indicator': {
                                    backgroundColor: '#9C27B0'
                                  }
                                }}
                              >
                                {groupedData.map((group, index) => (
                                  <Tab
                                    key={index}
                                    label={`${group.groupName} (${group.items.length})`}
                                    sx={{ minWidth: 'auto' }}
                                  />
                                ))}
                              </Tabs>
                            </Box>
                          )}

                          <Box
                            key={`tab-content-${selectedTab}`}
                            sx={{
                              flex: 1,
                              overflowY: 'auto',
                              minHeight: '300px',
                              maxHeight: groupedData.length > 0 ? 'calc(100vh - 400px)' : 'calc(100vh - 350px)',
                              paddingTop: groupedData.length > 0 ? '0px' : '20px'
                            }}
                            onScroll={handleScroll}
                          >
                            {/* Render grouped data as cards */}
                            {groupedData.length > 0 && groupedData[selectedTab] && (
                              <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                                gap: 2,
                                p: 1
                              }}>
                                {getVisibleItems().map((doc: DocumentResponse, index: number) => {
                                  const refDocs = getArrayValue<RefDoc>(doc, 'entitiesRelations');
                                  const title = getStringValue(doc, 'title');
                                  const briefDescription = getStringValue(doc, 'briefDescription');
                                  const topics = getArrayValue<string>(doc, 'topics');
                                  const highlights = getArrayValue<string>(doc, 'highlights');
                                  const sourceName = getStringValue(doc, 'sourceName');
                                  const sourceLink = getStringValue(doc, 'sourceLink');
                                  const documentIdentifier = getStringValue(doc, 'documentIdentifier');
                                  const uuid = getStringValue(doc, 'uuid');

                                  return (
                                    <Card
                                      key={uuid || `${selectedTab}-${doc.docUID}-${index}`}
                                      sx={{
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        border: isDocumentExportMode && selectedDocuments.has(uuid)
                                          ? '2px solid #4c3794'
                                          : selectedDocument?.docUID === doc.docUID
                                            ? '2px solid #9C27B0'
                                            : '1px solid #E0E0E0',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        backgroundColor: isDocumentExportMode && selectedDocuments.has(uuid)
                                          ? 'rgba(76, 55, 148, 0.05)'
                                          : 'transparent',
                                        position: 'relative',
                                        '&:hover': {
                                          borderColor: isDocumentExportMode ? '#4c3794' : '#9C27B0'
                                        }
                                      }}
                                      onClick={() => {
                                        if (isDocumentExportMode) {
                                          handleDocumentSelection(uuid);
                                        } else {
                                          setSelectedDocument(doc);
                                          // Reset chat to initial state when selecting new document
                                          // Chat state is now managed by the reusable Chatbot component
                                        }
                                      }}
                                    >
                                      {/* Selection checkbox for export mode */}
                                      {isDocumentExportMode && (
                                        <Box sx={{
                                          position: 'absolute',
                                          top: 8,
                                          right: 8,
                                          zIndex: 1
                                        }}>
                                          <input
                                            type="checkbox"
                                            checked={selectedDocuments.has(uuid)}
                                            onChange={() => handleDocumentSelection(uuid)}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                              width: '18px',
                                              height: '18px',
                                              accentColor: '#4c3794',
                                              cursor: 'pointer'
                                            }}
                                          />
                                        </Box>
                                      )}
                                      <CardContent sx={{ p: 2.5 }}>
                                        {/* Header */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                          <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                              variant="h6"
                                              sx={{
                                                fontWeight: 600,
                                                color: '#131F32',
                                                fontSize: '0.875rem',
                                                lineHeight: 1.3,
                                                mb: 0.5,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden'
                                              }}
                                            >
                                              {title || 'Untitled Document'}
                                            </Typography>
                                            {documentIdentifier && (
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: '#9C27B0',
                                                  fontWeight: 500,
                                                  fontSize: '0.7rem',
                                                  display: 'block',
                                                  mb: 1
                                                }}
                                              >
                                                Ref: {documentIdentifier}
                                              </Typography>
                                            )}
                                          </Box>
                                          {refDocs.length > 0 && (
                                            <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                                              {refDocs.map((refDoc, idx) => (
                                                <Tooltip key={idx} title="View Reference Document">
                                                  <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleRefDocClick(doc.docUID, refDoc.refDocUID);
                                                    }}
                                                    sx={{
                                                      color: '#9C27B0',
                                                      width: 24,
                                                      height: 24,
                                                      '&:hover': {
                                                        backgroundColor: '#F3E5F5'
                                                      }
                                                    }}
                                                  >
                                                    <LinkIcon sx={{ fontSize: 14 }} />
                                                  </IconButton>
                                                </Tooltip>
                                              ))}
                                            </Box>
                                          )}
                                        </Box>

                                        {/* Topics */}
                                        {topics.length > 0 && (
                                          <Box sx={{ mb: 2 }}>
                                            <Typography variant="caption" sx={{
                                              color: '#64748B',
                                              fontWeight: 600,
                                              fontSize: '0.7rem',
                                              textTransform: 'uppercase',
                                              letterSpacing: '0.5px',
                                              mb: 1,
                                              display: 'block'
                                            }}>
                                              Topics
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                              {topics.slice(0, expandedCards[doc.docUID]?.topics ? topics.length : 3).map((topic, idx) => (
                                                <Chip
                                                  key={idx}
                                                  label={topic}
                                                  size="small"
                                                  sx={{
                                                    backgroundColor: '#F3E5F5',
                                                    color: '#9C27B0',
                                                    fontSize: '0.6rem',
                                                    height: '18px',
                                                    '& .MuiChip-label': {
                                                      padding: '0 6px'
                                                    }
                                                  }}
                                                />
                                              ))}
                                              {topics.length > 3 && (
                                                <Chip
                                                  label={expandedCards[doc.docUID]?.topics ? 'Show less' : `+${topics.length - 3} more`}
                                                  size="small"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpandedCards(prev => ({
                                                      ...prev,
                                                      [doc.docUID]: {
                                                        ...prev[doc.docUID],
                                                        topics: !prev[doc.docUID]?.topics
                                                      }
                                                    }));
                                                  }}
                                                  sx={{
                                                    backgroundColor: '#F5F5F5',
                                                    color: '#64748B',
                                                    fontSize: '0.6rem',
                                                    height: '18px',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                      backgroundColor: '#E0E0E0'
                                                    },
                                                    '& .MuiChip-label': {
                                                      padding: '0 6px'
                                                    }
                                                  }}
                                                />
                                              )}
                                            </Box>
                                          </Box>
                                        )}

                                        {/* Description */}
                                        {briefDescription && (
                                          <Box sx={{ mb: 2 }}>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                color: '#64748B',
                                                fontSize: '0.75rem',
                                                lineHeight: 1.4,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden'
                                              }}
                                            >
                                              {briefDescription}
                                            </Typography>
                                          </Box>
                                        )}

                                        {/* Highlights Preview */}
                                        {highlights.length > 0 && (
                                          <Box sx={{ mb: 2 }}>
                                            <Typography variant="caption" sx={{
                                              color: '#64748B',
                                              fontWeight: 600,
                                              fontSize: '0.7rem',
                                              textTransform: 'uppercase',
                                              letterSpacing: '0.5px',
                                              mb: 1,
                                              display: 'block'
                                            }}>
                                              Key Points
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                              {highlights.slice(0, expandedCards[doc.docUID]?.highlights ? highlights.length : 2).map((highlight, idx) => (
                                                <Typography key={idx} variant="body2" sx={{
                                                  color: '#64748B',
                                                  fontSize: '0.7rem',
                                                  display: 'flex',
                                                  alignItems: 'flex-start',
                                                  gap: 0.5
                                                }}>
                                                  <Box sx={{
                                                    width: 3,
                                                    height: 3,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#9C27B0',
                                                    mt: 0.5,
                                                    flexShrink: 0
                                                  }} />
                                                  <span style={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden'
                                                  }}>
                                                    {highlight}
                                                  </span>
                                                </Typography>
                                              ))}
                                              {highlights.length > 2 && (
                                                <Typography
                                                  variant="caption"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpandedCards(prev => ({
                                                      ...prev,
                                                      [doc.docUID]: {
                                                        ...prev[doc.docUID],
                                                        highlights: !prev[doc.docUID]?.highlights
                                                      }
                                                    }));
                                                  }}
                                                  sx={{
                                                    color: '#9C27B0',
                                                    fontSize: '0.65rem',
                                                    fontStyle: 'italic',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                      textDecoration: 'underline'
                                                    }
                                                  }}
                                                >
                                                  {expandedCards[doc.docUID]?.highlights ? 'Show less' : `+${highlights.length - 2} more highlights`}
                                                </Typography>
                                              )}
                                            </Box>
                                          </Box>
                                        )}

                                        {/* Source Link */}
                                        {sourceName && sourceLink && (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1,
                                              p: 1,
                                              backgroundColor: '#F8F9FA',
                                              borderRadius: '6px',
                                              border: '1px solid #E5E9EF',
                                              transition: 'all 0.2s ease',
                                              cursor: 'pointer',
                                              '&:hover': {
                                                backgroundColor: '#F3E5F5',
                                                borderColor: '#9C27B0'
                                              }
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              window.open(sourceLink, '_blank');
                                            }}
                                          >
                                            <DescriptionOutlinedIcon sx={{
                                              fontSize: 16,
                                              color: '#9C27B0',
                                              flexShrink: 0
                                            }} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                              <Typography variant="caption" sx={{
                                                color: '#131F32',
                                                fontSize: '0.65rem',
                                                fontWeight: 500,
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                                display: 'block'
                                              }}>
                                                {sourceName}
                                              </Typography>
                                            </Box>
                                            <OpenInNewIcon sx={{
                                              fontSize: 14,
                                              color: '#9C27B0',
                                              flexShrink: 0
                                            }} />
                                          </Box>
                                        )}

                                        {/* Selection Indicator */}
                                        {selectedDocument?.docUID === doc.docUID && (
                                          <Box sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: 3,
                                            backgroundColor: '#9C27B0'
                                          }} />
                                        )}
                                      </CardContent>
                                    </Card>
                                  );
                                })}
                              </Box>
                            )}

                            {/* Render flat data - only if no grouped data */}
                            {groupedData.length === 0 && documents.length > 0 && documents.map((doc, index) => {
                              const refDocs = getArrayValue<RefDoc>(doc, 'entitiesRelations');
                              const title = getStringValue(doc, 'title');
                              const briefDescription = getStringValue(doc, 'briefDescription');
                              const topics = getArrayValue<string>(doc, 'topics');
                              const highlights = getArrayValue<string>(doc, 'highlights');
                              const sourceName = getStringValue(doc, 'sourceName');
                              const sourceLink = getStringValue(doc, 'sourceLink');
                              const documentIdentifier = getStringValue(doc, 'documentIdentifier');
                              const uuid = getStringValue(doc, 'uuid');

                              return (
                                <StyledAccordion
                                  key={uuid || `flat-${doc.docUID}-${index}`}
                                  expanded={selectedDocument?.docUID === doc.docUID}
                                  onChange={(event, expanded) => {
                                    if (expanded) {
                                      setSelectedDocument(doc);
                                      // Reset chat to initial state when selecting new document
                                      // Chat state is now managed by the reusable Chatbot component
                                      // No need to reset messages, newMessage, or suggestedQuestions
                                    } else {
                                      // Unselect document when accordion is collapsed
                                      setSelectedDocument(null);
                                    }
                                  }}
                                >
                                  <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ width: '100%' }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box sx={{ flex: 1 }}>
                                          <Typography
                                            variant="subtitle1"
                                            sx={{
                                              fontWeight: 600,
                                              color: '#131F32',
                                              marginBottom: '4px',
                                              fontSize: '0.875rem'
                                            }}
                                          >
                                            {title || 'Untitled Document'}
                                            {documentIdentifier && (
                                              <span style={{
                                                color: '#9C27B0',
                                                fontWeight: 500,
                                                fontSize: '0.75rem'
                                              }}>
                                                {' '}(Ref: {documentIdentifier})
                                              </span>
                                            )}
                                          </Typography>
                                        </Box>
                                        {refDocs.length > 0 && (
                                          <Box sx={{ display: 'flex', gap: 1 }}>
                                            {refDocs.map((refDoc, idx) => (
                                              <Tooltip key={idx} title="View Reference Document">
                                                <IconButton
                                                  size="small"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRefDocClick(doc.docUID, refDoc.refDocUID);
                                                  }}
                                                  sx={{
                                                    color: '#9C27B0',
                                                    '&:hover': {
                                                      backgroundColor: '#F3E5F5'
                                                    }
                                                  }}
                                                >
                                                  <LinkIcon fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                            ))}
                                          </Box>
                                        )}
                                      </Box>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {topics.map((topic, idx) => (
                                          <Chip
                                            key={idx}
                                            label={topic}
                                            size="small"
                                            sx={{
                                              backgroundColor: '#F3E5F5',
                                              color: '#9C27B0',
                                              fontSize: '0.625rem',
                                              height: '20px',
                                              '& .MuiChip-label': {
                                                padding: '0 6px'
                                              },
                                              '&:hover': {
                                                backgroundColor: '#F3E5F5'
                                              }
                                            }}
                                          />
                                        ))}
                                      </Box>
                                    </Box>
                                  </StyledAccordionSummary>
                                  <StyledAccordionDetails>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#131F32', fontSize: '0.75rem', fontWeight: 600 }}>
                                        Topics
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: '#64748B',
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                          fontSize: '0.75rem'
                                        }}
                                      >
                                        {briefDescription || 'No description available'}
                                      </Typography>
                                      {highlights.length > 0 && (
                                        <Box>
                                          <Typography variant="subtitle2" sx={{ mb: 1, color: '#131F32', fontSize: '0.75rem' }}>
                                            Highlights
                                          </Typography>
                                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {highlights.map((highlight, idx) => (
                                              <Typography key={idx} variant="body2" sx={{ color: '#64748B', fontSize: '0.75rem' }}>
                                                • {highlight}
                                              </Typography>
                                            ))}
                                          </Box>
                                        </Box>
                                      )}
                                      {sourceName && sourceLink && (
                                        <Box>
                                          <Typography variant="subtitle2" sx={{ mb: 1, color: '#131F32', fontSize: '0.75rem', fontWeight: 600 }}>
                                            Source
                                          </Typography>
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1,
                                              p: 1.5,
                                              backgroundColor: '#F8F9FA',
                                              borderRadius: '8px',
                                              border: '1px solid #E5E9EF',
                                              transition: 'all 0.2s ease',
                                              cursor: 'pointer',
                                              '&:hover': {
                                                backgroundColor: '#F3E5F5',
                                                borderColor: '#9C27B0',
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 2px 8px rgba(156, 39, 176, 0.1)'
                                              }
                                            }}
                                            onClick={() => window.open(sourceLink, '_blank')}
                                          >
                                            <DescriptionOutlinedIcon sx={{
                                              fontSize: 18,
                                              color: '#9C27B0',
                                              flexShrink: 0
                                            }} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                              <Typography variant="body2" sx={{
                                                color: '#131F32',
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap'
                                              }}>
                                                {sourceName}
                                              </Typography>

                                            </Box>
                                            <OpenInNewIcon sx={{
                                              fontSize: 16,
                                              color: '#9C27B0',
                                              flexShrink: 0
                                            }} />
                                          </Box>
                                        </Box>
                                      )}
                                    </Box>
                                  </StyledAccordionDetails>
                                </StyledAccordion>
                              );
                            })}
                          </Box>

                          {/* Loading more indicator */}
                          {isLoadingMore && (
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              py: 2,
                              borderTop: '1px solid #E0E0E0'
                            }}>
                              <CircularProgress size={24} sx={{ color: '#9C27B0' }} />
                              <Typography variant="body2" sx={{ ml: 1, color: '#64748B' }}>
                                Loading more documents...
                              </Typography>
                            </Box>
                          )}

                        </Box>
                      )}
                    </Paper>
                  </Box>

                  {/* Right Panel - Chat Interface */}
                  {isChatOpen && (
                    <Box sx={{ flex: 1.3, width: '35%', flexShrink: 0, overflow: 'hidden' }}>
                      <Paper sx={{
                        p: 0,
                        borderRadius: '16px',
                        height: 'calc(100vh - 180px)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                      }}>
                        {/* Chat Panel Header with Close Button */}
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          p: 1.5,
                          borderBottom: '1px solid #e9ecef'
                        }}>
                          <IconButton
                            onClick={handleCloseChat}
                            sx={{
                              color: '#64748B',
                              '&:hover': {
                                backgroundColor: 'rgba(76, 55, 148, 0.1)',
                                color: '#4c3794'
                              }
                            }}
                            aria-label="Close chat"
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                        {/* Chat Interface - Using Reusable Chatbot */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: 'calc(100% - 50px)' }}>
                          <Chatbot
                            placeholder="Type your message..."
                            welcomeMessage={selectedDocument ? `Hello! I'm your AI assistant. How can I help you analyze "${getStringValue(selectedDocument, 'title')}"?` : "Hello! I'm your AI assistant. How can I help you analyze the documents?"}
                            enableVoice={true}
                            enableGovAPI={true}
                            apiConfig={chatbotApiConfig}
                            getAuthToken={async () => localStorage.getItem('token')}
                          />
                        </Box>
                      </Paper>
                    </Box>
                  )}
                </Box>
              )}

              {/* Documents Tab */}
              {topLevelTab === 2 && (
                <Box sx={{ display: 'flex', gap: '24px', width: '100%', overflow: 'hidden' }}>
                  {/* Left Panel - Document List */}
                  <Box sx={{ flex: isChatOpen ? 1.7 : 2, width: isChatOpen ? '65%' : '100%', minWidth: 0, overflow: 'hidden', transition: 'width 0.3s ease-in-out' }}>
                    <Paper sx={{ p: 3, borderRadius: '16px', height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3
                      }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>Document List</Typography>
                        <Tooltip title={!canEditProject ? "Edit permission required to add documents" : ""}>
                          <span>
                            <Button
                              variant="contained"
                              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                              onClick={() => {
                                // Populate keywords from selected project
                                setUploadFormData({
                                  topic: '',
                                  keywords: selectedProject?.configuration?.keywords || []
                                });
                                setUploadDialogOpen(true);
                              }}
                              disabled={!canEditProject}
                              sx={{
                                backgroundColor: canEditProject ? '#9C27B0' : 'rgba(156, 39, 176, 0.3)',
                                fontSize: '0.75rem',
                                padding: '6px 12px',
                                '&:hover': {
                                  backgroundColor: canEditProject ? '#7B1FA2' : 'rgba(156, 39, 176, 0.3)'
                                },
                                '&.Mui-disabled': {
                                  backgroundColor: 'rgba(156, 39, 176, 0.3)',
                                  color: '#ffffff'
                                }
                              }}
                            >
                              Add Document
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>

                      {/* Source Type Filter */}
                      <Box sx={{
                        mb: 2,
                        borderBottom: 1,
                        borderColor: 'divider'
                      }}>
                        <Tabs
                          value={selectedSourceType}
                          onChange={(e, newValue) => setSelectedSourceType(newValue)}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{
                            '& .MuiTab-root': {
                              textTransform: 'none',
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              minWidth: 'auto',
                              px: 2,
                              py: 1,
                              color: '#64748B',
                              '&.Mui-selected': {
                                color: '#9C27B0',
                                fontWeight: 600
                              }
                            },
                            '& .MuiTabs-indicator': {
                              backgroundColor: '#9C27B0'
                            }
                          }}
                        >
                          <Tab
                            value="all"
                            label={`All (${getSourceTypeCounts(documents).all})`}
                          />
                          <Tab
                            value="user_upload"
                            label={`User Uploaded (${getSourceTypeCounts(documents).user_upload})`}
                          />
                          <Tab
                            value="shared_drive"
                            label={`Drive Synced (${getSourceTypeCounts(documents).shared_drive})`}
                          />
                          <Tab
                            value="web_search"
                            label={`Web Search (${getSourceTypeCounts(documents).web_search})`}
                          />
                        </Tabs>
                      </Box>

                      {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                          <CircularProgress sx={{ color: '#4c3794' }} />
                        </Box>
                      ) : (
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <Box
                            sx={{
                              flex: 1,
                              overflowY: 'auto',
                              minHeight: '300px',
                              maxHeight: 'calc(100vh - 420px)',
                              paddingTop: '20px'
                            }}
                            onScroll={handleScroll}
                          >
                            {/* Render filtered documents */}
                            {filterDocumentsBySourceType(documents, selectedSourceType).length === 0 ? (
                              <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 8,
                                textAlign: 'center'
                              }}>
                                <DescriptionIcon sx={{ fontSize: 64, color: '#9CA3AF', mb: 2 }} />
                                <Typography variant="body1" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                                  No documents found
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                                  {selectedSourceType === 'all'
                                    ? 'No documents available in this project'
                                    : `No documents with source type: ${selectedSourceType.replace('_', ' ')}`
                                  }
                                </Typography>
                              </Box>
                            ) : (
                              filterDocumentsBySourceType(documents, selectedSourceType).map((doc, index) => {
                                const refDocs = getArrayValue<RefDoc>(doc, 'entitiesRelations');
                                const title = getStringValue(doc, 'title');
                                const briefDescription = getStringValue(doc, 'briefDescription');
                                const topics = getArrayValue<string>(doc, 'topics');
                                const highlights = getArrayValue<string>(doc, 'highlights');
                                const sourceName = getStringValue(doc, 'sourceName');
                                const sourceLink = getStringValue(doc, 'sourceLink');
                                const documentIdentifier = getStringValue(doc, 'documentIdentifier');

                                return (
                                  <StyledAccordion
                                    key={doc.docUID}
                                    expanded={selectedDocument?.docUID === doc.docUID}
                                    onChange={(event, expanded) => {
                                      if (expanded) {
                                        setSelectedDocument(doc);
                                        // Reset chat to initial state when selecting new document
                                        // Chat state is now managed by the reusable Chatbot component
                                      } else {
                                        // Unselect document when accordion is collapsed
                                        setSelectedDocument(null);
                                      }
                                    }}
                                  >
                                    <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                                      <Box sx={{ width: '100%' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                          <Box sx={{ flex: 1 }}>
                                            <Typography
                                              variant="subtitle1"
                                              sx={{
                                                fontWeight: 600,
                                                color: '#131F32',
                                                marginBottom: '4px',
                                                fontSize: '0.875rem'
                                              }}
                                            >
                                              {title || 'Untitled Document'}
                                              {documentIdentifier && (
                                                <span style={{
                                                  color: '#9C27B0',
                                                  fontWeight: 500,
                                                  fontSize: '0.75rem'
                                                }}>
                                                  {' '}(Ref: {documentIdentifier})
                                                </span>
                                              )}
                                            </Typography>
                                          </Box>
                                          {refDocs.length > 0 && (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                              {refDocs.map((refDoc, idx) => (
                                                <Tooltip key={idx} title="View Reference Document">
                                                  <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleRefDocClick(doc.docUID, refDoc.refDocUID);
                                                    }}
                                                    sx={{
                                                      color: '#9C27B0',
                                                      '&:hover': {
                                                        backgroundColor: '#F3E5F5'
                                                      }
                                                    }}
                                                  >
                                                    <LinkIcon fontSize="small" />
                                                  </IconButton>
                                                </Tooltip>
                                              ))}
                                            </Box>
                                          )}
                                        </Box>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                          {topics.map((topic, idx) => (
                                            <Chip
                                              key={idx}
                                              label={topic}
                                              size="small"
                                              sx={{
                                                backgroundColor: '#F3E5F5',
                                                color: '#9C27B0',
                                                fontSize: '0.625rem',
                                                height: '20px',
                                                '& .MuiChip-label': {
                                                  padding: '0 6px'
                                                },
                                                '&:hover': {
                                                  backgroundColor: '#F3E5F5'
                                                }
                                              }}
                                            />
                                          ))}
                                        </Box>
                                      </Box>
                                    </StyledAccordionSummary>
                                    <StyledAccordionDetails>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#131F32', fontSize: '0.75rem', fontWeight: 600 }}>
                                          Topics
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: '#64748B',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            fontSize: '0.75rem'
                                          }}
                                        >
                                          {briefDescription || 'No description available'}
                                        </Typography>
                                        {highlights.length > 0 && (
                                          <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 1, color: '#131F32', fontSize: '0.75rem' }}>
                                              Highlights
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                              {highlights.map((highlight, idx) => (
                                                <Typography key={idx} variant="body2" sx={{ color: '#64748B', fontSize: '0.75rem' }}>
                                                  • {highlight}
                                                </Typography>
                                              ))}
                                            </Box>
                                          </Box>
                                        )}
                                        {sourceName && sourceLink && (
                                          <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 1, color: '#131F32', fontSize: '0.75rem', fontWeight: 600 }}>
                                              Source
                                            </Typography>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                p: 1.5,
                                                backgroundColor: '#F8F9FA',
                                                borderRadius: '8px',
                                                border: '1px solid #E5E9EF',
                                                transition: 'all 0.2s ease',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                  backgroundColor: '#F3E5F5',
                                                  borderColor: '#9C27B0',
                                                  transform: 'translateY(-1px)',
                                                  boxShadow: '0 2px 8px rgba(156, 39, 176, 0.1)'
                                                }
                                              }}
                                              onClick={() => window.open(sourceLink, '_blank')}
                                            >
                                              <DescriptionOutlinedIcon sx={{
                                                fontSize: 18,
                                                color: '#9C27B0',
                                                flexShrink: 0
                                              }} />
                                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" sx={{
                                                  color: '#131F32',
                                                  fontSize: '0.75rem',
                                                  fontWeight: 500,
                                                  textOverflow: 'ellipsis',
                                                  overflow: 'hidden',
                                                  whiteSpace: 'nowrap'
                                                }}>
                                                  {sourceName}
                                                </Typography>

                                              </Box>
                                              <OpenInNewIcon sx={{
                                                fontSize: 16,
                                                color: '#9C27B0',
                                                flexShrink: 0
                                              }} />
                                            </Box>
                                          </Box>
                                        )}
                                      </Box>
                                    </StyledAccordionDetails>
                                  </StyledAccordion>
                                );
                              })
                            )}
                          </Box>

                          {/* Loading more indicator */}
                          {isLoadingMore && (
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              py: 2,
                              borderTop: '1px solid #E0E0E0'
                            }}>
                              <CircularProgress size={24} sx={{ color: '#9C27B0' }} />
                              <Typography variant="body2" sx={{ ml: 1, color: '#64748B' }}>
                                Loading more documents...
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Paper>
                  </Box>

                  {/* Right Panel - Chat Interface */}
                  {isChatOpen && (
                    <Box sx={{ flex: 1.3, width: '35%', flexShrink: 0, overflow: 'hidden' }}>
                      <Paper sx={{
                        p: 0,
                        borderRadius: '16px',
                        height: 'calc(100vh - 180px)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                      }}>
                        {/* Chat Panel Header with Close Button */}
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          p: 1.5,
                          borderBottom: '1px solid #e9ecef'
                        }}>
                          <IconButton
                            onClick={handleCloseChat}
                            sx={{
                              color: '#64748B',
                              '&:hover': {
                                backgroundColor: 'rgba(76, 55, 148, 0.1)',
                                color: '#4c3794'
                              }
                            }}
                            aria-label="Close chat"
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                        {/* Chat Interface - Using Reusable Chatbot */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: 'calc(100% - 50px)' }}>
                          <Chatbot
                            placeholder="Type your message..."
                            welcomeMessage={selectedDocument ? `Hello! I'm your AI assistant. How can I help you analyze "${getStringValue(selectedDocument, 'title')}"?` : "Hello! I'm your AI assistant. How can I help you analyze the documents?"}
                            enableVoice={true}
                            enableGovAPI={true}
                            apiConfig={chatbotApiConfig}
                            getAuthToken={async () => localStorage.getItem('token')}
                          />
                        </Box>
                      </Paper>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}

          {/* Floating Chat Button - Only show when chat is not open and not in fullscreen and not on dashboard tab */}
          {!isChatOpen && !isFullScreen && topLevelTab !== 0 && (
            <Fab
              color="primary"
              aria-label="Open AI chat assistant"
              onClick={handleChatClick}
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                backgroundColor: '#4c3794',
                '&:hover': {
                  backgroundColor: '#3d2e7a',
                },
                '&:active': {
                  transform: 'scale(0.95)'
                },
                zIndex: 1000,
              }}
            >
              <img src={botIcon} alt="AI Chat Assistant" style={{ width: 74, height: 74 }} />
            </Fab>
          )}

          {/* Upload Dialog */}
          <Dialog
            open={uploadDialogOpen}
            onClose={() => {
              setUploadDialogOpen(false);
              // Reset form data when dialog closes
              setUploadFormData({
                topic: '',
                keywords: []
              });
            }}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Upload New Document</Typography>
                <IconButton onClick={() => {
                  setUploadDialogOpen(false);
                  // Reset form data when dialog closes
                  setUploadFormData({
                    topic: '',
                    keywords: []
                  });
                }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>

                {/* File Upload Section */}
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#131F32', fontWeight: 600 }}>
                  Upload Method
                </Typography>

                {/* Upload Method Selection */}
                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: uploadMethod === 'manual' ? '2px solid #9C27B0' : '1px solid #E0E0E0',
                          borderRadius: '12px',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: '#9C27B0',
                            backgroundColor: 'rgba(156, 39, 176, 0.05)'
                          }
                        }}
                        onClick={() => setUploadMethod('manual')}
                      >
                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                          <UploadFileIcon sx={{ fontSize: 32, color: '#9C27B0', mb: 1 }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Manual Upload
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Upload files from device or Google Drive
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          cursor: 'not-allowed',
                          border: '1px solid #E0E0E0',
                          borderRadius: '12px',
                          opacity: 0.6,
                          backgroundColor: '#F5F5F5'
                        }}
                      >
                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                          <DescriptionIcon sx={{ fontSize: 32, color: '#9CA3AF', mb: 1 }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#9CA3AF' }}>
                            Auto Upload from Sources
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                            Coming Soon - Auto fetch from data sources
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: uploadMethod === 'auto-web' ? '2px solid #9C27B0' : '1px solid #E0E0E0',
                          borderRadius: '12px',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: '#9C27B0',
                            backgroundColor: 'rgba(156, 39, 176, 0.05)'
                          }
                        }}
                        onClick={() => setUploadMethod('auto-web')}
                      >
                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                          <SearchIcon sx={{ fontSize: 32, color: '#9C27B0', mb: 1 }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Auto Web Search
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Search and fetch documents from web
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>

                {/* Manual Upload Section */}
                {uploadMethod === 'manual' && (
                  <Box>
                    {/* Manual Upload Type Selection */}
                    <Typography variant="subtitle2" sx={{ mb: 2, color: '#131F32', fontWeight: 600 }}>
                      Select Upload Type
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Card
                            sx={{
                              cursor: 'pointer',
                              border: manualUploadType === 'device' ? '2px solid #9C27B0' : '1px solid #E0E0E0',
                              borderRadius: '12px',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: '#9C27B0',
                                backgroundColor: 'rgba(156, 39, 176, 0.05)'
                              }
                            }}
                            onClick={() => setManualUploadType('device')}
                          >
                            <CardContent sx={{ textAlign: 'center', p: 3 }}>
                              <UploadFileIcon sx={{ fontSize: 32, color: '#9C27B0', mb: 1 }} />
                              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                Upload from Device
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Select a file from your computer or device
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Card
                            sx={{
                              cursor: 'pointer',
                              border: manualUploadType === 'google-drive' ? '2px solid #9C27B0' : '1px solid #E0E0E0',
                              borderRadius: '12px',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: '#9C27B0',
                                backgroundColor: 'rgba(156, 39, 176, 0.05)'
                              }
                            }}
                            onClick={() => setManualUploadType('google-drive')}
                          >
                            <CardContent sx={{ textAlign: 'center', p: 3 }}>
                              <AssessmentIcon sx={{ fontSize: 32, color: '#9C27B0', mb: 1 }} />
                              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                Google Drive
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Select a file from your Google Drive
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Device Upload Section */}
                    {manualUploadType === 'device' && (
                      <Box sx={{
                        border: '2px dashed #E5E9EF',
                        borderRadius: '12px',
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: '#F8F9FA',
                        mt: 2
                      }}>
                        <input
                          type="file"
                          id="document-upload"
                          hidden
                          onChange={handleFileUpload}
                          accept=".pdf"
                        />
                        <label htmlFor="document-upload">
                          <Button
                            component="span"
                            variant="contained"
                            startIcon={<UploadFileIcon />}
                            disabled={uploadLoading}
                            sx={{
                              backgroundColor: '#9C27B0',
                              '&:hover': {
                                backgroundColor: '#7B1FA2'
                              }
                            }}
                          >
                            {uploadLoading ? 'Uploading...' : 'Upload Document'}
                          </Button>
                        </label>
                        <Typography variant="body2" sx={{ mt: 2, color: '#64748B' }}>
                          Supported formats: PDF
                        </Typography>
                      </Box>
                    )}

                    {/* Google Drive Upload Section */}
                    {manualUploadType === 'google-drive' && (
                      <Box sx={{
                        border: '2px dashed #E5E9EF',
                        borderRadius: '12px',
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: '#F8F9FA',
                        mt: 2
                      }}>
                        <Typography variant="h6" sx={{ mb: 2, color: '#131F32', fontWeight: 600 }}>
                          Select a file from Google Drive
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 3, color: '#64748B' }}>
                          Authenticate with Google Drive and select a file to upload.
                        </Typography>

                        {googleDriveAuthState.isAuthenticated && (
                          <Box sx={{
                            mb: 2,
                            p: 2,
                            backgroundColor: 'rgba(16, 185, 129, 0.05)',
                            borderRadius: 2,
                            border: '1px solid rgba(16, 185, 129, 0.1)'
                          }}>
                            <Typography variant="body2" sx={{ color: '#10B981', fontWeight: 600, mb: 1 }}>
                              ✅ Connected to Google Drive
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#10B981', fontSize: '0.875rem', mb: 2 }}>
                              You're currently connected. If you need to switch accounts, click "Switch Account" below.
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={clearGoogleDriveAuth}
                              sx={{
                                borderColor: '#10B981',
                                color: '#10B981',
                                '&:hover': {
                                  borderColor: '#059669',
                                  backgroundColor: 'rgba(16, 185, 129, 0.05)'
                                }
                              }}
                            >
                              Switch Account
                            </Button>
                          </Box>
                        )}

                        <Button
                          variant="contained"
                          startIcon={<AssessmentIcon />}
                          onClick={openGoogleDriveDialog}
                          sx={{
                            backgroundColor: '#9C27B0',
                            '&:hover': {
                              backgroundColor: '#7B1FA2',
                            },
                            '&.Mui-disabled': {
                              backgroundColor: 'rgba(156, 39, 176, 0.3)',
                              color: '#ffffff'
                            }
                          }}
                        >
                          {googleDriveAuthState.isAuthenticated ? 'Browse & Select File' : 'Connect to Google Drive'}
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Auto Upload from Sources Section */}
                {uploadMethod === 'auto-sources' && (
                  <Box sx={{
                    border: '2px dashed #E5E9EF',
                    borderRadius: '12px',
                    p: 4,
                    textAlign: 'center',
                    backgroundColor: '#F8F9FA',
                    mt: 2
                  }}>
                    <DescriptionIcon sx={{ fontSize: 48, color: '#9CA3AF', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 2, color: '#9CA3AF', fontWeight: 600 }}>
                      Auto Upload from Sources
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3, color: '#9CA3AF' }}>
                      This feature is coming soon. You'll be able to automatically fetch documents from various data sources.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>
                      Document Topics and Document Keywords will be automatically configured based on the source.
                    </Typography>
                  </Box>
                )}

                {/* Auto Web Search Section */}
                {uploadMethod === 'auto-web' && (
                  <Box>
                    {/* Topic Field */}
                    <TextField
                      fullWidth
                      label="Document Topic"
                      value={uploadFormData.topic}
                      onChange={(e) => setUploadFormData(prev => ({ ...prev, topic: e.target.value }))}
                      sx={{ mb: 3 }}
                      placeholder="Enter the topic for web search (e.g., 'AI regulations', 'Climate change policies')"
                    />

                    {/* Keywords Section */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 2, color: '#131F32', fontWeight: 600 }}>
                        Document Keywords
                      </Typography>

                      {/* Keyword Input */}
                      <Box sx={{ mb: 2 }}>
                        <TextField
                          fullWidth
                          placeholder="Add a keyword and press Enter"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              const input = e.target as HTMLInputElement;
                              const keyword = input.value.trim();
                              if (keyword && !uploadFormData.keywords.includes(keyword)) {
                                setUploadFormData(prev => ({
                                  ...prev,
                                  keywords: [...prev.keywords, keyword]
                                }));
                                input.value = '';
                              }
                            }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#9C27B0'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#9C27B0'
                              }
                            }
                          }}
                        />
                      </Box>

                      <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        p: 2,
                        backgroundColor: '#F8F9FA',
                        borderRadius: '8px',
                        border: '1px solid #E5E9EF',
                        minHeight: '60px'
                      }}>
                        {uploadFormData.keywords.length > 0 ? (
                          uploadFormData.keywords.map((keyword, index) => (
                            <Chip
                              key={index}
                              label={keyword}
                              onDelete={() => {
                                const updatedKeywords = uploadFormData.keywords.filter((_, i) => i !== index);
                                setUploadFormData(prev => ({ ...prev, keywords: updatedKeywords }));
                              }}
                              sx={{
                                backgroundColor: '#F3E5F5',
                                color: '#9C27B0',
                                '& .MuiChip-deleteIcon': {
                                  color: '#9C27B0',
                                  '&:hover': {
                                    color: '#7B1FA2'
                                  }
                                }
                              }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" sx={{ color: '#64748B', fontStyle: 'italic' }}>
                            No keywords added yet. Add keywords above to help with web search.
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Auto Scheduling Configuration */}
                    <Box sx={{
                      border: '2px solid #E5E9EF',
                      borderRadius: '12px',
                      p: 4,
                      backgroundColor: '#F8F9FA',
                      mt: 2
                    }}>
                      <Typography variant="h6" sx={{ mb: 2, color: '#131F32', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ScheduleIcon sx={{ color: '#9C27B0' }} />
                        Auto Scheduling Configuration
                      </Typography>

                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Schedule Frequency</InputLabel>
                            <Select
                              value={scheduleFrequency}
                              onChange={(e) => setScheduleFrequency(e.target.value as 'daily' | 'weekly' | 'monthly')}
                              label="Schedule Frequency"
                            >
                              <MenuItem value="daily">Daily</MenuItem>
                              <MenuItem value="weekly">Weekly</MenuItem>
                              <MenuItem value="monthly">Monthly</MenuItem>
                            </Select>
                            <FormHelperText>How often to run the web search</FormHelperText>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Time"
                            type="time"
                            value={scheduleTime}
                            onChange={(e) => setScheduleTime(e.target.value)}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            sx={{ mb: 2 }}
                            helperText="Time to run the scheduled job"
                          />
                        </Grid>
                      </Grid>

                      {/* Error and Success Messages */}
                      {webSearchError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {webSearchError}
                        </Alert>
                      )}

                      {webSearchSuccess && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          {webSearchSuccess}
                        </Alert>
                      )}

                      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          onClick={handleWebSearch}
                          disabled={isWebSearchLoading}
                          sx={{
                            backgroundColor: '#9C27B0',
                            '&:hover': {
                              backgroundColor: '#7B1FA2'
                            },
                            '&.Mui-disabled': {
                              backgroundColor: 'rgba(156, 39, 176, 0.3)',
                              color: 'rgba(255, 255, 255, 0.5)'
                            }
                          }}
                        >
                          {isWebSearchLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={16} sx={{ color: 'white' }} />
                              Scheduling...
                            </Box>
                          ) : (
                            'Schedule Web Search Job'
                          )}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button
                onClick={() => {
                  setUploadDialogOpen(false);
                  // Reset form data when dialog closes
                  setUploadFormData({
                    topic: '',
                    keywords: []
                  });
                }}
                sx={{ color: '#64748B' }}
              >
                Cancel
              </Button>
            </DialogActions>
          </Dialog>

          {/* Reference Document Dialog */}
          <Dialog
            open={refDocDialogOpen}
            onClose={() => setRefDocDialogOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden'
              }
            }}
          >
            <DialogTitle sx={{
              backgroundColor: '#F8F9FA',
              borderBottom: '1px solid #E5E9EF',
              p: 3
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <DescriptionIcon sx={{ color: '#9C27B0', fontSize: 24 }} />
                  <Typography variant="h6" sx={{
                    fontWeight: 600,
                    color: '#131F32',
                    fontSize: '1.25rem'
                  }}>
                    Document Updates
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => setRefDocDialogOpen(false)}
                  sx={{
                    color: '#64748B',
                    '&:hover': {
                      backgroundColor: '#F3E5F5',
                      color: '#9C27B0'
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              {loadingDifferences ? (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '200px'
                }}>
                  <CircularProgress sx={{ color: '#9C27B0' }} />
                </Box>
              ) : docDifferences && docDifferences.length > 0 ? (
                <Box sx={{ mt: 2 }}>
                  {docDifferences[currentPage]?.entries
                    .filter((entry: any) =>
                      entry.key === 'changeType' ||
                      entry.key === 'amendedContent' ||
                      entry.key === 'changeSummary'
                    )
                    .map((entry: any, index: number) => (
                      <Box
                        key={index}
                        sx={{
                          mb: 3,
                          p: 2,
                          backgroundColor: '#F8F9FA',
                          borderRadius: '8px',
                          border: '1px solid #E5E9EF',
                          '&:hover': {
                            borderColor: '#9C27B0',
                            backgroundColor: '#F3E5F5'
                          }
                        }}
                      >
                        <Typography variant="subtitle2" sx={{
                          fontWeight: 600,
                          color: '#131F32',
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}>
                          {entry.key === 'changeType' && <TrendingUpIcon sx={{ fontSize: 18, color: '#9C27B0' }} />}
                          {entry.key === 'amendedContent' && <EditIcon sx={{ fontSize: 18, color: '#9C27B0' }} />}
                          {entry.key === 'changeSummary' && <SummarizeIcon sx={{ fontSize: 18, color: '#9C27B0' }} />}
                          {entry.label}
                        </Typography>
                        <Typography variant="body2" sx={{
                          color: '#666',
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.6,
                          fontSize: '0.875rem'
                        }}>
                          {entry.value}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              ) : (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '200px',
                  gap: 2
                }}>
                  <InfoOutlinedIcon sx={{ fontSize: 48, color: '#64748B' }} />
                  <Typography variant="body2" sx={{
                    color: '#64748B',
                    textAlign: 'center',
                    fontSize: '0.875rem'
                  }}>
                    No differences found between the documents.
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{
              p: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#F8F9FA',
              borderTop: '1px solid #E5E9EF'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{
                  color: '#64748B',
                  fontSize: '0.875rem'
                }}>
                  Page {currentPage + 1} of {totalPages}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                  disabled={currentPage === 0}
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    color: '#64748B',
                    '&:hover': {
                      backgroundColor: '#F3E5F5',
                      color: '#9C27B0'
                    }
                  }}
                >
                  Previous
                </Button>
                <Button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                  disabled={currentPage === totalPages - 1}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    color: '#64748B',
                    '&:hover': {
                      backgroundColor: '#F3E5F5',
                      color: '#9C27B0'
                    }
                  }}
                >
                  Next
                </Button>
                <Button
                  onClick={() => setRefDocDialogOpen(false)}
                  sx={{
                    color: '#64748B',
                    '&:hover': {
                      backgroundColor: '#F3E5F5',
                      color: '#9C27B0'
                    }
                  }}
                >
                  Close
                </Button>
              </Box>
            </DialogActions>
          </Dialog>


          {/* Google Drive Authentication Dialog */}
          <Dialog
            open={showGoogleDriveAuthDialog}
            onClose={() => {
              setShowGoogleDriveAuthDialog(false);
              if (googleDriveAuthWindow) {
                googleDriveAuthWindow.close();
                setGoogleDriveAuthWindow(null);
              }
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid #e0e0e0',
              backgroundColor: '#f5f5f5'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon sx={{ color: '#4c3794' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Google Drive Authentication
                </Typography>
              </Box>
              <IconButton
                onClick={() => {
                  setShowGoogleDriveAuthDialog(false);
                  if (googleDriveAuthWindow) {
                    googleDriveAuthWindow.close();
                    setGoogleDriveAuthWindow(null);
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                  Connect to Google Drive to access your files securely.
                </Typography>

                <Box sx={{
                  p: 2,
                  backgroundColor: 'rgba(76, 55, 148, 0.05)',
                  borderRadius: 2,
                  border: '1px solid rgba(76, 55, 148, 0.1)',
                  mb: 3
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#4c3794' }}>
                    What happens next:
                  </Typography>
                  <ol style={{ margin: 0, paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '8px' }}>
                      <Typography variant="body2">
                        A new tab will open with Google's secure authentication page
                      </Typography>
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      <Typography variant="body2">
                        <strong>Select the correct Google account</strong>
                      </Typography>
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      <Typography variant="body2">
                        Sign in and click "Allow" to grant access to your Google Drive
                      </Typography>
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      <Typography variant="body2">
                        <strong>The tab will close automatically</strong> when authentication is complete
                      </Typography>
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      <Typography variant="body2">
                        You'll see a success message and can then browse your Google Drive files
                      </Typography>
                    </li>
                  </ol>
                </Box>

                {isGoogleDriveAuthInProgress && (
                  <Box sx={{
                    p: 2,
                    backgroundColor: 'rgba(76, 55, 148, 0.1)',
                    borderRadius: 2,
                    border: '1px solid rgba(76, 55, 148, 0.2)',
                    mb: 2
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <CircularProgress size={20} sx={{ color: '#4c3794' }} />
                      <Typography variant="body2" sx={{ color: '#4c3794', fontWeight: 600 }}>
                        Authentication in progress...
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#4c3794', fontSize: '0.875rem' }}>
                      Please complete the authentication in the new tab. This dialog will close automatically when done.
                    </Typography>
                  </Box>
                )}

                <Box sx={{
                  p: 2,
                  backgroundColor: 'rgba(16, 185, 129, 0.05)',
                  borderRadius: 2,
                  border: '1px solid rgba(16, 185, 129, 0.1)'
                }}>
                  <Typography variant="body2" sx={{ color: '#10B981', fontWeight: 600, mb: 1 }}>
                    🔒 Secure & Private
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#10B981', fontSize: '0.875rem' }}>
                    We only request access to view your Google Drive files. We cannot modify, delete, or access any other data.
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowGoogleDriveAuthDialog(false);
                  if (googleDriveAuthWindow) {
                    googleDriveAuthWindow.close();
                    setGoogleDriveAuthWindow(null);
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={initiateGoogleDriveAuth}
                disabled={isGoogleDriveAuthInProgress}
                sx={{
                  backgroundColor: '#4c3794',
                  '&:hover': {
                    backgroundColor: '#3a2a73',
                  }
                }}
              >
                {isGoogleDriveAuthInProgress ? 'Authenticating...' : 'Start Authentication'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Google Drive Dialog */}
          <Dialog
            open={showGoogleDriveDialog}
            onClose={closeGoogleDriveDialog}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                overflow: 'hidden'
              }
            }}
          >
            <DialogTitle sx={{
              p: 3,
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#ffffff'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                Select a file
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              {/* Tabs */}
              <Box sx={{ borderBottom: '1px solid #e5e7eb' }}>
                <Box sx={{ display: 'flex' }}>
                  <Button
                    variant="text"
                    onClick={() => handleTabChange('myDrive')}
                    sx={{
                      px: 3,
                      py: 2,
                      textTransform: 'none',
                      fontWeight: activeTab === 'myDrive' ? 600 : 500,
                      color: activeTab === 'myDrive' ? '#111827' : '#6b7280',
                      borderBottom: activeTab === 'myDrive' ? '2px solid #111827' : '2px solid transparent',
                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: '#f9fafb'
                      }
                    }}
                  >
                    My Drive
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => handleTabChange('shared')}
                    sx={{
                      px: 3,
                      py: 2,
                      textTransform: 'none',
                      fontWeight: activeTab === 'shared' ? 600 : 500,
                      color: activeTab === 'shared' ? '#111827' : '#6b7280',
                      borderBottom: activeTab === 'shared' ? '2px solid #111827' : '2px solid transparent',
                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: '#f9fafb'
                      }
                    }}
                  >
                    Shared with me
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => handleTabChange('teamDrives')}
                    sx={{
                      px: 3,
                      py: 2,
                      textTransform: 'none',
                      fontWeight: activeTab === 'teamDrives' ? 600 : 500,
                      color: activeTab === 'teamDrives' ? '#111827' : '#6b7280',
                      borderBottom: activeTab === 'teamDrives' ? '2px solid #111827' : '2px solid transparent',
                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: '#f9fafb'
                      }
                    }}
                  >
                    Shared drives
                  </Button>
                </Box>
              </Box>

              {/* Search Bar */}
              <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
                <TextField
                  fullWidth
                  placeholder="Search"
                  value={googleDriveSearchQuery}
                  onChange={(e) => setGoogleDriveSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#6b7280' }} />
                      </InputAdornment>
                    ),
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#d1d5db',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#9C27B0',
                        }
                      }
                    }
                  }}
                />
              </Box>

              {/* Navigation Controls */}
              {/* <Box sx={{ 
            p: 3, 
            pt: folderBreadcrumb.length > 0 ? 2 : 3,
            display: 'flex', 
            gap: 2, 
            alignItems: 'center', 
            flexWrap: 'wrap',
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #f3f4f6'
          }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigateToFolder('root')}
              disabled={currentFolderId === 'root'}
              sx={{
                color: currentFolderId === 'root' ? '#9ca3af' : '#374151',
                borderColor: currentFolderId === 'root' ? '#e5e7eb' : '#d1d5db',
                fontWeight: 500,
                borderRadius: 1,
                px: 2.5,
                py: 1,
                textTransform: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  borderColor: currentFolderId === 'root' ? '#e5e7eb' : '#9ca3af',
                  backgroundColor: currentFolderId === 'root' ? 'transparent' : '#f9fafb',
                },
                '&:disabled': {
                  borderColor: '#e5e7eb',
                  color: '#9ca3af',
                  backgroundColor: 'transparent'
                }
              }}
            >
              Root Directory
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={loadFolderTree}
              disabled={isLoadingFolderTree}
                sx={{ 
                color: '#374151',
                borderColor: '#d1d5db',
                fontWeight: 500,
                borderRadius: 1,
                px: 2.5,
                py: 1,
                textTransform: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: '#f9fafb',
                }
              }}
            >
              {isLoadingFolderTree ? 'Loading...' : 'Folder Structure'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSessionRecovery}
              sx={{
                color: '#dc2626',
                borderColor: '#fecaca',
                fontWeight: 500,
                borderRadius: 1,
                px: 2.5,
                py: 1,
                textTransform: 'none',
                fontSize: '0.875rem',
                '&:hover': {
                  borderColor: '#f87171',
                  backgroundColor: '#fef2f2',
                }
              }}
            >
              Reset Session
            </Button>
          </Box> */}

              {/* Folders Section */}
              <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#111827' }}>
                  Folders
                </Typography>
                {isLoadingGoogleFiles ? (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 4
                  }}>
                    <CircularProgress sx={{ color: '#9C27B0', mr: 2 }} size={20} />
                    <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                      Loading folders...
                    </Typography>
                  </Box>
                ) : filteredGoogleDriveFiles.filter(file => file.mimeType === 'application/vnd.google-apps.folder').length > 0 ? (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {filteredGoogleDriveFiles
                      .filter(file => file.mimeType === 'application/vnd.google-apps.folder')
                      .map((folder) => (
                        <Box
                          key={folder.id}
                          onClick={() => navigateToFolder(folder.id)}
                          sx={{
                            p: 2,
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            backgroundColor: '#ffffff',
                            '&:hover': {
                              backgroundColor: '#f9fafb',
                              borderColor: '#9C27B0'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FolderIcon sx={{ color: '#9C27B0', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                              {folder.name}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                  </Box>
                ) : (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 4,
                    textAlign: 'center'
                  }}>
                    <FolderIcon sx={{ color: '#9ca3af', fontSize: 48, mb: 2 }} />
                    <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                      No folders available
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                      This location doesn't contain any folders
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Content Section */}
              <Box sx={{ p: 3 }}>
                {activeTab === 'teamDrives' ? (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#111827' }}>
                      Team Drives
                    </Typography>
                    {isLoadingGoogleFiles ? (
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8
                      }}>
                        <CircularProgress sx={{ color: '#9C27B0', mb: 2 }} size={32} />
                        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                          Loading team drives...
                        </Typography>
                      </Box>
                    ) : teamDrives.length > 0 ? (
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: 2,
                        maxHeight: '400px',
                        overflowY: 'auto'
                      }}>
                        {teamDrives.map((drive) => (
                          <Box
                            key={drive.id}
                            onClick={() => handleTeamDriveSelect(drive.id)}
                            sx={{
                              p: 2,
                              border: selectedTeamDrive === drive.id
                                ? '2px solid #9C27B0'
                                : '1px solid #e5e7eb',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              backgroundColor: selectedTeamDrive === drive.id
                                ? 'rgba(156, 39, 176, 0.05)'
                                : '#ffffff',
                              '&:hover': {
                                backgroundColor: selectedTeamDrive === drive.id
                                  ? 'rgba(156, 39, 176, 0.1)'
                                  : '#f9fafb',
                                borderColor: '#9C27B0'
                              }
                            }}
                          >
                            <Box sx={{
                              height: 120,
                              background: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 2,
                              border: '1px solid #e5e7eb'
                            }}>
                              <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                zIndex: 1,
                                position: 'relative'
                              }}>
                                <Typography sx={{
                                  fontSize: '2.5rem',
                                  mb: 0.5,
                                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                                }}>
                                  🏢
                                </Typography>
                                <Typography sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  opacity: 0.9,
                                  textAlign: 'center'
                                }}>
                                  TEAM DRIVE
                                </Typography>
                              </Box>
                            </Box>

                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: '#111827',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                mb: 0.5
                              }}
                            >
                              {drive.name}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8,
                        textAlign: 'center'
                      }}>
                        <Typography sx={{
                          fontSize: '3rem',
                          mb: 2,
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                        }}>
                          🏢
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                          No team drives available
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                          You don't have access to any shared drives
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#111827' }}>
                      Files
                    </Typography>

                    {/* File Grid */}
                    {isLoadingGoogleFiles ? (
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8
                      }}>
                        <CircularProgress sx={{ color: '#9C27B0', mb: 2 }} size={32} />
                        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                          Loading files...
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: 2,
                        maxHeight: '400px',
                        overflowY: 'auto'
                      }}>
                        {filteredGoogleDriveFiles
                          .filter(file => file.mimeType !== 'application/vnd.google-apps.folder')
                          .map((file) => (
                            <Box
                              key={file.id}
                              onClick={(event) => handleGoogleDriveFileSelect(file, event)}
                              sx={{
                                p: 2,
                                border: selectedGoogleFile?.id === file.id
                                  ? '2px solid #9C27B0'
                                  : '1px solid #e5e7eb',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                backgroundColor: selectedGoogleFile?.id === file.id
                                  ? 'rgba(156, 39, 176, 0.05)'
                                  : '#ffffff',
                                '&:hover': {
                                  backgroundColor: selectedGoogleFile?.id === file.id
                                    ? 'rgba(156, 39, 176, 0.1)'
                                    : '#f9fafb',
                                  borderColor: '#9C27B0'
                                }
                              }}
                            >
                              {/* File Preview */}
                              <Box sx={{
                                height: 120,
                                background: file.mimeType?.includes('image')
                                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                  : file.mimeType?.includes('pdf')
                                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                    : file.mimeType?.includes('word') || file.mimeType?.includes('document')
                                      ? 'linear-gradient(135deg, #2b6cb0 0%, #3182ce 100%)'
                                      : file.mimeType?.includes('excel') || file.mimeType?.includes('spreadsheet')
                                        ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
                                        : file.mimeType?.includes('powerpoint') || file.mimeType?.includes('presentation')
                                          ? 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)'
                                          : 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)',
                                backgroundSize: 'auto',
                                backgroundPosition: 'center',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 2,
                                border: '1px solid #e5e7eb',
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  backdropFilter: 'blur(1px)'
                                }
                              }}>
                                {file.mimeType?.includes('image') ? (
                                  <ImagePreview file={file} />
                                ) : (
                                  <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    zIndex: 1,
                                    position: 'relative'
                                  }}>
                                    <Typography sx={{
                                      fontSize: '2.5rem',
                                      mb: 0.5,
                                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                                    }}>
                                      {getFileTypeIcon(file.mimeType)}
                                    </Typography>
                                    <Typography sx={{
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px',
                                      opacity: 0.9,
                                      textAlign: 'center'
                                    }}>
                                      {getFileTypeLabel(file.mimeType)}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>

                              {/* File Name */}
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: '#111827',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  mb: 0.5
                                }}
                              >
                                {file.name}
                              </Typography>

                              {/* File Info */}
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#6b7280',
                                  fontSize: '0.75rem'
                                }}
                              >
                                {formatFileSize(file.size || '0')}
                              </Typography>
                            </Box>
                          ))}
                      </Box>
                    )}

                    {filteredGoogleDriveFiles.filter(file => file.mimeType !== 'application/vnd.google-apps.folder').length === 0 && !isLoadingGoogleFiles && (
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8,
                        textAlign: 'center'
                      }}>
                        <Typography sx={{
                          fontSize: '3rem',
                          mb: 2,
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                        }}>
                          📄
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                          {googleDriveSearchQuery ? 'No files match your search' : 'No files available'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                          {googleDriveSearchQuery
                            ? 'Try adjusting your search terms or clear the search to see all files'
                            : 'This location doesn\'t contain any files'
                          }
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={closeGoogleDriveDialog}
                sx={{
                  color: '#6b7280',
                  borderColor: '#d1d5db',
                  '&:hover': {
                    borderColor: '#9ca3af',
                    backgroundColor: '#f9fafb'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  if (selectedGoogleFiles.length > 0) {
                    downloadMultipleGoogleDriveFiles(selectedGoogleFiles);
                  } else if (selectedGoogleFile) {
                    downloadGoogleDriveFile(selectedGoogleFile);
                  }
                }}
                disabled={!selectedGoogleFile && selectedGoogleFiles.length === 0 || isDownloadingGoogleFile || isDownloadingMultipleFiles}
                sx={{
                  backgroundColor: '#9C27B0',
                  '&:hover': {
                    backgroundColor: '#7B1FA2',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(156, 39, 176, 0.3)',
                    color: '#ffffff'
                  }
                }}
              >
                {isDownloadingGoogleFile || isDownloadingMultipleFiles ? 'Processing...' : 'Select'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Folder Tree Dialog */}
          <Dialog
            open={showFolderTree}
            onClose={() => setShowFolderTree(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid #e0e0e0',
              backgroundColor: '#f5f5f5'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  📁 Folder Structure
                </Typography>
              </Box>
              <IconButton
                onClick={() => setShowFolderTree(false)}
                size="small"
                sx={{
                  color: '#1a1a1a',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              {isLoadingFolderTree ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <CircularProgress sx={{ color: '#4c3794' }} />
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Click on any folder to navigate to it.
                  </Typography>
                  {folderTree.length > 0 ? (
                    <Box>
                      {folderTree.map((folder) => (
                        <FolderTreeItem
                          key={folder.id}
                          folder={folder}
                          onNavigate={navigateToFolder}
                          onCloseDialog={() => setShowFolderTree(false)}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography sx={{ color: 'text.secondary', textAlign: 'center' }}>
                      No folders found.
                    </Typography>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                onClick={() => setShowFolderTree(false)}
                sx={{ color: '#4c3794' }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* File Info Dialog */}
          <Dialog
            open={showFileInfo}
            onClose={() => setShowFileInfo(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid #e0e0e0',
              backgroundColor: '#f5f5f5'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon sx={{ color: '#4c3794' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  File Information
                </Typography>
              </Box>
              <IconButton
                onClick={() => setShowFileInfo(false)}
                size="small"
                sx={{
                  color: '#1a1a1a',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              {isLoadingFileInfo ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress sx={{ color: '#4c3794' }} />
                </Box>
              ) : fileInfo ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '1.5rem' }}>{getFileIcon(fileInfo.mimeType)}</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {fileInfo.name}
                    </Typography>
                  </Box>

                  <Box sx={{
                    p: 2,
                    backgroundColor: 'rgba(76, 55, 148, 0.05)',
                    borderRadius: 2,
                    border: '1px solid rgba(76, 55, 148, 0.1)'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#4c3794' }}>
                      File Details:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2">
                        <strong>Size:</strong> {formatFileSize(fileInfo.size || '0')}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Type:</strong> {fileInfo.mimeType}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Created:</strong> {formatDate(fileInfo.createdTime)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Modified:</strong> {formatDate(fileInfo.modifiedTime)}
                      </Typography>
                      {fileInfo.description && (
                        <Typography variant="body2">
                          <strong>Description:</strong> {fileInfo.description}
                        </Typography>
                      )}
                      {fileInfo.owners && fileInfo.owners.length > 0 && (
                        <Typography variant="body2">
                          <strong>Owner:</strong> {fileInfo.owners[0].displayName}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Typography color="text.secondary">No file information available.</Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setShowFileInfo(false)}
                sx={{ color: '#4c3794' }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Notifications */}
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError(null)}
          >
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Snackbar>

          <Snackbar
            open={!!success}
            autoHideDuration={6000}
            onClose={() => setSuccess(null)}
          >
            <Alert severity="success" onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          </Snackbar>
        </>
      )}

      {/* Project Dialog - Always available */}
      <Dialog open={isProjectDialogOpen} onClose={() => setIsProjectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Project</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Project Name"
              value={newProject.project_name}
              onChange={e => setNewProject(prev => ({ ...prev, project_name: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Project Description"
              value={newProject.project_description}
              onChange={e => setNewProject(prev => ({ ...prev, project_description: e.target.value }))}
              multiline
              rows={3}
            />
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={newProject.period_start_date}
              onChange={e => setNewProject(prev => ({ ...prev, period_start_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={newProject.period_end_date}
              onChange={e => setNewProject(prev => ({ ...prev, period_end_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setIsProjectDialogOpen(false);
            setNewProject({
              project_name: '',
              project_description: '',
              period_start_date: new Date().toISOString().split('T')[0],
              period_end_date: new Date().toISOString().split('T')[0]
            });
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateProject}
            variant="contained"
            disabled={!newProject.project_name.trim() || isSubmittingProject}
            sx={{
              backgroundColor: '#9C27B0',
              '&:hover': { backgroundColor: '#7B1FA2' },
              fontWeight: 600
            }}
          >
            {isSubmittingProject ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                Creating...
              </>
            ) : (
              'Create Project'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Content Dialog */}
      {showExportDialog && (
        <Dialog
          open={showExportDialog}
          onClose={handleCloseExportDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{
            backgroundColor: '#F8F9FA',
            borderBottom: '1px solid #E5E9EF',
            p: 3
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DownloadIcon sx={{ color: '#4c3794', fontSize: 24 }} />
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: '#1a1a1a',
                  fontSize: '1.25rem'
                }}>
                  Export Content
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#64748B' }}>
                Step {currentStep} of 2
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {currentStep === 1 && (
              <Box sx={{ space: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                    Brand Details
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B' }}>
                    These details help us create content that matches your brand voice and style.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Company Website */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Company Website
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <TextField
                        fullWidth
                        type="url"
                        value={brandDetails.website}
                        onChange={(e) => handleBrandDetailsChange('website', e.target.value)}
                        disabled={brandDetails.websiteNotApplicable}
                        placeholder="https://www.example.com"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            }
                          }
                        }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <input
                          type="checkbox"
                          checked={brandDetails.websiteNotApplicable}
                          onChange={(e) => setBrandDetails(prev => ({ ...prev, websiteNotApplicable: e.target.checked }))}
                          style={{ accentColor: '#4c3794' }}
                        />
                        <Typography variant="body2" sx={{ color: '#64748B' }}>
                          Not applicable / No website
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Industry Keywords */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Industry Keywords
                    </Typography>
                    <TextField
                      fullWidth
                      value={brandDetails.industryKeywords}
                      onChange={(e) => handleBrandDetailsChange('industryKeywords', e.target.value)}
                      placeholder="fintech, banking, financial services, digital payments, public sector"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          }
                        }
                      }}
                    />
                  </Box>

                  {/* Target Audience */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Target Audience / Customer Segments
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Selected audiences */}
                      {brandDetails.targetAudience.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {brandDetails.targetAudience.map((audience) => (
                            <Chip
                              key={audience}
                              label={audience}
                              onDelete={() => handleRemoveAudience(audience)}
                              sx={{
                                backgroundColor: '#F3E5F5',
                                color: '#4c3794',
                                '& .MuiChip-deleteIcon': {
                                  color: '#4c3794',
                                  '&:hover': {
                                    color: '#7B1FA2'
                                  }
                                }
                              }}
                            />
                          ))}
                        </Box>
                      )}

                      {/* Add custom audience */}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={newAudience}
                          onChange={(e) => setNewAudience(e.target.value)}
                          placeholder="Add custom audience"
                          onKeyPress={(e) => e.key === 'Enter' && handleAddCustomAudience()}
                        />
                        <Button
                          onClick={handleAddCustomAudience}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: '#4c3794',
                            color: '#4c3794',
                            '&:hover': {
                              borderColor: '#7B1FA2',
                              backgroundColor: 'rgba(76, 55, 148, 0.05)'
                            }
                          }}
                        >
                          Add
                        </Button>
                      </Box>

                      {/* Audience options */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {audienceOptions.map((option) => (
                          <Chip
                            key={option}
                            label={option}
                            onClick={() => handleAudienceToggle(option)}
                            variant={brandDetails.targetAudience.includes(option) ? 'filled' : 'outlined'}
                            sx={{
                              backgroundColor: brandDetails.targetAudience.includes(option) ? '#F3E5F5' : 'transparent',
                              color: brandDetails.targetAudience.includes(option) ? '#4c3794' : '#64748B',
                              borderColor: '#4c3794',
                              '&:hover': {
                                backgroundColor: 'rgba(76, 55, 148, 0.05)'
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>

                  {/* Brand Voice */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Brand Voice & Tone
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={brandDetails.brandVoice}
                      onChange={(e) => handleBrandDetailsChange('brandVoice', e.target.value)}
                      placeholder="bold, warm, authoritative, casual, formal, friendly, professional"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          }
                        }
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#64748B', mt: 0.5, display: 'block' }}>
                      Describe your brand's voice and tone. You can select specific tones for each content piece during generation.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {currentStep === 2 && (
              <Box sx={{ space: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                    Content Preferences
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B' }}>
                    Choose your preferred content format and style.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Content Category */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Content Category
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={contentPreferences.category}
                        onChange={(e) => handleContentPreferenceChange('category', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E0E0E0'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          }
                        }}
                      >
                        {contentTypes.map((contentType) => (
                          <MenuItem key={contentType.contentType} value={contentType.contentType}>
                            {contentType.contentTypeDescription}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Content Format */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Content Format
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={contentPreferences.format}
                        onChange={(e) => handleContentPreferenceChange('format', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E0E0E0'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          }
                        }}
                      >
                        <MenuItem value="Text Only">Text Only</MenuItem>
                        <MenuItem value="Image Only">Image Only</MenuItem>
                        <MenuItem value="Infographic">Infographic</MenuItem>
                        <MenuItem value="Video">Video</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Content Length */}
                  {isContentLengthApplicable() && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                        Content Length
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={contentPreferences.length}
                          onChange={(e) => handleContentPreferenceChange('length', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#E0E0E0'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4c3794'
                            }
                          }}
                        >
                          <MenuItem value="Short (100-200 words)">Short (100-200 words)</MenuItem>
                          <MenuItem value="Medium (200-500 words)">Medium (200-500 words)</MenuItem>
                          <MenuItem value="Long (500+ words)">Long (500+ words)</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {/* Writing Style */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Writing Style
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={contentPreferences.style}
                        onChange={(e) => handleContentPreferenceChange('style', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E0E0E0'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          }
                        }}
                      >
                        <MenuItem value="Professional">Professional</MenuItem>
                        <MenuItem value="Casual">Casual</MenuItem>
                        <MenuItem value="Technical">Technical</MenuItem>
                        <MenuItem value="Creative">Creative</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Additional Instructions */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 600 }}>
                      Additional Instructions
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={contentPreferences.instructions}
                      onChange={(e) => handleContentPreferenceChange('instructions', e.target.value)}
                      placeholder="Any specific requirements, tone, or guidelines for the content..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4c3794'
                          }
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E9EF' }}>
            <Button
              onClick={handleCloseExportDialog}
              sx={{ color: '#64748B' }}
            >
              Cancel
            </Button>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {currentStep > 1 && (
                <Button
                  onClick={handlePrevStep}
                  variant="outlined"
                  sx={{
                    borderColor: '#9E9E9E',
                    color: '#9E9E9E',
                    '&:hover': {
                      borderColor: '#757575',
                      backgroundColor: 'rgba(158, 158, 158, 0.05)'
                    }
                  }}
                >
                  Previous
                </Button>
              )}

              {currentStep < 2 ? (
                <Button
                  onClick={handleNextStep}
                  variant="contained"
                  sx={{
                    backgroundColor: '#4c3794',
                    '&:hover': {
                      backgroundColor: '#3d2e7a'
                    }
                  }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handleGenerateContent}
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  disabled={isGeneratingContent}
                  sx={{
                    backgroundColor: '#4c3794',
                    '&:hover': {
                      backgroundColor: '#3d2e7a'
                    },
                    '&.Mui-disabled': {
                      backgroundColor: 'rgba(76, 55, 148, 0.3)',
                      color: 'rgba(255, 255, 255, 0.5)'
                    }
                  }}
                >
                  {isGeneratingContent ? 'Generating...' : 'Generate Content'}
                </Button>
              )}
            </Box>
          </DialogActions>
        </Dialog>
      )}

      {/* Generated Content Display Dialog */}
      {showContentDisplay && generatedContent && (
        <Dialog
          open={showContentDisplay}
          onClose={handleCloseContentDisplay}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{
            backgroundColor: '#F8F9FA',
            borderBottom: '1px solid #E5E9EF',
            p: 3
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FileText sx={{ color: '#4c3794', fontSize: 24 }} />
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: '#1a1a1a',
                  fontSize: '1.25rem'
                }}>
                  Generated Content
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isEditingGeneratedContent ? (
                  <>
                    <Button
                      onClick={handleCancelEditingGeneratedContent}
                      sx={{
                        color: '#4c3794',
                        textTransform: 'none',
                        fontWeight: 500,
                        '&:hover': { backgroundColor: 'rgba(76, 55, 148, 0.08)' }
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveEditedGeneratedContent}
                      variant="contained"
                      sx={{
                        backgroundColor: '#4c3794',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { backgroundColor: '#3d2e7a' }
                      }}
                    >
                      Save Changes
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={handleStartEditingGeneratedContent}
                    startIcon={<EditIcon fontSize="small" />}
                    sx={{
                      color: '#4c3794',
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': { backgroundColor: 'rgba(76, 55, 148, 0.08)' }
                    }}
                  >
                    Edit Content
                  </Button>
                )}
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              {isEditingGeneratedContent ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    label="Title"
                    value={editableGeneratedTitle}
                    onChange={(e) => setEditableGeneratedTitle(e.target.value)}
                    fullWidth
                    variant="outlined"
                    InputProps={{ sx: { fontWeight: 600, fontSize: '1.2rem' } }}
                  />
                  <TextField
                    label="Content"
                    value={editableGeneratedBody}
                    onChange={(e) => setEditableGeneratedBody(e.target.value)}
                    fullWidth
                    variant="outlined"
                    multiline
                    minRows={10}
                    InputProps={{
                      sx: {
                        lineHeight: 1.8,
                        fontSize: '1rem',
                        alignItems: 'flex-start'
                      }
                    }}
                  />
                  <Typography variant="caption" sx={{ color: '#64748B' }}>
                    Tip: Make final tweaks here before downloading or copying.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="h5" sx={{
                    fontWeight: 600,
                    color: '#1a1a1a',
                    mb: 2
                  }}>
                    {generatedContent.title}
                  </Typography>

                  <Box sx={{
                    backgroundColor: '#F8F9FA',
                    borderRadius: '12px',
                    p: 3,
                    border: '1px solid #E5E9EF'
                  }}>
                    <Box sx={{
                      color: '#475569',
                      lineHeight: 1.8,
                      fontSize: '1rem',
                      '& p': { mb: 2 },
                      '& ul, & ol': { pl: 3, mb: 2 },
                      '& li': { mb: 1 },
                      '& strong': { fontWeight: 600, color: '#1a1a1a' },
                      '& em': { fontStyle: 'italic' },
                      '& h1, & h2, & h3, & h4': {
                        fontWeight: 600,
                        color: '#1a1a1a',
                        mt: 2,
                        mb: 1
                      }
                    }}>
                      <ReactMarkdown>{generatedContent.content}</ReactMarkdown>
                    </Box>
                  </Box>
                </>
              )}
            </Box>

            {generatedContent.followupQuestions && generatedContent.followupQuestions.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: '#1a1a1a',
                  mb: 2
                }}>
                  Follow-up Questions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {generatedContent.followupQuestions.map((question, index) => (
                    <Typography key={index} variant="body2" sx={{
                      color: '#64748B',
                      pl: 2,
                      position: 'relative',
                      '&:before': {
                        content: '"•"',
                        position: 'absolute',
                        left: 0,
                        color: '#4c3794'
                      }
                    }}>
                      {question}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {generatedContent.sourceLinks && generatedContent.sourceLinks.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: '#1a1a1a',
                  mb: 2
                }}>
                  Source Links
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {generatedContent.sourceLinks.map((link, index) => (
                    <Typography key={index} variant="body2" sx={{
                      color: '#4c3794',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      '&:hover': {
                        color: '#7B1FA2'
                      }
                    }}>
                      {link}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E9EF' }}>
            <Button
              onClick={handleCloseContentDisplay}
              sx={{ color: '#64748B' }}
            >
              Close
            </Button>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Copy button for text-based content */}
              {(generatedContent.format === 'Text Only' || generatedContent.format === 'Video') && (
                <Button
                  onClick={handleCopyContent}
                  variant="outlined"
                  startIcon={<FileText />}
                  sx={{
                    borderColor: '#4c3794',
                    color: '#4c3794',
                    '&:hover': {
                      borderColor: '#7B1FA2',
                      backgroundColor: 'rgba(76, 55, 148, 0.05)'
                    }
                  }}
                >
                  Copy Content
                </Button>
              )}

              <Button
                onClick={handleDownloadContent}
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: '#4c3794',
                  '&:hover': {
                    backgroundColor: '#7B1FA2'
                  }
                }}
              >
                Download {
                  generatedContent.format === 'Image Only' || generatedContent.format === 'Infographic' ? 'PNG' :
                    generatedContent.format === 'Video' ? 'MP4' :
                      'Content'
                }
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default DocsInsights; 